// FloatingChatbot.js

import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  IconButton,
  VStack,
  Flex,
  Text,
  Avatar,
  Spinner,
  Button,
  Tooltip,
  chakra,
  HStack,
  useToast,
  keyframes,
} from "@chakra-ui/react";
import { CloseIcon, ArrowForwardIcon, RepeatIcon, ChevronRightIcon, CopyIcon, CheckIcon } from "@chakra-ui/icons";
import ReactMarkdown from "react-markdown";
import "./FloatingChatbot.css";
import { BiExpand, BiCollapse } from "react-icons/bi";
import TextareaAutosize from "react-textarea-autosize";
import { UserContext } from "./UserContext";
import WorkflowProgressUI from "./WorkflowProgressUI"; // Import the WorkflowProgressUI
import aiComponentManager from "../services/AIComponentInteractionManager";
import aiActionManager from "../services/AIActionManager";
import AIActionHandler from "./AIActionHandler";
import { formatAIMessage } from "../utils/messageFormatting";
import ChatFooterBar from "./ChatFooterBar";
import GitHubConnectBanner from "./GitHubConnectBanner";
import chatStateManager from "../services/ChatStateManager";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import AddFeatureCard from "./AddFeatureCard";
import { workflowLaunchCache } from "../services/WorkflowLaunchCache";
import AgentToolCallCard, { GithubOutcomeCard, shouldRenderGithubOutcomeCard } from "./AgentToolCallCard";
import { isComponentActionCapability } from "../utils/componentActionPolicy";
import FeatureEnablementConfirmation from "./FeatureEnablementConfirmation";
import { POST_STRATEGY_REPLIES, buildShowChatEditingMessage } from "../utils/postStrategyMessages";
import LearningDetectedTag from "./LearningDetectedTag";
import CreditTopUpModal from "./CreditTopUpModal";

const CHAT_ATTACHMENT_MAX_BYTES = 500 * 1024;
const CHAT_ATTACHMENT_MAX_FILES = 3;
const CHAT_ATTACHMENT_TYPES = new Set(["txt", "csv"]);
// Minimum time the "reading" shimmer stays visible so the transition reads as
// smooth even for tiny files (the actual read is near-instant). Delight, not fakery.
const CHAT_ATTACHMENT_MIN_READ_MS = 480;

function isCompressedContextMessage(message) {
  const text = String(message?.text || message?.message || "").trim();
  return Boolean(message?.isCompressedContext || message?.hiddenFromChat) ||
    text.startsWith("## Conversation context") ||
    text.startsWith("Conversation context") ||
    text.includes("This conversation was compacted automatically");
}

// Light sweep across the pill while the file is being read.
const attachmentShimmer = keyframes`
  0%   { transform: translateX(-120%); }
  100% { transform: translateX(220%); }
`;
// Gentle entry when a pill first appears.
const attachmentPillEnter = keyframes`
  from { opacity: 0; transform: translateY(4px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

function formatAttachmentSize(bytes = 0) {
  if (bytes < 1024) return `${bytes}B`;
  return `${Math.ceil(bytes / 1024)}KB`;
}

function getAttachmentExt(name = "") {
  return String(name).split(".").pop()?.toLowerCase() || "";
}

function validateChatFiles(files = [], existingCount = 0) {
  const accepted = [];
  const errors = [];
  let formatRejected = false;
  const availableSlots = CHAT_ATTACHMENT_MAX_FILES - existingCount;
  if (files.length > availableSlots) {
    errors.push(`You can attach up to ${CHAT_ATTACHMENT_MAX_FILES} files at a time.`);
  }
  for (const file of files.slice(0, Math.max(0, availableSlots))) {
    const ext = getAttachmentExt(file.name);
    if (!CHAT_ATTACHMENT_TYPES.has(ext)) {
      // Format rejections surface as a friendly "more formats coming" card instead
      // of a terse error line.
      formatRejected = true;
      continue;
    }
    if (file.size > CHAT_ATTACHMENT_MAX_BYTES) {
      errors.push(`${file.name} is over the 500KB limit. Try a smaller file.`);
      continue;
    }
    accepted.push({
      id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
      file,
      name: file.name,
      type: file.type || (ext === "csv" ? "text/csv" : "text/plain"),
      size: file.size,
      ext,
      status: "reading",
      content_base64: null,
    });
  }
  return { accepted, errors: [...new Set(errors)], formatRejected };
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      resolve(result.includes(",") ? result.split(",").pop() : result);
    };
    reader.onerror = () => reject(reader.error || new Error("Could not read file"));
    reader.readAsDataURL(file);
  });
}

function githubPayloadKey(result) {
  if (!result || typeof result !== 'object') return 'none';
  return result.id || result.run_id || result.result?.proposed_branch || result.proposed_branch || 'result';
}

function getGithubOutcomeToolCall(message) {
  if (!message || message.sender !== 'bot') return null;
  const calls = [];
  if (Array.isArray(message.segments)) {
    calls.push(...message.segments.filter(seg => seg?.type === 'tool'));
  }
  if (Array.isArray(message.agentToolCalls)) {
    calls.push(...message.agentToolCalls);
  }
  const seen = new Set();
  const githubCalls = calls
    .filter(shouldRenderGithubOutcomeCard)
    .filter(call => {
      const key = call.tool_call_id || call.result?.id || call.id || `${call.tool}:${githubPayloadKey(call.result)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  return githubCalls[githubCalls.length - 1] || null;
}

const viewSlugToQuery = {
  strategy: "strategy",
  "product-strategy": "productStrategy",
  product_strategy: "productStrategy",
  productStrategy: "productStrategy",
  "competitor-analysis": "competitor",
  competitor_analysis: "competitor",
  competitor: "competitor",
  "action-plan": "stepByStep",
  action_plan: "stepByStep",
  stepByStep: "stepByStep",
  decisions: "decisions",
  pulse: "pulse",
  documents: "readme",
  readme: "readme",
};

const normalizePabsHref = (href) => {
  if (!href || typeof window === "undefined") return href;
  const currentPlanId = window.location.pathname.match(/\/plan\/(\d+)/)?.[1];
  if (!currentPlanId) return href;

  let url;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return href;
  }

  const isLocalPabs =
    url.origin === window.location.origin ||
    url.hostname === "localhost" ||
    url.hostname === "127.0.0.1";
  if (!isLocalPabs) return href;

  const parts = url.pathname.split("/").filter(Boolean);
  if (parts[0] !== "plan") return href;

  const rawSlug =
    parts.length === 2 && !/^\d+$/.test(parts[1])
      ? parts[1]
      : parts.length >= 3
        ? parts[2]
        : null;
  const view = rawSlug ? viewSlugToQuery[rawSlug] : null;
  if (!view) return href;

  return `/plan/${currentPlanId}?view=${view}`;
};

// ReactMarkdown component map — defined once at module level to prevent recreation on every render
const markdownComponents = {
  p: ({ children }) => (
    <Text mb={3} lineHeight="1.7" color="gray.700" whiteSpace="pre-wrap">
      {children}
    </Text>
  ),
  strong: ({ children }) => (
    <Text as="strong" fontWeight="bold" color="gray.800">
      {children}
    </Text>
  ),
  ol: ({ children }) => (
    <ol style={{ paddingLeft: "24px", marginBottom: "16px", marginTop: "8px", listStyleType: "decimal" }}>
      {children}
    </ol>
  ),
  ul: ({ children }) => (
    <ul style={{ paddingLeft: "24px", marginBottom: "16px", marginTop: "8px", listStyleType: "disc" }}>
      {children}
    </ul>
  ),
  li: ({ children }) => (
    <li style={{ marginBottom: "8px", lineHeight: "1.6", color: "#4A5568", display: "list-item" }}>
      {children}
    </li>
  ),
  h1: ({ children }) => (
    <Text fontSize="xl" fontWeight="bold" mb={3} mt={4} color="gray.800">
      {children}
    </Text>
  ),
  h2: ({ children }) => (
    <Text fontSize="lg" fontWeight="bold" mb={3} mt={4} color="gray.800">
      {children}
    </Text>
  ),
  h3: ({ children }) => (
    <Text fontSize="md" fontWeight="bold" mb={2} mt={3} color="gray.800">
      {children}
    </Text>
  ),
  hr: () => (
    <Box
      as="hr"
      my={4}
      borderColor="gray.200"
      borderTopWidth="1px"
      borderStyle="solid"
      opacity={0.6}
    />
  ),
  br: () => <Box mb={2} />,
  a: ({ href, children }) => {
    const normalizedHref = normalizePabsHref(href);
    const jobMatch = normalizedHref?.match(/^#job-(\d+)$/);
    if (jobMatch) {
      const taskId = parseInt(jobMatch[1]);
      return (
        <Box
          as="span"
          onClick={() => window.dispatchEvent(new CustomEvent('pabs:openJobs', { detail: { taskId } }))}
          color="blue.500"
          fontWeight="500"
          textDecoration="underline"
          _hover={{ color: 'blue.700' }}
          display="inline"
          cursor="pointer"
        >
          {children}
        </Box>
      );
    }
    if (normalizedHref === '/topup') {
      return (
        <Box
          as="a"
          href="/topup"
          onClick={(e) => {
            e.preventDefault();
            window.dispatchEvent(new CustomEvent('pabs:openTopUpModal'));
          }}
          color="blue.500"
          fontWeight="500"
          textDecoration="underline"
          _hover={{ color: 'blue.700' }}
          display="inline"
          cursor="pointer"
        >
          {children}
        </Box>
      );
    }
    const isExternal = normalizedHref?.startsWith('http') || normalizedHref?.startsWith('//');
    if (!isExternal && normalizedHref) {
      return (
        <RouterLink
          to={normalizedHref}
          style={{ color: 'var(--chakra-colors-blue-500)', fontWeight: 500, textDecoration: 'underline', display: 'inline' }}
        >
          {children}
        </RouterLink>
      );
    }
    return (
      <Box
        as="a"
        href={normalizedHref}
        target="_blank"
        rel="noopener noreferrer"
        color="blue.500"
        fontWeight="500"
        textDecoration="underline"
        _hover={{ color: 'blue.700' }}
        display="inline"
      >
        {children}
      </Box>
    );
  },
  code: ({ inline, children }) => {
    // react-markdown v7+ may not pass `inline`; infer from content — no newlines = inline code
    const childStr = Array.isArray(children) ? children.join('') : String(children || '');
    const isInline = inline !== undefined ? inline : !childStr.includes('\n');
    if (isInline) {
      return (
        <Text as="code" bg="gray.100" px={1} py={0.5} borderRadius="sm" fontSize="sm" display="inline">
          {children}
        </Text>
      );
    }
    return (
      <Box as="pre" bg="gray.100" p={3} borderRadius="md" overflowX="auto" mb={3}>
        <Text as="code" fontSize="sm">
          {children}
        </Text>
      </Box>
    );
  },
  text: ({ children }) => <>{children}</>,
};

export { markdownComponents };

// Check if we're in development mode (for simulated typing)
const isDevelopment = process.env.NODE_ENV === 'development' || 
                      window.location.hostname === 'localhost' ||
                      window.location.hostname.includes('.replit.dev');

// Simulated typing effect for development environment
// When proxy buffering causes all chunks to arrive at once, this creates a progressive typing UX
const simulateTyping = (fullText, onUpdate, onComplete, options = {}) => {
  const { 
    charsPerFrame = 3,      // Characters to add per frame
    frameDelay = 16,        // ~60fps base rate
    initialDelay = 100,     // Delay before starting
    wordBoundaryPause = 50  // Extra pause at word boundaries
  } = options;
  
  let currentIndex = 0;
  let timeoutId = null;
  
  const type = () => {
    if (currentIndex >= fullText.length) {
      onComplete?.(fullText);
      return;
    }
    
    // Add characters
    const nextChars = fullText.slice(currentIndex, currentIndex + charsPerFrame);
    currentIndex += charsPerFrame;
    
    onUpdate(fullText.slice(0, currentIndex));
    
    // Calculate delay - slightly longer pause at word boundaries for readability
    const lastChar = nextChars[nextChars.length - 1];
    const delay = (lastChar === ' ' || lastChar === '\n') 
      ? frameDelay + wordBoundaryPause 
      : frameDelay;
    
    timeoutId = setTimeout(type, delay);
  };
  
  // Start typing after initial delay
  timeoutId = setTimeout(type, initialDelay);
  
  // Return cancel function
  return () => {
    if (timeoutId) clearTimeout(timeoutId);
  };
};

// Helper function to consume SSE stream from /api/chat-stream
const streamChatResponse = async (requestBody, callbacks) => {
  const { onThinking, onStreamStart, onChunk, onDone, onError, onSimulatedTyping, onAgentStart, onAgentRun, onTaskChunk, onAgentToolStart, onAgentToolDone, onStateSync, onDocumentGenerationEvent } = callbacks;
  const token = localStorage.getItem("token");
  let chunkCount = 0;
  let firstChunkTime = null;
  let lastChunkTime = null;
  
  try {
    const response = await fetch('/api/chat-stream', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = null;
    let finalData = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      
      // Process complete SSE events (separated by double newlines)
      const events = buffer.split('\n\n');
      buffer = events.pop() || ''; // Keep incomplete event in buffer

      for (const eventBlock of events) {
        if (!eventBlock.trim()) continue;
        
        const lines = eventBlock.split('\n');
        let eventType = null;
        let eventData = null;

        for (const line of lines) {
          if (line.startsWith('event: ')) {
            eventType = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            // Server sends single-line JSON after "data: "
            try {
              eventData = JSON.parse(line.slice(6));
            } catch (e) {
              console.warn('SSE parse error:', e, 'line:', line);
            }
          }
        }

        if (eventType && eventData) {
          switch (eventType) {
            case 'thinking':
              onThinking?.(eventData);
              break;
            case 'stream_start':
              onStreamStart?.(eventData);
              break;
            case 'chunk':
              if (eventData.content) {
                // Track chunk timing for buffering detection
                const now = Date.now();
                if (!firstChunkTime) firstChunkTime = now;
                lastChunkTime = now;
                chunkCount++;
                onChunk?.(eventData.content);
              }
              break;
            case 'done':
              finalData = eventData;
              // Simulated typing disabled - just call onDone directly
              const totalChunkTime = lastChunkTime ? (lastChunkTime - firstChunkTime) : 0;
              onDone?.(eventData);
              break;
            case 'agent_start':
              onAgentStart?.(eventData);
              // rAF guarantees the browser commits a paint frame before processing the
              // next event — without this, agent_start + agent_done are batched into one
              // React render and the "Consulting..." active blue state is never visible
              await new Promise(r => requestAnimationFrame(r));
              break;
            case 'agent_run':
              onAgentRun?.(eventData);
              break;
            case 'task_chunk':
              onTaskChunk?.(eventData);
              break;
            case 'agent_tool_start':
              onAgentToolStart?.(eventData);
              await new Promise(r => requestAnimationFrame(r));
              break;
            case 'agent_tool_done':
              onAgentToolDone?.(eventData);
              await new Promise(r => requestAnimationFrame(r));
              break;
            case 'state_sync':
              onStateSync?.(eventData);
              await new Promise(r => requestAnimationFrame(r));
              break;
            case 'document_generation_started':
            case 'document_generation_finalising':
            case 'document_generation_completed':
            case 'document_generation_failed':
              onDocumentGenerationEvent?.(eventType, eventData);
              await new Promise(r => requestAnimationFrame(r));
              break;
            case 'error':
              onError?.(eventData);
              throw new Error(eventData.message || 'Streaming error');
            default:
              break;
          }
        }
      }
    }
    
    return finalData;
  } catch (error) {
    onError?.({ message: error.message });
    throw error;
  }
};

const compactForChatHistory = (value, max = 700) => {
  if (value === undefined || value === null) return '';
  const text = typeof value === 'string' ? value : JSON.stringify(value);
  return text.replace(/\s+/g, ' ').trim().slice(0, max);
};

const summarizeToolActivityForHistory = (msg) => {
  if (!msg || msg.sender !== 'bot') return '';

  const toolItems = [];
  const addTool = (toolLike) => {
    if (!toolLike?.tool) return;
    const params = toolLike.params || {};
    const result = toolLike.result || {};
    const resultSummary =
      result._context ||
      result.answer ||
      result.summary ||
      result.value ||
      result.message ||
      result.error ||
      result.subject ||
      result.status ||
      '';
    toolItems.push([
      `tool=${toolLike.tool}`,
      `status=${toolLike.status || result.status || 'unknown'}`,
      params.query ? `query="${compactForChatHistory(params.query, 180)}"` : null,
      params.capability ? `capability=${params.capability}` : null,
      params.task_id ? `task_id=${params.task_id}` : null,
      result.id ? `task_result_id=${result.id}` : null,
      result.sent_at ? `sent_at=${result.sent_at}` : null,
      resultSummary ? `result="${compactForChatHistory(resultSummary, 500)}"` : null,
    ].filter(Boolean).join(' | '));
  };

  (msg.segments || [])
    .filter(seg => seg.type === 'tool')
    .forEach(addTool);
  if (toolItems.length === 0) {
    (msg.agentToolCalls || []).forEach(addTool);
  }

  return toolItems.length
    ? `\n\n[Persisted tool activity from this assistant turn]\n${toolItems.map(item => `- ${item}`).join('\n')}`
    : '';
};

// Agent profiles mapping (matching routes/agents.js structure)
const AGENT_PROFILES = {
  DESIGN_LEAD: { id: "DESIGN_LEAD", name: "Product Lead", color: "blue.500" },
  UX_RESEARCH: { id: "UX_RESEARCH", name: "UX Research Lead", color: "green.500" },
  UX_DESIGN: { id: "UX_DESIGN", name: "UX Design Lead", color: "orange.500" },
  UI_DESIGN: { id: "UI_DESIGN", name: "UI Design Lead", color: "pink.500" },
  PROJECT_MANAGER: { id: "PROJECT_MANAGER", name: "Project Manager", color: "purple.500" },
};

// Create a Chakra-styled TextareaAutosize component
const AutoResizeTextarea = chakra(TextareaAutosize, {
  baseStyle: {
    width: "100%",
    padding: "8px 16px",
    border: "1px solid",
    borderColor: "gray.300",
    fontSize: "md",
    overflowY: "auto",
  },
});

// Disable tooltips on touch devices (iOS Safari fires synthetic mouseenter on tap,
// causing tooltips to appear immediately on open and never dismiss).
const isTouch =
  typeof window !== "undefined" && window.matchMedia("(hover: none)").matches;

const LoadingIndicator = () => (
  <Flex align="center" justify="flex-start" p={1}>
    <Spinner size="sm" color="blue.500" thickness="2px" mr={2} />
    <Text fontSize="sm" color="gray.500">
      🤖 Analyzing your plan with AI agents...
    </Text>
  </Flex>
);

// Copy message button component
const CopyMessageButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const toast = useToast();

  const handleCopy = async () => {
    try {
      // Convert markdown to clean plain text while preserving readable content
      let cleanText = text
        // Remove bold markers (** or __), adding spaces to prevent word merging
        // when gpt-5.2 writes markers without surrounding spaces (e.g. "a**word**b")
        .replace(/(\w)\*\*(.*?)\*\*(\w)/g, '$1 $2 $3')
        .replace(/(\w)\*\*(.*?)\*\*/g, '$1 $2')
        .replace(/\*\*(.*?)\*\*(\w)/g, '$1 $2')
        .replace(/\*\*(.*?)\*\*/g, '$1')
        .replace(/(\w)__(.*?)__(\w)/g, '$1 $2 $3')
        .replace(/(\w)__(.*?)__/g, '$1 $2')
        .replace(/__(.*?)__(\w)/g, '$1 $2')
        .replace(/__(.*?)__/g, '$1')
        // Remove italic markers (* or _) - be careful not to affect asterisks in lists
        .replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, '$1')
        .replace(/(?<!_)_([^_\n]+)_(?!_)/g, '$1')
        // Remove inline code backticks
        .replace(/`([^`]+)`/g, '$1')
        // Remove code block markers
        .replace(/```[\s\S]*?```/g, (match) => match.replace(/```\w*\n?/g, '').replace(/```/g, ''))
        // Remove header markers but keep text
        .replace(/^#{1,6}\s+/gm, '')
        // Convert links to just the text
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        // Convert blockquotes to regular text
        .replace(/^>\s+/gm, '')
        // Keep bullet points readable
        .replace(/^[-*+]\s+/gm, '• ')
        // Keep numbered lists readable
        .replace(/^\d+\.\s+/gm, (match) => match)
        // Clean up extra whitespace
        .replace(/\n{3,}/g, '\n\n')
        .trim();
      
      await navigator.clipboard.writeText(cleanText);
      setCopied(true);
      toast({
        title: "Copied to clipboard",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
      toast({
        title: "Failed to copy",
        description: "Please try again",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    }
  };

  return (
    <Tooltip label={copied ? "Copied!" : "Copy message"} placement="top" isDisabled={isTouch}>
      <IconButton
        icon={copied ? <CheckIcon /> : <CopyIcon />}
        size="xs"
        variant="ghost"
        colorScheme={copied ? "green" : "gray"}
        aria-label="Copy message"
        onClick={handleCopy}
        opacity={0.6}
        _hover={{ opacity: 1 }}
      />
    </Tooltip>
  );
};

// Dynamic agent utility functions
const getAgentDisplayName = (agentInfo) => {
  if (!agentInfo) return "Pabs' Project Assistant";

  // If agentInfo has agentId, use AGENT_PROFILES to get proper name
  if (agentInfo.agentId && AGENT_PROFILES[agentInfo.agentId]) {
    return AGENT_PROFILES[agentInfo.agentId].name;
  }

  // Fallback to agent_name if available (for backward compatibility)
  if (agentInfo.agent_name) {
    return agentInfo.agent_name;
  }

  return "Pabs' Project Assistant";
};

const getAgentColor = (agentInfo) => {
  if (!agentInfo) return "gray.500";

  // If agentInfo has agentId, use AGENT_PROFILES to get proper color
  if (agentInfo.agentId && AGENT_PROFILES[agentInfo.agentId]) {
    return AGENT_PROFILES[agentInfo.agentId].color;
  }

  // Fallback to role-based mapping if available
  if (agentInfo.role && AGENT_PROFILES[agentInfo.role]) {
    return AGENT_PROFILES[agentInfo.role].color;
  }

  // Legacy fallback using agent_name patterns
  if (agentInfo.agent_name) {
    if (agentInfo.agent_name.includes("Product Lead")) return "blue.500";
    if (agentInfo.agent_name.includes("UX Research")) return "green.500";
    if (agentInfo.agent_name.includes("UX Design")) return "orange.500";
    if (agentInfo.agent_name.includes("UI Design")) return "pink.500";
    if (agentInfo.agent_name.includes("Project Manager")) return "purple.500";
  }

  return "gray.500";
};

const COMPONENT_MAP = {
  metricsStatement: "### Recommended Metrics",
  objectivesStatement: "### Recommended Key Objectives",
  stakeholdersCustomersInvolvement:
    "### Outlined Stakeholders and Customers Involvement",
};

// ---------------------------------------------------------------------------
// StreamingTextBox — fixed-height viewport that auto-scrolls to the latest
// streamed line. Uses overflowY:scroll with a hidden scrollbar so the outer
// maskImage gradient still works without the text staying stuck at the top.
// ---------------------------------------------------------------------------
function StreamingTextBox({ content, ml, mt }) {
  const ref = React.useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.scrollTop = ref.current.scrollHeight;
  }, [content]);
  return (
    <Box
      ref={ref}
      ml={ml}
      mt={mt}
      className="streaming-scroll-hide"
      style={{
        height: '52px',
        overflowY: 'scroll',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        maskImage: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.9) 35%)',
        WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.9) 35%)',
      }}
    >
      <Text fontSize="10px" style={{ color: '#A0AEC0' }} fontStyle="italic" lineHeight="1.5">
        {content}
      </Text>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// PhasePill — renders a single T or W phase pill in a message bubble
// phase: { id: 'T'|'W', state: 'active'|'done'|'error', label: string, tasks: [] }
// ---------------------------------------------------------------------------
const PhasePill = ({ phase, isLast, expanded, onToggle }) => {
  const isActive = phase.state === 'active';
  const hasError = phase.state === 'error';
  const doneTasks = (phase.tasks || []).filter(t => t.state === 'done').length;
  const totalTasks = (phase.tasks || []).length;
  const hasTasks = totalTasks > 0;
  const isW = phase.id === 'W';
  const isC = phase.id === 'C';
  const isK = phase.id === 'K';

  // Done state is always the same PABS gray — only active state is coloured
  const doneCfg = { doneBg: '#F7FAFC', doneBorder: '#CBD5E0', doneText: '#4A5568' };

  const cfg = isW
    ? { icon: 'settings', activeBorder: '#FED7AA', activeText: '#C05621', activeIcon: '#ED8936', ...doneCfg,
        activeBg: '#FFFAF5',
        borderShimmer: 'linear-gradient(90deg,#FED7AA 20%,#ED8936 50%,#FED7AA 80%)',
        textShimmer:   'linear-gradient(90deg,#C05621 20%,#F6AD55 50%,#C05621 80%)' }
    : isC
    ? { icon: 'forum', activeBorder: '#BEE3F8', activeText: '#2B6CB0', activeIcon: '#4299E1', ...doneCfg,
        activeBg: '#F3F9FF',
        borderShimmer: 'linear-gradient(90deg,#BEE3F8 20%,#4299E1 50%,#BEE3F8 80%)',
        textShimmer:   'linear-gradient(90deg,#2B6CB0 20%,#63B3ED 50%,#2B6CB0 80%)' }
    : isK
    ? { icon: 'language', activeBorder: '#CBD5E0', activeText: '#4A5568', activeIcon: '#718096', ...doneCfg,
        activeBg: '#FAFBFC',
        borderShimmer: 'linear-gradient(90deg,#CBD5E0 20%,#718096 50%,#CBD5E0 80%)',
        textShimmer:   'linear-gradient(90deg,#4A5568 20%,#A0AEC0 50%,#4A5568 80%)' }
    : { icon: 'auto_awesome', activeBorder: '#CBD5E0', activeText: '#4A5568', activeIcon: '#718096', ...doneCfg,
        activeBg: '#FAFBFC',
        borderShimmer: 'linear-gradient(90deg,#CBD5E0 20%,#718096 50%,#CBD5E0 80%)',
        textShimmer:   'linear-gradient(90deg,#4A5568 20%,#A0AEC0 50%,#4A5568 80%)' };

  const allTasksDone = hasTasks && doneTasks === totalTasks;
  // Unified label — null means icon-only (T done with no tasks)
  const label = isActive
    ? (isW ? (phase.label || 'Working...')
           : isC ? (allTasksDone ? 'Synthesizing...' : `Consulting... ${doneTasks}/${totalTasks}`)
           : isK ? 'Checking sources...'
           : 'Thinking...')
    : hasError ? 'Failed'
    : hasTasks ? (isK ? `\u2295 ${totalTasks} ${totalTasks === 1 ? 'source' : 'sources'}` : `${totalTasks} ${totalTasks === 1 ? 'task' : 'tasks'}`)
    : isW ? (phase.label || 'Done')
    : null;

  const iconColor = hasError ? '#FC8181' : isActive ? cfg.activeIcon : cfg.doneText;
  const textColor = hasError ? '#E53E3E' : isActive ? cfg.activeText : cfg.doneText;
  const borderColor = hasError ? '#FEB2B2' : isActive ? cfg.activeBorder : cfg.doneBorder;
  // Active: gradient border via two background layers (padding-box fill + border-box gradient)
  const bgStyle = isActive
    ? {
        border: '1px solid transparent',
        backgroundImage: `linear-gradient(${cfg.activeBg}, ${cfg.activeBg}), ${cfg.borderShimmer}`,
        backgroundClip: 'padding-box, border-box',
        backgroundOrigin: 'padding-box, border-box',
        backgroundSize: 'auto, 560px 100%',
      }
    : { backgroundColor: hasError ? '#FFF5F5' : cfg.doneBg };

  const separator = !isLast && (
    <Text fontSize="10px" color="gray.300" userSelect="none" flexShrink={0} mx="1px">·</Text>
  );

  // Single unified DOM node — React keeps it alive across active→done so CSS transitions fire
  return (
    <Flex align="center" className="phase-pill-enter">
      <Flex
        as={!isActive && hasTasks ? 'button' : 'div'}
        align="center" gap={1.5}
        px={3} minH="28px"
        borderRadius="full"
        border={isActive ? undefined : "1px solid"}
        borderColor={isActive ? undefined : borderColor}
        cursor={!isActive && hasTasks ? 'pointer' : 'default'}
        _hover={!isActive && hasTasks ? { opacity: 0.85 } : {}}
        onClick={!isActive && hasTasks ? onToggle : undefined}
        overflow="hidden"
        className={isActive ? 'phase-pill-compress phase-shimmer' : 'phase-pill-compress'}
        style={{ ...bgStyle, transition: 'color 0.2s ease' }}
      >
        <Box as="span" className="material-icons-outlined"
          style={{ fontSize: '12px', color: iconColor, transition: 'color 0.2s ease' }}>
          {hasError ? 'error_outline' : cfg.icon}
        </Box>
        {label && (
          <Text fontSize="xs" fontWeight="medium" userSelect="none"
            className={isActive ? 'phase-text-shimmer' : ''}
            style={{ color: textColor, transition: 'color 0.2s ease',
              ...(isActive ? { backgroundImage: cfg.textShimmer } : {}) }}>
            {label}
          </Text>
        )}
        {isActive && hasTasks && !isC && (
          <Text fontSize="10px" opacity={0.7} userSelect="none" style={{ color: textColor }}>
            {doneTasks}/{totalTasks}
          </Text>
        )}
        {isActive && hasTasks && isC && (
          <Flex align="center" gap="2px">
            {(phase.tasks || []).map(t => (
              <Avatar key={t.id} name={t.label} size="xs"
                bg={t.state === 'done' ? 'green.400' : 'blue.300'} color="white"
                style={{ width: '16px', height: '16px', fontSize: '7px', fontWeight: 700, transition: 'background-color 0.3s ease' }} />
            ))}
          </Flex>
        )}
        {!isActive && hasTasks && (
          <Box as="span" className="material-icons-outlined"
            style={{ fontSize: '11px', color: cfg.doneText, opacity: 0.5 }}>
            {expanded ? 'expand_less' : 'expand_more'}
          </Box>
        )}
      </Flex>
      {separator}
    </Flex>
  );
};

// Agent attribution pill — appended at the end of the breadcrumb line
const AgentPill = ({ agentInfo }) => {
  const name = getAgentDisplayName(agentInfo);
  const color = getAgentColor(agentInfo);
  return (
    <Flex
      align="center" gap={1.5} px={3} minH="28px"
      borderRadius="full" border="1px solid" borderColor="gray.200"
      bg="white"
    >
      <Box w="6px" h="6px" borderRadius="full" bg={color} flexShrink={0} />
      <Text fontSize="xs" fontWeight="medium" color="gray.600" userSelect="none">{name}</Text>
      <Box as="span" className="material-icons-outlined"
        style={{ fontSize: '11px', color: '#CBD5E0' }}>
        star
      </Box>
    </Flex>
  );
};

const FloatingChatbot = React.memo(
  forwardRef(
    (
      {
        currentDesignChallenge,
        triggeredByButton,
        prefilledPrompt,
        selectedPrograms,
        shortSummary,
        goalStatement,
        problemStatement,
        targetStatement,
        objectivesStatement,
        handleComponentAction,
        handleDurationChange,
        finalProgramSequence,
        deliverablesStatement,
        metricsStatement,
        stakeholdersCustomersInvolvement,
        totalDuration,
        isEmbedded,
        workflowStatus,
        isStrategyCreationMode,
        planId,
        currentWorkflowId,
        uiConfig,
        programsOfWork,
        methods,
        tools,
        deliverables,
        strategyWorkflowManager,
        isCollapsed,
        onNavigateToView,
        currentView,
        onOpenShareModal,
        onOpenMcpModal,
        planContext,
        onOpenLearnings,
        onLearningsCountChanged,
        onExecutionStateChange,
        planStatus,
      },
      ref,
    ) => {
      const location = useLocation();
      const navigate = useNavigate();
      const isOnBriefPage = location.pathname === '/' || location.pathname === '/brief';
      const { currentPlanId } = useContext(UserContext);
      const showGitHubBanner = !!currentPlanId && !isOnBriefPage;
      const planStatusRef = useRef(planStatus);
      useEffect(() => { planStatusRef.current = planStatus; }, [planStatus]);
      const [isChatTopUpOpen, setIsChatTopUpOpen] = useState(false);
      useEffect(() => {
        const openTopUp = () => setIsChatTopUpOpen(true);
        window.addEventListener('pabs:openTopUpModal', openTopUp);
        return () => window.removeEventListener('pabs:openTopUpModal', openTopUp);
      }, []);

      // Don't show initial message for strategy creation mode - let the user's challenge come first
      const shouldShowInitialMessage =
        !isStrategyCreationMode || !currentDesignChallenge;

      // UI-only state
      const [isOpen, setIsOpen] = useState(false);

      // Auto-start a generation workflow when another component fires pabs:autoStartGeneration
      // (e.g. EmbeddedChatPanel routing quick_plan creation through SSE instead of legacy workflow)
      useEffect(() => {
        const handler = (e) => {
          const { capability } = e.detail || {};
          if (!capability) return;
          executeGenerationWorkflowRef.current?.(capability);
        };
        window.addEventListener('pabs:autoStartGeneration', handler);
        return () => window.removeEventListener('pabs:autoStartGeneration', handler);
      }, []); // empty deps — always calls latest via ref

      const [input, setInput] = useState("");
      const [pendingAttachments, setPendingAttachments] = useState([]);
      const [attachmentError, setAttachmentError] = useState("");
      const [showFormatHelp, setShowFormatHelp] = useState(false);
      const [isDragActive, setIsDragActive] = useState(false);
      const fileInputRef = useRef(null);
      const inputRef = useRef(null);
      const activeRefinementRef = useRef(null);
      const [loading, setLoading] = useState(false);
      const [loadingPhase, setLoadingPhase] = useState("routing");
      const [selectedAgent, setSelectedAgent] = useState(null);
      const [selectedAgents, setSelectedAgents] = useState([]);
      const [interactionMode, setInteractionMode] = useState('advisory'); // 'advisory' | 'work'
      const [activeAgents, setActiveAgents] = useState([]); // used by W-phase polling and legacy agent bar
      const leadAgentRef = useRef(null); // set synchronously in onAgentStart — no React cycle dependency
      const pendingGenExpandRef = useRef(false); // set to true by executeGenerationWorkflow on completion; useEffect auto-expands W pill
      const executeGenerationWorkflowRef = useRef(null); // updated each render — used by pabs:autoStartGeneration listener
      const [workExpanded, setWorkExpanded] = useState(null); // messageIndex | null
      const [expandedLogId, setExpandedLogId] = useState(null); // agentId | null
      const messagesEndRef = useRef(null);
      const messagesContainerRef = useRef(null);
      const virtualizerRef = useRef(null); // updated after virtualizer is created
      const lastStreamRenderRef = useRef(0);
      const lastLoadedCountRef = useRef(0); // tracks how many messages are historical (no animation)
      const isBackendExecutingRef = useRef(false); // guard: prevents chatStateManager subscriber from wiping messages during backend execution

      useEffect(() => {
        const handlePrefillPlanChat = async event => {
          if (event.detail?.planId && String(event.detail.planId) !== String(currentPlanId)) return;
          // Plan chat prefills belong exclusively to the embedded left panel.
          // A legacy global FloatingChatbot is also mounted on plan pages; keep
          // that instance closed so the event cannot create a duplicate chat.
          if (!isEmbedded) {
            setIsOpen(false);
            return;
          }
          const message = String(event.detail?.message || '').trim();
          if (!message) return;
          if (event.detail?.newConversation) {
            activeRefinementRef.current = null;
            const newConversation = await chatStateManager.startNewChat(true);
            setConversation(newConversation);
            setMessages([...(newConversation?.messages || [])]);
            setSuggestedContent([]);
            setTargetComponent(null);
            setIsAwaitingConfirmation(false);
            setAwaitingPlanCreation(null);
          }
          setInput(message);
          window.setTimeout(() => inputRef.current?.focus(), 50);
        };
        window.addEventListener('pabs:prefillPlanChat', handlePrefillPlanChat);
        return () => window.removeEventListener('pabs:prefillPlanChat', handlePrefillPlanChat);
      }, [currentPlanId, isEmbedded]);

      useEffect(() => {
        const handleAssistantMessage = event => {
          const text = String(event.detail?.text || '').trim();
          if (!text) return;
          if (currentPlanId && !isEmbedded) return;
          chatStateManager.addMessage({
            text,
            sender: "bot",
            isHtml: false,
            timestamp: new Date().toISOString(),
          });
        };
        window.addEventListener('pabs:assistantMessage', handleAssistantMessage);
        return () => window.removeEventListener('pabs:assistantMessage', handleAssistantMessage);
      }, [currentPlanId, isEmbedded]);
      const closingConvHistoryRef = useRef([]); // snapshot of last 2 messages captured at stream-finalize time (avoids stale closure in executeGenerationWorkflow)
      const isFinalizingStreamRef = useRef(false); // guard: prevents double-render flicker when replaceLastPlaceholder + setMessages both fire
      const createPlanInFlightRef = useRef(false); // idempotency guard: prevents duplicate create_plan SSE streams
      const createPlanRequestIdRef = useRef(null);

      const acquireCreatePlanBrowserLock = () => {
        if (typeof window === 'undefined') return true;
        if (window.__pabsCreatePlanInFlight === true) return false;
        window.__pabsCreatePlanInFlight = true;
        return true;
      };

      const releaseCreatePlanBrowserLock = () => {
        if (typeof window !== 'undefined') {
          window.__pabsCreatePlanInFlight = false;
        }
      };

      // Chat state managed by ChatStateManager
      const [conversation, setConversation] = useState(null);

      // Legacy state for compatibility with existing code
      const [suggestedContent, setSuggestedContent] = useState([]);
      const [targetComponent, setTargetComponent] = useState(null);
      const [isAwaitingConfirmation, setIsAwaitingConfirmation] =
        useState(false);
      
      // Plan creation confirmation state
      const [awaitingPlanCreation, setAwaitingPlanCreation] = useState(null); // { planType: 'quick_plan' | 'autonomous_design_strategy', conversationSummary: string }

      // Conversation state (for backward compatibility)
      const currentConversationId = conversation?.id;
      const setCurrentConversationId = (id) => {
        // This is handled by ChatStateManager now, but keeping for compatibility
        console.log("Legacy setCurrentConversationId called with:", id);
      };
      const [messages, setMessages] = useState([]);
      const [compressionSuggested, setCompressionSuggested] = useState(false);
      const [compressing, setCompressing] = useState(false);

      // Handle agent selection from dropdown
      const handleAgentSelect = (enabledAgents) => {
        console.log("🤖 FloatingChatbot: Agent selection changed:", enabledAgents?.map(a => a.name));
        setSelectedAgents(enabledAgents || []);
      };

      // Strips trailing unclosed bold/italic markers so partial streaming text
      // doesn't render literal asterisks mid-sentence.
      const sanitizeStreamingMarkdown = (text) => {
        if (!text || typeof text !== 'string') return text;
        let result = text;
        const boldCount = (result.match(/\*\*/g) || []).length;
        if (boldCount % 2 !== 0) {
          result = result.replace(/\*\*([^*]*)$/, "$1");
        }
        const italicCount = (result.match(/(?<!\*)\*(?!\*)/g) || []).length;
        if (italicCount % 2 !== 0) {
          result = result.replace(/(?<!\*)\*([^*]*)$/, "$1");
        }
        return result;
      };

      // Helper: Strip only CONTROL-SIGNAL JSON from text for display (doesn't parse CTAs).
      // Legitimate code/JSON the assistant wants to show is preserved and rendered as-is.
      const stripJsonForDisplay = (text) => {
        if (!text || typeof text !== 'string') return text;

        const original = text;
        let clean = text;

        // 1) Strip control-signal fenced blocks outright — the tag itself marks them as
        //    control payloads, never meant for display. Replace with a space to prevent word merge.
        clean = clean.replace(/```(?:cta_json|detected_learnings)\s*\n?[\s\S]*?```/gi, ' ');
        // Incomplete/streaming control fence — tag REQUIRED, so plain code blocks are never touched.
        clean = clean.replace(/```(?:cta_json|detected_learnings)[\s\S]*/gi, '');

        // 2) Strip ```json fences ONLY when they carry a machine control payload — a CTA,
        //    a capability instruction, or a plan/navigation action (see routes/agents.js and
        //    routes/chat.js prompt formats). A ```json block the assistant is showing purely
        //    for reference (no control marker) is left intact and rendered.
        const controlPayloadRegex = /"cta"\s*:|"capability"\s*:|"action"\s*:\s*"(?:create_plan|navigate_tab|quick_reply|open_modal)"|"type"\s*:\s*"navigate_tab"/;
        clean = clean.replace(/```json\s*\n?[\s\S]*?```/gi, (block) => (controlPayloadRegex.test(block) ? ' ' : block));

        // 3) Protect every remaining fenced code block (any language) so the raw-JSON cleanup
        //    below can't corrupt legitimate code/JSON the assistant is displaying.
        const preserved = [];
        const stash = (block) => {
          preserved.push(block);
          return `\u0000FENCE${preserved.length - 1}\u0000`;
        };
        clean = clean.replace(/```[\s\S]*?```/g, stash);
        // Protect a trailing incomplete fence (still streaming) so it renders as a growing code block.
        clean = clean.replace(/```[\s\S]*$/g, stash);

        // 4) Strip raw (un-fenced) control JSON from the remaining prose.
        const capabilityStartRegex = /\{\s*"capability"\s*:/g;
        let capMatch;
        const ranges = [];
        while ((capMatch = capabilityStartRegex.exec(clean)) !== null) {
          const startIdx = capMatch.index;
          let braceCount = 0;
          let endIdx = -1;
          for (let i = startIdx; i < clean.length; i++) {
            if (clean[i] === '{') braceCount++;
            if (clean[i] === '}') braceCount--;
            if (braceCount === 0 && i > startIdx) {
              endIdx = i + 1;
              break;
            }
          }
          if (endIdx > startIdx) {
            ranges.push([startIdx, endIdx]);
          } else {
            ranges.push([startIdx, clean.length]);
          }
        }
        for (let i = ranges.length - 1; i >= 0; i--) {
          // Replace with a space (not empty string) so surrounding words don't merge
          clean = clean.substring(0, ranges[i][0]) + ' ' + clean.substring(ranges[i][1]);
        }

        // Strip raw JSON arrays that look like CTAs
        clean = clean.replace(/\[\s*\{[^[\]]*?"action"\s*:\s*"create_plan"[^[\]]*?\}\s*(?:,\s*\{[^[\]]*?"action"\s*:\s*"create_plan"[^[\]]*?\}\s*)*\]/g, '');
        // Strip navigation CTA JSON
        clean = clean.replace(/\{\s*"type"\s*:\s*"navigate_tab"[^}]*\}/g, '');

        // Strip announcement phrases that precede JSON blocks (poor UX to show these)
        clean = clean.replace(/Here['']s the JSON[^:]*:?\s*/gi, '');
        clean = clean.replace(/Executing the change now!?\s*/gi, '');
        clean = clean.replace(/Now,?\s*I['']ll execute the change!?\s*/gi, '');
        clean = clean.replace(/Let me execute this:?\s*/gi, '');
        clean = clean.replace(/I['']ll proceed with the modification:?\s*/gi, '');

        clean = clean.replace(/\*{0,2}\s*\}{2,}\s*\*{0,2}/g, '');
        clean = clean.replace(/(?:^|\n)\s*\}\s*(?:\n|$)/g, '\n');

        // 5) Restore the preserved fenced code blocks.
        clean = clean.replace(/\u0000FENCE(\d+)\u0000/g, (m, i) => preserved[Number(i)] ?? '');

        if (clean !== original) { /* json stripped */ }

        return clean.trim();
      };

      // FIX #52: Parse CTAs from AI message text for navigation and plan creation
      const parseCTAsFromMessage = (messageText) => {
        if (!messageText || typeof messageText !== 'string') return { cleanText: messageText, ctas: [] };
        
        const ctas = [];
        let cleanText = messageText;
        
        // Pattern 1: Match JSON blocks with CTA format: ```json\n{"cta": {...}}\n```
        const ctaPattern = /```json\s*\n?\s*(\{"cta":\s*\{[^}]+\}\})\s*\n?```/gi;
        
        let match;
        while ((match = ctaPattern.exec(messageText)) !== null) {
          try {
            const parsed = JSON.parse(match[1]);
            if (parsed.cta && parsed.cta.type === 'navigate_tab' && parsed.cta.tabId) {
              ctas.push({
                type: parsed.cta.type,
                tabId: parsed.cta.tabId,
                label: parsed.cta.label || `View ${parsed.cta.tabId}`,
              });
            }
          } catch (e) {
            console.warn('Failed to parse CTA JSON:', e);
          }
        }
        
        // Pattern 2: Match cta_json blocks for plan creation: ```cta_json\n[{...}]\n```
        const ctaJsonPattern = /```cta_json\s*\n?\s*(\[[\s\S]*?\])\s*\n?```/gi;
        
        let ctaJsonMatch;
        while ((ctaJsonMatch = ctaJsonPattern.exec(messageText)) !== null) {
          try {
            const parsed = JSON.parse(ctaJsonMatch[1]);
            if (Array.isArray(parsed)) {
              parsed.forEach(ctaItem => {
                if (ctaItem.action === 'create_plan' && ctaItem.plan_type) {
                  ctas.push({
                    type: 'create_plan',
                    planType: ctaItem.plan_type,
                    briefSummary: ctaItem.brief_summary || '',
                    label: ctaItem.label || `Create ${ctaItem.plan_type === 'quick_plan' ? 'Quick' : 'Full'} Plan`,
                  });
                }
                if (ctaItem.action === 'quick_reply' && ctaItem.reply_key) {
                  ctas.push({
                    type: 'quick_reply',
                    replyKey: ctaItem.reply_key,
                    label: ctaItem.label || ctaItem.reply_key,
                    style: ctaItem.style || 'default',
                  });
                }
                if (ctaItem.action === 'open_modal' && ctaItem.modal) {
                  ctas.push({
                    type: 'open_modal',
                    modal: ctaItem.modal,
                    label: ctaItem.label || 'Open',
                    style: ctaItem.style || 'default',
                  });
                }
                if (ctaItem.action === 'navigate_tab' && ctaItem.tabId) {
                  ctas.push({
                    type: 'navigate_tab',
                    tabId: ctaItem.tabId,
                    label: ctaItem.label || ctaItem.tabId,
                    style: ctaItem.style || 'default',
                  });
                }
              });
            }
          } catch (e) {
            console.warn('Failed to parse cta_json:', e);
          }
        }
        
        // Pattern 3: FALLBACK - Match raw JSON arrays with create_plan structure (no code fence)
        // This catches cases where AI outputs JSON without proper code fence wrapper
        if (ctas.length === 0 && messageText.includes('"action"') && messageText.includes('"create_plan"')) {
          const rawJsonPattern = /\[\s*\{[^[\]]*?"action"\s*:\s*"create_plan"[^[\]]*?\}\s*(?:,\s*\{[^[\]]*?"action"\s*:\s*"create_plan"[^[\]]*?\}\s*)*\]/g;
          let rawMatch;
          while ((rawMatch = rawJsonPattern.exec(messageText)) !== null) {
            try {
              const parsed = JSON.parse(rawMatch[0]);
              if (Array.isArray(parsed)) {
                parsed.forEach(ctaItem => {
                  if (ctaItem.action === 'create_plan' && ctaItem.plan_type) {
                    ctas.push({
                      type: 'create_plan',
                      planType: ctaItem.plan_type,
                      briefSummary: ctaItem.brief_summary || '',
                      label: ctaItem.label || `Create ${ctaItem.plan_type === 'quick_plan' ? 'Quick' : 'Full'} Plan`,
                    });
                  }
                });
              }
            } catch (e) {
              console.warn('Failed to parse raw CTA JSON:', e);
            }
          }
          
          // Also remove the raw JSON from cleanText
          if (ctas.length > 0) {
            cleanText = messageText.replace(rawJsonPattern, '').trim();
          }
        }
        
        // Remove CTA JSON blocks from the displayed text
        cleanText = messageText.replace(ctaPattern, '').replace(ctaJsonPattern, '').trim();
        
        // CRITICAL FIX: Also strip capability JSON from display (complete objects)
        // This handles cases where AI outputs capability JSON without code fences
        cleanText = cleanText.replace(/\{\s*"capability"\s*:\s*"[^"]+"\s*,\s*"data"\s*:\s*\{[\s\S]*?\}\s*\}/g, '');
        
        // Also strip partial capability JSON (incomplete objects during streaming edge cases)
        const capabilityStartMatch = cleanText.match(/\{\s*"capability"\s*:/);
        if (capabilityStartMatch) {
          const startIndex = cleanText.indexOf(capabilityStartMatch[0]);
          const afterCapability = cleanText.substring(startIndex);
          let braceCount = 0;
          let isComplete = false;
          for (let i = 0; i < afterCapability.length; i++) {
            if (afterCapability[i] === '{') braceCount++;
            if (afterCapability[i] === '}') braceCount--;
            if (braceCount === 0 && i > 0) {
              isComplete = true;
              break;
            }
          }
          if (!isComplete) {
            cleanText = cleanText.substring(0, startIndex);
          }
        }
        
        // Clean up any trailing/leading whitespace
        cleanText = cleanText.trim();
        
        return { cleanText, ctas };
      };

      // FIX #52: Handle CTA navigation and plan creation clicks
      const handleCTAClick = (cta) => {
        console.log('🔗 CTA clicked:', cta);
        
        if (cta.type === 'navigate_tab' && cta.tabId && onNavigateToView) {
          onNavigateToView(cta.tabId);
        } else if (cta.type === 'create_plan' && cta.planType) {
          // Trigger plan creation using existing workflowLaunchCache pattern (same as ClientBriefForm)
          console.log('🚀 Creating plan from conversation:', cta);
          
          // Build the conversation context as the design challenge
          const conversationMessages = messages.filter(m => m.sender === 'user').map(m => m.text).join('\n\n');
          const designChallenge = cta.briefSummary || conversationMessages || 'Design challenge from conversation';
          
          // Map plan_type to workflowType
          const workflowType = cta.planType === 'quick_plan' ? 'quick_plan' : 'autonomous_design_strategy';
          
          // Use the existing workflowLaunchCache pattern (same as ClientBriefForm)
          const cacheKey = `workflow_${Date.now()}`;
          const launchState = {
            designChallenge,
            isNewPlan: true,
            initiateStrategyCreation: true,
            sourceFlow: 'conversation_mode',
            workflowType,
            selectedTool: {
              id: workflowType,
              name: workflowType === 'quick_plan' ? 'Quick Strategy' : 'Full Strategy',
            },
            isAutonomous: true,
            experienceOption: 'autonomous',
          };
          
          // Cache the launch state (survives React strict mode remounts)
          workflowLaunchCache.set(cacheKey, launchState);
          
          console.log('🚀 NAVIGATING TO /plan with cached state:', {
            cacheKey,
            workflowType,
            designChallenge: designChallenge.substring(0, 100) + '...',
          });
          
          // Navigate using React Router with _cacheKey
          navigate('/plan', {
            state: {
              ...launchState,
              _cacheKey: cacheKey,
            },
          });
        } else if (cta.type === 'quick_reply' && cta.replyKey) {
          // Add user message echoing their selection
          if (chatStateManager?.addMessage) {
            chatStateManager.addMessage({ text: cta.label, sender: 'user', timestamp: new Date().toISOString() });
          }
          // Personalize show_chat_editing with actual brief content; fall back to static for others
          const response = cta.replyKey === 'show_chat_editing'
            ? buildShowChatEditingMessage(planContext)
            : POST_STRATEGY_REPLIES[cta.replyKey];
          if (response && chatStateManager?.addMessage) {
            chatStateManager.addMessage({ text: response, sender: 'bot', isHtml: false, timestamp: new Date().toISOString() });
          }
        } else if (cta.type === 'open_modal') {
          if (cta.modal === 'share' && onOpenShareModal) onOpenShareModal();
          if (cta.modal === 'mcp' && onOpenMcpModal) onOpenMcpModal();
        }
      };

      // Initialize chat state when component mounts or plan changes
      // FIX #43: Also initialize for brief page (when currentPlanId is null)
      useEffect(() => {
        console.log(
          "FloatingChatbot: Initializing chat state",
          currentPlanId ? `for plan ${currentPlanId}` : "for brief page (no plan)",
        );
        
        // Handle async initialization
        const initializeChat = async () => {
          // Initialize for plan if we have one, otherwise create a planless conversation
          const activeConversation = currentPlanId 
            ? await chatStateManager.initializeForPlan(currentPlanId)
            : await chatStateManager.getOrCreateBriefConversation();

          // Add welcome message only if conversation is truly new (not persisted from database)
          // and has no messages when user opens the chat
          if (
            activeConversation &&
            activeConversation.messages.length === 0 &&
            !activeConversation.isPersisted &&
            (isOpen || isEmbedded) &&
            !currentPlanId   // plan welcome is handled by post-strategy or returning-user flows
          ) {
            // CRITICAL FIX: Use different welcome message depending on context
            // Brief page (no plan) = pure consultant mode
            // Plan page (has plan) = plan assistance mode
            const welcomeText = currentPlanId 
              ? "Hi! I'm here to help you with your design plan. Feel free to ask me anything about your strategy, programs, or tools. You can change things in your plan manually or ask me to do it."
              : "Hi! What are you working on? I can help you quickly figure out what matters and what to do next.";
            
            chatStateManager.addMessage({
              text: welcomeText,
              sender: "bot",
              isHtml: false,
            });
          }

          if (activeConversation) {
            console.log(`[PABS-DEBUG][initializeChat] convId=${activeConversation.id} msgCount=${activeConversation.messages.length}`, activeConversation.messages.map((m,i)=>({i,sender:m.sender,ts:m.timestamp,text:(m.text||'').slice(0,60)})));
            setConversation(activeConversation);
            setMessages(prev => {
              if (activeConversation.messages.length === 0 && prev.length > 0) return prev;
              return [...activeConversation.messages];
            });
          }
        };
        
        initializeChat();
      }, [currentPlanId, isOpen, isEmbedded]);

      // Subscribe to chat state changes
      useEffect(() => {
        const unsubscribe = chatStateManager.subscribe(async (event) => {

          switch (event.type) {
            case "message_added":
            case "message_updated":
            case "conversation_loaded":
            case "conversation_created": {
              // Note: conversation_saved is intentionally excluded — it's a write-only operation
              // and re-syncing messages after a save would wipe any in-progress streaming state.
              //
              // CRITICAL: check the ref BEFORE the async call — checking after allows a race where
              // the ref clears while getActiveConversation() is awaited, causing state overwrite.
              if (isBackendExecutingRef.current || isFinalizingStreamRef.current) break;
              const updatedConversation =
                await chatStateManager.getActiveConversation();
              if (updatedConversation) {
                setConversation(updatedConversation);
                const safeMessages = updatedConversation.messages || [];
                console.log(`[PABS-DEBUG][subscriber] event=${event.type} convId=${updatedConversation.id} msgCount=${safeMessages.length}`, safeMessages.map((m,i)=>({i, sender:m.sender, ts:m.timestamp, text:(m.text||'').slice(0,60)})));
                // On load/creation events, mark all current messages as historical (no animation)
                if (event.type === "conversation_loaded" || event.type === "conversation_created") {
                  lastLoadedCountRef.current = safeMessages.length;
                }
                // Second guard: bail out if execution is running OR stream finalization is syncing
                if (isBackendExecutingRef.current || isFinalizingStreamRef.current) break;
                console.log('[chatStateManager][debug] SECOND GUARD PASSED — event:', event.type, 'isBackendExecuting:', isBackendExecutingRef.current, 'isFinalizingStream:', isFinalizingStreamRef.current, new Error().stack?.split('\n').slice(0,4).join(' | '));
                console.log('[chatStateManager][debug] subscriber MERGING messages — event:', event.type, 'isBackendExecuting:', isBackendExecutingRef.current);
                // Merge phases+text from existing React state — phases are only in React,
                // not in chatStateManager. Without this, message_added events wipe inPlace phases.
                //
                // CRITICAL: iterate over prev (React's authoritative length), not safeMessages.
                // DB saves filter out progress UI messages, so chatStateManager can have fewer entries
                // than React state. Mapping over safeMessages would drop React-only messages and shift
                // indices, landing the phases message at the wrong position → phases cleared → stale
                // streaming text overwrites the execution confirmation ~5s later.
                setMessages(prev => {
                  if (!safeMessages.length) return prev;
                  // conversation_created / conversation_loaded: replace prev entirely.
                  // Merging would let messages from the previous conversation bleed into the
                  // new one when the new conversation has fewer messages than the old one
                  // (the !m → return existing branch would keep old messages at higher indices).
                  if (event.type === 'conversation_created' || event.type === 'conversation_loaded') {
                    return [...safeMessages];
                  }
                  // message_added / message_updated: merge to preserve in-progress phases.
                  // DB saves filter out progress UI messages, so chatStateManager can have fewer
                  // entries than React state. Mapping over safeMessages would drop React-only
                  // messages and shift indices, landing phases at the wrong position.
                  const maxLen = Math.max(prev.length, safeMessages.length);
                  return Array.from({ length: maxLen }, (_, i) => {
                    const m = safeMessages[i];
                    const existing = prev[i];
                    if (!m) return existing;   // React-only message (e.g. progress UI) — keep it
                    if (!existing) return m;   // chatStateManager-only — append
                    // During streaming, React state is authoritative — the chatStateManager snapshot
                  // is stale (isLoading: true, text: "..."). Returning `m` would wipe the intro text
                  // and reset isLoading, causing the flicker where the intro appears then disappears.
                  if (existing?.isStreaming) return existing;
                  if (existing?.phases || existing?.agentToolCalls?.length || existing?.segments?.length) return { ...m, ...(existing.phases ? { phases: existing.phases } : {}), text: existing.text, rawText: existing.rawText, ...(existing.closingText !== undefined ? { closingText: existing.closingText } : {}), ...(existing.agentToolCalls?.length ? { agentToolCalls: existing.agentToolCalls } : {}), ...(existing.segments?.length ? { segments: existing.segments } : {}) };
                    return m;
                  });
                });
              }
              break;
            }
          }
        });

        return unsubscribe;
      }, []);

      // Make AI Component Manager globally available
      useEffect(() => {
        window.aiComponentManager = aiComponentManager;
      }, []);


      useEffect(() => {
        scrollToBottom();
        // Fallback: re-scroll after virtualizer finishes measuring item heights
        const fallback = setTimeout(scrollToBottom, 200);
        return () => clearTimeout(fallback);
      }, [messages]);

      // Auto-expand W phase after generation completes so user sees task rows immediately
      useEffect(() => {
        if (!pendingGenExpandRef.current) return;
        pendingGenExpandRef.current = false;
        const idx = messages.findLastIndex(m =>
          !m._executing && !m.isGenerating &&
          m.phases?.some(p => p.id === 'W' && p.state === 'done' && p.tasks?.length > 0)
        );
        if (idx !== -1) setWorkExpanded(`${idx}-W`);
      }, [messages]);

      // Pin scroll to bottom while streaming so content scrolls up as it arrives
      useEffect(() => {
        if (!loading) return;
        const interval = setInterval(() => {
          if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          }
        }, 50);
        return () => clearInterval(interval);
      }, [loading]);

      const scrollToBottom = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const v = virtualizerRef.current;
            if (v && v.options.count > 0) {
              v.scrollToIndex(v.options.count - 1, { align: 'end', behavior: 'auto' });
            }
            // Always also force container scroll to bottom — handles growing message height during streaming
            if (messagesContainerRef.current) {
              messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            }
          });
        });
      };

      const handleUserConfirmation = useCallback(
        (confirmation) => {
          let actionMessage = "";

          switch (confirmation) {
            case "confirm":
              handleComponentAction("add", targetComponent, suggestedContent);
              actionMessage = `I've added the recommended **${COMPONENT_MAP[targetComponent]}** to your plan. **Remember to save your changes**.`;
              break;

            case "update":
              handleComponentAction(
                "update",
                targetComponent,
                suggestedContent,
              );
              actionMessage = `The **${COMPONENT_MAP[targetComponent]}** has been updated in your plan with the suggested content. **Remember to save your changes**.`;
              break;

            case "delete":
              handleComponentAction("delete", targetComponent);
              actionMessage = `The selected **${COMPONENT_MAP[targetComponent]}** has been removed from your plan.`;
              break;

            case "discard":
              actionMessage = `No changes made to your ${COMPONENT_MAP[targetComponent]}.`;
              break;

            default:
              actionMessage = "Invalid action.";
          }

          setMessages((prevMessages) => [
            ...prevMessages,
            { text: actionMessage, sender: "bot", isHtml: false, timestamp: new Date().toISOString() },
          ]);

          setSuggestedContent([]);
          setIsAwaitingConfirmation(false);
        },
        [handleComponentAction, targetComponent, suggestedContent],
      );

      // Handle agent action execution
      const handleAgentAction = async (action) => {
        try {
          // Get the active component from AIComponentInteractionManager
          const aiManager = window.aiComponentManager;
          if (!aiManager) {
            throw new Error("AI Component Manager not available");
          }

          // Execute the action on the component
          const result = await aiManager.executeAction(
            action.component_id,
            action.capability,
            action.data,
          );

          activeRefinementRef.current = null;

          setMessages((prev) => [
            ...prev,
            {
              text: `✅ **Action Applied Successfully**\n\n${action.description}\n\nResult: ${result.message || "Changes have been applied to your plan."}`,
              sender: "bot",
              timestamp: new Date().toISOString(),
              isHtml: false,
            },
          ]);

          // Trigger component refresh if needed
          //if (result.shouldRefresh && planPageRef.current) {
          //  planPageRef.current.refreshPlanData();
          //}
        } catch (error) {
          console.error("Agent action failed:", error);
          setMessages((prev) => [
            ...prev,
            {
              text: `❌ **Action Failed**\n\n${error.message}`,
              sender: "bot",
              timestamp: new Date().toISOString(),
              isHtml: false,
            },
          ]);
        }
      };

      // Use AIActionManager for parsing and processing actions
      const processAIResponse = async (aiResponse, context) => {
        try {
          // Parse AI response for actions
          const detectedActions = await aiActionManager.parseAIResponse(
            aiResponse,
            context,
          );

          // Generate previews for detected actions
          const actionsWithPreviews = await Promise.all(
            detectedActions.map(async (action) => {
              const preview = await aiActionManager.generatePreview(
                action,
                context,
              );
              return { ...action, preview };
            }),
          );

          // Clean the message text
          const cleanText = aiActionManager.cleanMessageText(
            aiResponse,
            detectedActions,
          );

          return {
            cleanText,
            actions: actionsWithPreviews,
          };
        } catch (error) {
          console.error("Error processing AI response:", error);
          return {
            cleanText: aiResponse,
            actions: [],
          };
        }
      };

      const handleCompressConversation = async () => {
        if (compressing) return;
        setCompressing(true);
        try {
          const token = localStorage.getItem('token');
          const conversation = await chatStateManager.getActiveConversation();
          const historyToCompress = (conversation?.messages || messages)
            .filter(m => !m.isLoading && !m.isPlaceholder)
            .map(m => ({ sender: m.sender, text: m.text || m.rawText || '' }));

          const res = await fetch('/api/compress-conversation', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ conversation_history: historyToCompress }),
          });
          if (!res.ok) throw new Error('Compression failed');
          const { summary } = await res.json();

          const summaryMessage = {
            id: `compressed_${Date.now()}`,
            sender: 'bot',
            text: summary,
            rawText: summary,
            isCompressedContext: true,
            timestamp: Date.now(),
          };
          setMessages([summaryMessage]);
          if (conversation) {
            conversation.messages = [summaryMessage];
            chatStateManager.saveConversation(conversation);
          }
          setCompressionSuggested(false);
        } catch (err) {
          console.error('Compression error:', err);
        } finally {
          setCompressing(false);
        }
      };

      // Simplified action handling using AIActionManager
      const [isExecutingAction, setIsExecutingAction] = useState(false);
      const [executingActionId, setExecutingActionId] = useState(null);
      const [executedBackendActions, setExecutedBackendActions] = useState(new Set());

      // All capabilities routed through the backend execution endpoint (Tier A + Tier C)
      const TIER_A_CAPABILITIES = new Set([
        // Tier A — Brief text fields
        'update_summary', 'update_problem_statement', 'update_goal_statement',
        'update_target_statement', 'update_design_challenge', 'update_objectives',
        'update_deliverables', 'update_metrics', 'update_stakeholders',
        // Tier C — Product Strategy
        'update_design_vision', 'update_strategic_themes', 'update_opportunity_areas',
        'update_audience_insights', 'update_design_hypotheses', 'update_brief_deconstruction',
        'update_information_assessment', 'update_strategic_guidance', 'update_success_metrics',
        'update_unmet_needs', 'update_competitive_ux_positioning', 'update_experience_design_principles',
        // Tier C — Competitor Analysis
        'update_market_intelligence', 'update_opportunity_analysis', 'update_methodology',
        // Tier B — Program/tool sequence
        'tool_duration_editing', 'add_tools_to_stages', 'remove_tools_from_stages', 'modify_tools_content',
      ]);
      const PRODUCT_STRATEGY_REACT_FIELDS = new Set([
        'designVision', 'strategicThemes', 'opportunityAreas', 'audienceInsights',
        'designHypotheses', 'briefDeconstruction', 'informationAssessment',
        'strategicGuidance', 'successMetrics', 'unmetNeeds',
        'competitiveUxPositioning', 'experienceDesignPrinciples',
      ]);

      // Generation capabilities — long-running SSE workflows. Never go through executeViaBackend.
      // They bypass the Apply button system entirely and run their own SSE stream.
      const GENERATION_CAPABILITIES = new Set([
        'generate_product_strategy',
        'generate_competitor_analysis',
        'generate_action_plan',
        'create_plan',
      ]);

      // ALWAYS_AUTO_EXECUTE is intentionally empty.
      // Auto-execution is now a runtime decision made by the agent based on conversational context.
      // The agent sets auto_execute: true only when the user gave an explicit literal value to apply.
      // Hardcoding capabilities here bypassed that judgment — e.g. "suggest durations" would
      // auto-execute because tool_duration_editing was in this set, ignoring intent.
      const ALWAYS_AUTO_EXECUTE = new Set([]);

      // Capabilities that MUST always show the Apply button — never auto-execute.
      // Strategic/generated content always needs human review regardless of what the AI flags.
      const NEVER_AUTO_EXECUTE = new Set([
        'update_design_vision', 'update_strategic_themes', 'update_opportunity_areas',
        'update_audience_insights', 'update_design_hypotheses', 'update_brief_deconstruction',
        'update_information_assessment', 'update_strategic_guidance', 'update_success_metrics',
        'update_unmet_needs', 'update_competitive_ux_positioning', 'update_experience_design_principles',
        'update_market_intelligence', 'update_opportunity_analysis', 'update_methodology',
        // Brief fields that require full rewrite/generation also go here when proposed (not dictated):
        // auto_execute: true is only safe for these if the user literally dictated the value
        // The AI instruction covers this, but as a final guard we keep them in NEVER_AUTO_EXECUTE
        // for Brief fields too — only tool_duration_editing is truly safe to auto-execute
        'update_summary', 'update_problem_statement', 'update_goal_statement',
        'update_target_statement', 'update_design_challenge', 'update_objectives',
        'update_deliverables', 'update_metrics', 'update_stakeholders',
      ]);

      // ---------------------------------------------------------------------------
      // executeViaBackend — routes Tier A actions through POST /api/agent/execute
      // Streams SSE events: execution_start, task_start, task_done, chunk, execution_done
      // ---------------------------------------------------------------------------
      // Fallback label when backend hasn't emitted activeLabel/doneLabel yet
      const capabilityLabel = (id, done = false) => {
        const base = (id || '').replace(/^update_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        return done ? `${base} Updated` : base;
      };

      const executeViaBackend = async (action, originalMessage, opts = {}) => {
        const { inPlace = false, tPhaseTasks = [], targetIdx: _targetIdx, skipTPhase = false, skipPreText = false } = opts;
        const token = localStorage.getItem("token");
        const taskId = `task-${Date.now()}`;

        // Find the message currently being executed — identified by _executing flag, not by phases.
        // Every advisory message has phases:[T done], so searching "any phases" hits the wrong message.
        const findPhasesMessageIdx = (prev) => {
          for (let i = prev.length - 1; i >= 0; i--) {
            if (prev[i].sender === 'bot' && prev[i]._executing) return i;
          }
          return -1;
        };

        // Helper to clear the stuck loading placeholder on failure
        const clearLoadingPlaceholder = (errorText) => {
          if (inPlace) {
            setMessages(prev => {
              const idx = findPhasesMessageIdx(prev);
              if (idx === -1) return prev;
              return prev.map((m, i) => i === idx ? {
                ...m,
                _executing: false,
                text: m._preExecutionText || m.text || '',
                rawText: m._preExecutionRawText || m.rawText || '',
                phases: (m.phases || []).map((p) => p.id === 'W' ? { ...p, state: 'error', label: errorText || '❌ Execution failed' } : p),
              } : m);
            });
          } else {
            setMessages(prev => {
              const idx = prev.findLastIndex(m => m.isLoading && m.isPlaceholder && m.sender === 'bot');
              if (idx === -1) return prev;
              return prev.map((m, i) => i === idx
                ? { ...m, isLoading: false, isPlaceholder: false, isStreaming: false,
                    text: errorText || '❌ Execution failed' }
                : m
              );
            });
          }
        };

        // Guard: block chatStateManager subscriber from overwriting messages during execution.
        isBackendExecutingRef.current = true;

        const additionalCount = (action.additional_actions || []).filter(a => TIER_A_CAPABILITIES.has(a.capability)).length;
        const workingLabel = additionalCount > 0
          ? `Applying ${additionalCount + 1} changes`
          : undefined;

        // Capture the streaming analysis text before inPlace clears it — passed to backend as context
        // Prefer messages with rawText (AI analysis) over injected messages like "Welcome back!" that lack it
        let preExecutionText = '';
        let preExecutionRawText = '';
        if (inPlace) {
          const snapIdx = _targetIdx !== undefined
            ? _targetIdx
            : (messages.map((m, i) => (m.sender === 'bot' && !m.isLoading && m.rawText ? i : -1)).filter(i => i >= 0).pop()
               ?? messages.map((m, i) => (m.sender === 'bot' && !m.isLoading ? i : -1)).filter(i => i >= 0).pop());
          if (snapIdx !== undefined && snapIdx !== -1 && messages[snapIdx]) {
            preExecutionText = messages[snapIdx].text || '';
            preExecutionRawText = messages[snapIdx].rawText || '';
          }
        }

        if (inPlace) {
          // Transition the last bot message in-place: attach phases, clear pre-execution text
          // Prefer messages with rawText (AI analysis) over injected messages (e.g. welcome injection) that lack it
          setMessages(prev => {
            const idx = _targetIdx !== undefined
              ? _targetIdx
              : (prev.map((m, i) => (m.sender === 'bot' && !m.isLoading && m.rawText ? i : -1)).filter(i => i >= 0).pop()
                 ?? prev.map((m, i) => (m.sender === 'bot' && m.isStreaming ? i : -1)).filter(i => i >= 0).pop()
                 ?? prev.map((m, i) => (m.sender === 'bot' && !m.isLoading ? i : -1)).filter(i => i >= 0).pop());
            console.log(`🔵 EXEC-START: setting _executing=true on prev[${idx}] (prev.length=${prev.length}, _targetIdx=${_targetIdx})`);
            if (idx === undefined || idx === -1) return prev;
            return prev.map((m, i) => i === idx ? {
              ...m,
              _executing: true,
              _preExecutionText: preExecutionText,
              _preExecutionRawText: preExecutionRawText,
              phases: [
                // Preserve existing phases (T done from advisory) — just append W active.
                // If no existing phases, fall back to building T from tPhaseTasks.
                ...(m.phases?.filter(p => p.id !== 'W') || (skipTPhase ? [] : [{ id: 'T', state: 'done', tasks: tPhaseTasks }])),
                { id: 'W', state: 'active', label: workingLabel, tasks: [] }, // workingLabel only set for multi-action batches
              ],
            } : m);
          });
        } else {
          // Add a new message with W active pill — execution result appears here, advisory stays unchanged
          setMessages(prev => [...prev, {
            text: "",
            sender: "bot",
            isLoading: true,
            isPlaceholder: true,
            timestamp: new Date().toISOString(),
            phases: [{ id: 'W', state: 'active', label: workingLabel, tasks: [] }],
            agent_info: { agentId: 'pabs-executor', agentType: 'PABS_EXECUTOR', name: 'PABS', isLead: true },
          }]);
        }

        // Dispatch global event so individual view components can blur affected sections
        window.dispatchEvent(new CustomEvent('pabs:capabilityExecuting', {
          detail: { capability: action.capabilityId, planId: currentPlanId },
        }));

        let response;
        try {
          response = await fetch("/api/agent/execute", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              planId: currentPlanId,
              tasks: [
                {
                  id: taskId,
                  capability: action.capabilityId,
                  section: action.section || action.capabilityId,
                  data: action.data,
                },
                // Batch any additional Tier A actions detected alongside the primary action
                ...((action.additional_actions || [])
                  .filter(a => TIER_A_CAPABILITIES.has(a.capability))
                  .map((a, i) => ({
                    id: `${taskId}-${i + 1}`,
                    capability: a.capability,
                    section: a.section || a.capability,
                    data: a.data,
                  }))
                ),
              ],
              agentContext: {
                originalMessage: originalMessage || "",
                proposalDescription: action.description || "",
                // skipPreText = auto_execute path: LLM output is a JSON signal, not analysis.
                // Send empty so the backend confirms strictly from changeDescriptions, not stale T-phase text.
                analysisContext: skipPreText ? '' : (preExecutionText || ""),
              },
            }),
          });
        } catch (fetchErr) {
          isBackendExecutingRef.current = false;
          clearLoadingPlaceholder(`❌ Could not reach execution endpoint: ${fetchErr.message}`);
          throw fetchErr;
        }

        if (!response.ok) {
          isBackendExecutingRef.current = false;
          const errText = `❌ Execution failed (${response.status})`;
          clearLoadingPlaceholder(errText);
          throw new Error(`Execution endpoint returned ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let streamingText = "";
        let executionSucceeded = false;
        // Track W-phase tasks in a plain closure variable alongside setMessages calls.
        // React may batch setMessages callbacks and not flush them synchronously before
        // chatStateManager.addMessage runs, so we cannot rely on the setMessages side-effect.
        let wTasksLocal = [];

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split("\n\n");
          buffer = events.pop() || "";

          for (const eventBlock of events) {
            if (!eventBlock.trim()) continue;
            let eventType = null;
            let eventData = null;
            for (const line of eventBlock.split("\n")) {
              if (line.startsWith("event: ")) eventType = line.slice(7).trim();
              else if (line.startsWith("data: ")) {
                try { eventData = JSON.parse(line.slice(6)); } catch {}
              }
            }
            if (!eventType || !eventData) continue;

            switch (eventType) {
              case "task_start":
                if (eventData.taskId) {
                  if (inPlace) {
                    setMessages(prev => {
                      const idx = findPhasesMessageIdx(prev);
                      if (idx === -1) return prev;
                      return prev.map((m, i) => i === idx ? {
                        ...m,
                        phases: m.phases.map((p) => p.id === 'W' ? {
                          ...p,
                          tasks: [...p.tasks, {
                            id: eventData.taskId,
                            label: eventData.activeLabel || capabilityLabel(eventData.capability || ''),
                            doneLabel: eventData.doneLabel,
                            state: 'active',
                          }],
                        } : p),
                      } : m);
                    });
                  } else {
                    const newTask = {
                      id: eventData.taskId,
                      label: eventData.activeLabel || capabilityLabel(eventData.capability || ''),
                      doneLabel: eventData.doneLabel,
                      state: 'active',
                    };
                    wTasksLocal = [...wTasksLocal, newTask];
                    setMessages(prev => prev.map((msg, i) =>
                      i === prev.length - 1 && msg.phases
                        ? {
                            ...msg,
                            phases: msg.phases.map(p => p.id === 'W' ? {
                              ...p,
                              tasks: [...p.tasks, newTask],
                            } : p),
                          }
                        : msg
                    ));
                  }
                }
                break;

              case "task_done":
                executionSucceeded = true;
                // Sync local React state from backend response.
                // For bulk_actions, each sub-task emits its own task_done with the sub-task's
                // capability so we update the correct field — not the outer 'bulk_actions' id.
                if (eventData.reactField && handleComponentAction) {
                  try {
                    if (eventData.reactField === 'finalProgramSequence' && eventData.rawValue) {
                      await handleComponentAction({
                        capability: 'update_programs',
                        data: { programs: eventData.rawValue },
                      });
                    } else {
                      const syncCapability = eventData.capability || action.capabilityId;
                      const syncData = eventData.rawValue !== undefined
                        ? { value: eventData.rawValue, content: eventData.rawValue, objectives: eventData.rawValue }
                        : { value: eventData.value, content: eventData.value };
                      await handleComponentAction({
                        capability: syncCapability,
                        data: syncData,
                      });
                    }
                  } catch (syncErr) {
                    console.warn("⚠️ Local state sync after backend execution failed:", syncErr.message);
                  }
                }
                if (inPlace) {
                  setMessages(prev => {
                    const idx = findPhasesMessageIdx(prev);
                    if (idx === -1) return prev;
                    return prev.map((m, i) => i === idx ? {
                      ...m,
                      phases: m.phases.map((p) => p.id === 'W' ? {
                        ...p,
                        tasks: p.tasks.map(t => t.id === eventData.taskId ? {
                          ...t, state: 'done',
                          label: eventData.doneLabel || t.doneLabel || capabilityLabel(eventData.capability, true),
                          description: eventData.value || undefined,
                        } : t),
                      } : p),
                    } : m);
                  });
                } else {
                  wTasksLocal = wTasksLocal.map(t => t.id === eventData.taskId
                    ? { ...t, state: 'done', label: eventData.doneLabel || t.doneLabel || capabilityLabel(eventData.capability, true), description: eventData.value || undefined }
                    : t
                  );
                  setMessages(prev => prev.map((msg, i) =>
                    i === prev.length - 1 && msg.phases
                      ? {
                          ...msg,
                          phases: msg.phases.map(p => p.id === 'W' ? {
                            ...p,
                            tasks: p.tasks.map(t => t.id === eventData.taskId ? {
                              ...t, state: 'done',
                              label: eventData.doneLabel || t.doneLabel || capabilityLabel(eventData.capability, true),
                              description: eventData.value || undefined,
                            } : t),
                          } : p),
                        }
                      : msg
                  ));
                }
                break;

              case "task_error":
                if (inPlace) {
                  if (eventData.error) {
                    streamingText = streamingText
                      ? `${streamingText}\n\n⚠️ ${eventData.error}`
                      : `⚠️ ${eventData.error}`;
                    setMessages(prev => {
                      const idx = findPhasesMessageIdx(prev);
                      if (idx === -1) return prev;
                      return prev.map((m, i) => i === idx ? { ...m, text: streamingText } : m);
                    });
                  }
                } else {
                  setActiveAgents(prev => prev.map(a =>
                    a.agentId === "pabs-executor" ? { ...a, state: "done" } : a
                  ));
                  if (eventData.error) {
                    streamingText = streamingText
                      ? `${streamingText}\n\n⚠️ ${eventData.error}`
                      : `⚠️ ${eventData.error}`;
                    setMessages(prev => prev.map((msg, i) =>
                      i === prev.length - 1 && (msg.isLoading || msg.isStreaming)
                        ? { ...msg, text: streamingText, isLoading: false, isStreaming: true, isPlaceholder: false }
                        : msg
                    ));
                  }
                }
                break;

              case "agent_message_start":
                // All tasks are complete — backend starts LLM synthesis only after every task finishes.
                // Transition W phase to done NOW so all task rows show done state before text streams in.
                if (inPlace) {
                  setMessages(prev => {
                    const idx = findPhasesMessageIdx(prev);
                    if (idx === -1) return prev;
                    return prev.map((m, i) => i === idx ? {
                      ...m,
                      phases: m.phases.map((p) => p.id === 'W' ? {
                        ...p,
                        state: 'done',
                        tasks: p.tasks.map(t => t.state === 'active' ? { ...t, state: 'done' } : t),
                      } : p),
                    } : m);
                  });
                } else {
                  wTasksLocal = wTasksLocal.map(t => t.state === 'active' ? { ...t, state: 'done' } : t);
                  setMessages(prev => prev.map((msg, i) =>
                    i === prev.length - 1 && msg.phases
                      ? { ...msg, phases: msg.phases.map(p => p.id === 'W' ? {
                          ...p, state: 'done',
                          tasks: p.tasks.map(t => t.state === 'active' ? { ...t, state: 'done' } : t),
                        } : p) }
                      : msg
                  ));
                }
                break;

              case "chunk":
                if (eventData.content) {
                  streamingText += eventData.content;
                  if (inPlace) {
                    setMessages(prev => {
                      const idx = findPhasesMessageIdx(prev);
                      if (idx === -1) return prev;
                      // Stream conclusion into closingText (below phases), keep advisory text intact
                      return prev.map((m, i) => i === idx ? { ...m, closingText: streamingText } : m);
                    });
                  } else {
                    setMessages(prev => prev.map((msg, i) =>
                      i === prev.length - 1 && (msg.isLoading || msg.isStreaming)
                        ? { ...msg, text: streamingText, isLoading: false, isStreaming: true, isPlaceholder: false }
                        : msg
                    ));
                  }
                }
                break;

              case "execution_done": {
                console.log(`🔵 EXEC-DONE event received, streamingText.length=${streamingText.length}`);
                const confirmationText = streamingText || eventData.agentMessage || "Done — change applied.";
                if (inPlace) {
                  setMessages(prev => {
                    const idx = findPhasesMessageIdx(prev);
                    console.log(`🔵 EXEC-DONE setMessages: findPhasesMessageIdx=${idx}, prev.length=${prev.length}`);
                    if (idx === -1) return prev;
                    const next = prev.map((m, i) => {
                      if (i !== idx) return m;
                      // Restore advisory text above phases; put conclusion in closingText below phases.
                      const advisoryText = m._preExecutionText || preExecutionText || m.text || '';
                      const advisoryRawText = m._preExecutionRawText || preExecutionRawText || m.rawText || advisoryText;
                      return {
                        ...m,
                        _executing: false,
                        text: advisoryText,
                        rawText: advisoryRawText,
                        closingText: confirmationText || undefined,
                        isStreaming: false,
                        phases: m.phases.map((p) => p.id === 'W' ? { ...p, state: 'done' } : p),
                      };
                    });
                    // Persist finalized inPlace state so manual refresh doesn't lose it
                    setTimeout(() => chatStateManager.syncInPlace(next), 0);
                    return next;
                  });
                } else {
                  // Build confirmation phases synchronously from wTasksLocal — avoids the race
                  // condition where React hasn't flushed the setMessages callback yet when
                  // chatStateManager.addMessage runs (chatStateManager saves to DB immediately).
                  const confirmationPhases = [{ id: 'W', state: 'done', tasks: wTasksLocal }];
                  setMessages(prev => prev.map((msg, i) => {
                    if (i !== prev.length - 1) return msg;
                    if (!msg.isStreaming && !msg.isLoading) return msg;
                    return {
                      ...msg,
                      text: confirmationText,
                      isLoading: false,
                      isStreaming: false,
                      isPlaceholder: false,
                      phases: confirmationPhases,
                    };
                  }));
                  await chatStateManager.addMessage({
                    text: confirmationText,
                    sender: 'bot',
                    phases: confirmationPhases,
                  });
                }
                break;
              }

              case "error":
                isBackendExecutingRef.current = false;
                clearLoadingPlaceholder(`❌ ${eventData.message || "Execution failed"}`);
                throw new Error(eventData.message || "Execution failed");
            }
          }
        }

        if (!inPlace) setActiveAgents([]);
        // Delay clearing the guard by one macrotask — chatStateManager.addMessage fires message_added
        // via setTimeout(fn, 0), so the subscriber must still see the guard as true when that fires,
        // otherwise it runs setMessages([...store.messages]) and causes a visible reload flash.
        setTimeout(() => { isBackendExecutingRef.current = false; }, 0);
        window.dispatchEvent(new CustomEvent('pabs:capabilityExecuted', {
          detail: { capability: action.capabilityId, planId: currentPlanId },
        }));
        return { success: executionSucceeded };
      };

      const handleActionExecute = async (action, messageIndex) => {
        if (!action || !handleComponentAction)
          return { success: false, message: "No action handler available" };

        const actionId = `${messageIndex}-${action.capabilityId}`;
        setIsExecutingAction(true);
        setExecutingActionId(actionId);

        // Route Tier A capabilities AND bulk_actions through the backend execution loop.
        // bulk_actions goes here too — the backend's executeTierB handles the sub-action loop
        // (tool_duration_editing, add_tools_to_stages, etc.) atomically and correctly.
        // Routing through the legacy AIActionManager path caused: no Working pill, Apply button
        // staying visible, and partial-failure errors for duration edits.
        if ((TIER_A_CAPABILITIES.has(action.capabilityId) || action.capabilityId === 'bulk_actions') && currentPlanId) {
          // Immediately hide the Apply button so only the Working pill takes over
          setExecutedBackendActions(prev => new Set(prev).add(actionId));
          try {
            console.log("🚀 Routing Tier A capability to backend executor:", action.capabilityId);
            const originalMessage = messages
              .filter(m => m.sender === "user")
              .slice(-1)[0]?.text || "";
            const result = await executeViaBackend(action, originalMessage, { inPlace: false });
            console.log(`✅ Backend execution complete for ${action.capabilityId}`);
            return { success: result.success, message: "Change applied" };
          } catch (err) {
            console.error("❌ Backend execution failed (inPlace):", err.message);
            return { success: false, message: err.message, error: true };
          } finally {
            setIsExecutingAction(false);
            setExecutingActionId(null);
            setActiveAgents([]);
          }
        }

        // Show Working pill for legacy AIActionManager path too
        setActiveAgents([{
          agentId: "pabs-executor",
          agentType: "PABS_EXECUTOR",
          name: capabilityLabel(action.capabilityId),
          state: "working",
          isLead: false,
          exchangeLog: [],
        }]);
        window.dispatchEvent(new CustomEvent('pabs:capabilityExecuting', {
          detail: { capability: action.capabilityId, planId: currentPlanId },
        }));

        try {
          console.log("🔧 Executing AI action via AIActionManager:", action);

          const context = {
            handleComponentAction,
            finalProgramSequence,
            selectedPrograms,
            currentPlanId,
          };

          const result = await aiActionManager.executeAction(action, context);
          activeRefinementRef.current = null;

          setActiveAgents(prev => prev.map(a =>
            a.agentId === "pabs-executor" ? { ...a, state: "done" } : a
          ));
          window.dispatchEvent(new CustomEvent('pabs:capabilityExecuted', {
            detail: { capability: action.capabilityId, planId: currentPlanId },
          }));

          if (result && result.success) {
            let successMessage = `Done — change applied.`;

            if (result.partialSuccess) {
              successMessage = `Done — changes partially applied. ${result.message || ""}`.trim();
            } else if (result.totalActions && result.successCount) {
              successMessage = `Done — ${result.successCount} of ${result.totalActions} changes applied.`;
            }

            chatStateManager.addMessage({
              text: successMessage,
              sender: "bot",
              timestamp: new Date().toISOString(),
              isHtml: false,
              phases: [{ id: 'W', state: 'done', tasks: [] }],
            });

            // If this action had pending plan steps (steps that couldn't run before user confirmation),
            // fire a continuation turn so the agent executes them now.
            const confirmedMsg = messages[messageIndex];
            const pendingSteps = confirmedMsg?.pendingPlanSteps;
            if (pendingSteps?.length > 0) {
              const stepDesc = pendingSteps.map(s => s.what).join(', ');
              console.log(`🔄 Firing plan continuation after Apply: ${stepDesc}`);
              // Small delay so the success message renders before the next turn starts
              setTimeout(() => {
                handleSend(`Update confirmed. Continue with: ${stepDesc}.`);
              }, 600);
            }

            console.log(`✅ Action ${actionId} completed successfully`);
            return {
              success: true,
              message: result.message || "Change applied successfully",
            };
          } else {
            console.log(
              `⚠️ Action ${actionId} completed but not successful:`,
              result,
            );

            // Show detailed error message in chat
            const errorMessage = `❌ **Action failed**: ${result.message || "Unknown error occurred"}`;
            chatStateManager.addMessage({
              text: errorMessage,
              sender: "bot",
              timestamp: new Date().toISOString(),
              isHtml: false,
            });

            return {
              success: false,
              message: result.message || "Action completed with issues",
            };
          }
        } catch (error) {
          console.error("Error executing action:", error);
          setActiveAgents([]);
          window.dispatchEvent(new CustomEvent('pabs:capabilityExecuted', {
            detail: { capability: action.capabilityId, planId: currentPlanId },
          }));
          chatStateManager.addMessage({
            text: `❌ Failed to apply change: ${error.message}. Please try again or make the change manually.`,
            sender: "bot",
            isHtml: false,
          });
          return { success: false, message: error.message, error: true };
        } finally {
          setIsExecutingAction(false);
          setExecutingActionId(null);
          setActiveAgents([]);
        }
      };

      // ---------------------------------------------------------------------------
      // executeGenerationWorkflow — runs a long SSE generation workflow.
      // Generation W progress is a chronological chat event, not decoration on
      // the advisory message. Keeping it as a separate message prevents the
      // renderer from hoisting W above text and preserves the real sequence:
      // user request → agent/read cards → W workflow → closing result.
      // ---------------------------------------------------------------------------
      const executeGenerationWorkflow = async (capability, actionData = {}) => {
        const endpointMap = {
          generate_product_strategy: '/api/generate/product-strategy',
          generate_competitor_analysis: '/api/generate/competitor-analysis',
          generate_action_plan: '/api/generate/action-plan',
          create_plan: '/api/generate/create-plan',
        };
        const endpoint = endpointMap[capability];
        // create_plan runs before the plan exists — planId is intentionally null
        if (!endpoint || (!planId && capability !== 'create_plan')) return;

        let acquiredCreatePlanBrowserLock = false;

        // Idempotency guard: only one create_plan stream at a time.
        // Both the auto-start path (pabs:autoStartGeneration) and the agent-tool
        // path (line ~3478) can invoke this function — guard prevents double-creation.
        if (capability === 'create_plan') {
          acquiredCreatePlanBrowserLock = acquireCreatePlanBrowserLock();
          if (!acquiredCreatePlanBrowserLock) {
            console.warn('[gen] create_plan already in flight globally — ignoring duplicate call');
            return;
          }
          if (createPlanInFlightRef.current) {
            console.warn('[gen] create_plan already in flight — ignoring duplicate call');
            releaseCreatePlanBrowserLock();
            return;
          }
          createPlanInFlightRef.current = true;
        }

        const token = localStorage.getItem('token');
        // Use the snapshot captured inside setMessages at stream-finalize time.
        // Reading from closingConvHistoryRef avoids the stale-closure bug where
        // messages captured in the useCallback closure contains CA context instead of PS context.
        const closingConvHistory = closingConvHistoryRef.current;

        const briefData = {
          goalStatement,
          problemStatement,
          targetStatement,
          objectives: objectivesStatement,
          objectivesStatement,
          deliverables: deliverablesStatement,
          deliverablesStatement,
          metrics: metricsStatement,
          metricsStatement,
          stakeholders: stakeholdersCustomersInvolvement,
        };

        // Block chatStateManager subscriber FIRST so the addMessage call below doesn't
        // fire the subscriber and add a duplicate to React state.
        isBackendExecutingRef.current = true;
        window.dispatchEvent(new CustomEvent('pabs:generationStart', {
          detail: { capability, planId, actionData },
        }));

        // For create_plan: route the user's design challenge through chatStateManager.addMessage
        // to set hasUserInteraction=true on the active conversation so syncInPlace actually
        // persists messages at the end of the stream. Subscriber is already blocked above,
        // so this does NOT add the message to React state — we add it once via setMessages below.
        if (capability === 'create_plan' && currentDesignChallenge) {
          chatStateManager.addMessage({
            text: currentDesignChallenge,
            sender: 'user',
            timestamp: new Date().toISOString(),
          });
        }

        const makeGenerationMessage = (id, text = "") => ({
          id,
          sender: 'bot',
          text,
          rawText: text,
          isHtml: false,
          _executing: true,
          isGenerationWorkflow: true,
          phases: [{ id: 'W', state: 'active', tasks: [] }],
          timestamp: new Date().toISOString(),
          agent_info: { agentId: 'pabs-generator', agentType: 'PABS_GENERATOR', name: 'PABS', isLead: true },
        });

        // For create_plan the plan doesn't exist yet and no agent stream ran before this,
        // so add a short advisory first, then the chronological W workflow message.
        // For plan-page generation tools, attach W to the assistant turn that produced
        // the tool call so it reads as one sequence: intro/read cards -> W -> closing.
        // The old breadcrumb bug was caused by phase hoisting; with that removed, the
        // same-message model is the better root fix.
        if (capability === 'create_plan') {
          const advisoryText = "I'm building your plan now. I'm writing the project brief and product strategy in one go, which takes a couple of minutes.";
          const now = Date.now();
          setMessages(prev => {
            // Only add user-dc if there is no prior user message in the conversation.
            // When create_plan is triggered via the planning agent (chat flow), the user's
            // original message is already in prev — adding user-dc again would create a
            // duplicate with a LATER timestamp that sorts to the wrong position on reload.
            // When triggered via button click (no planning agent exchange), prev has no user
            // message, so user-dc provides the required first user message for context.
            const hasExistingUserMsg = prev.some(m => m.sender === 'user');
            return [
            ...prev,
            ...(!hasExistingUserMsg && currentDesignChallenge ? [{
              id: `user-dc-${now}`,
              sender: 'user',
              text: currentDesignChallenge,
              rawText: currentDesignChallenge,
              timestamp: new Date().toISOString(),
            }] : []),
            {
              id: `gen-create-${now}`,
              sender: 'bot',
              text: advisoryText,
              rawText: advisoryText,
              isHtml: false,
              timestamp: new Date().toISOString(),
            },
            makeGenerationMessage(`gen-workflow-${now + 1}`),
            ];
          });
	        } else {
	          setMessages(prev => {
	            if (prev.some(m => m.isGenerationWorkflow && m._executing)) return prev;
            const idx = prev.findLastIndex(m => m.sender === 'bot');
            if (idx === -1) {
              return [...prev, makeGenerationMessage(`gen-workflow-${Date.now()}`)];
            }
            return prev.map((m, i) => i !== idx ? m : {
              ...m,
              _executing: true,
              isGenerationWorkflow: true,
              phases: [
                ...(m.phases || [{ id: 'T', state: 'done', tasks: [] }]).filter(p => p.id !== 'W'),
                { id: 'W', state: 'active', tasks: [] },
              ],
            });
	          });
	        }

        // Find the executing message — last bot message with _executing: true
        const findExecIdx = (prev) => prev.findLastIndex(m => m.sender === 'bot' && m._executing === true);

        console.log('[gen] starting', capability, 'planId:', planId);

        let completionData = null; // set by execution_done; used after stream ends to call closing agent

        try {
          const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              designChallenge: currentDesignChallenge,
              creationRequestId: capability === 'create_plan'
                ? (window.history.state?.usr?._cacheKey || (createPlanRequestIdRef.current ||= window.crypto.randomUUID()))
                : undefined,
              briefData,
              planId,
              planName: shortSummary,
              enabledFeatures: [],
              generationContext: actionData?.generation_context || actionData?.context || actionData?.focus || null,
            }),
          });

          console.log('[gen] response status:', response.status);
          if (!response.ok) {
            throw new Error(`Generation endpoint returned ${response.status}`);
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) { console.log('[gen] stream done'); break; }

            buffer += decoder.decode(value, { stream: true });
            const eventBlocks = buffer.split('\n\n');
            buffer = eventBlocks.pop() || '';

            for (const block of eventBlocks) {
              if (!block.trim()) continue;
              let eventType = null, eventData = null;
              for (const line of block.split('\n')) {
                if (line.startsWith('event: ')) eventType = line.slice(7).trim();
                else if (line.startsWith('data: ')) {
                  try { eventData = JSON.parse(line.slice(6)); } catch {}
                }
              }
              if (!eventType || !eventData) continue;

              console.log('[gen] event:', eventType, JSON.stringify(eventData).slice(0, 120));

              switch (eventType) {
                case 'workflow_start':
                  setMessages(prev => {
                    const idx = findExecIdx(prev);
                    console.log('[gen] workflow_start — exec idx:', idx, 'steps:', eventData.steps?.length);
                    if (idx === -1) return prev;
                    return prev.map((m, i) => i !== idx ? m : {
                      ...m,
                      phases: m.phases.map(p => p.id === 'W' ? {
                        ...p,
                        tasks: (eventData.steps || []).map(s => ({ id: s.id, label: s.label, state: 'pending' })),
                      } : p),
                    });
                  });
                  await new Promise(r => requestAnimationFrame(r));
                  break;

                case 'task_label_update':
                  setMessages(prev => {
                    const idx = findExecIdx(prev);
                    if (idx === -1) return prev;
                    const labelMap = {};
                    (eventData.steps || []).forEach(s => { labelMap[s.id] = s.label; });
                    return prev.map((m, i) => i !== idx ? m : {
                      ...m,
                      phases: m.phases.map(p => p.id === 'W' ? {
                        ...p,
                        tasks: p.tasks.map(t => labelMap[t.id] ? { ...t, label: labelMap[t.id] } : t),
                      } : p),
                    });
                  });
                  break;

                case 'task_start':
                  setMessages(prev => {
                    const idx = findExecIdx(prev);
                    if (idx === -1) return prev;
                    return prev.map((m, i) => i !== idx ? m : {
                      ...m,
                      phases: m.phases.map(p => p.id === 'W' ? {
                        ...p,
                        tasks: p.tasks.map(t => t.id === eventData.stepId ? { ...t, state: 'active', subtitle: eventData.subtitle || null } : t),
                      } : p),
                    });
                  });
                  await new Promise(r => requestAnimationFrame(r));
                  break;

                case 'task_progress':
                  setMessages(prev => {
                    const idx = findExecIdx(prev);
                    if (idx === -1) return prev;
                    return prev.map((m, i) => i !== idx ? m : {
                      ...m,
                      phases: m.phases.map(p => p.id === 'W' ? {
                        ...p,
                        tasks: p.tasks.map(t => t.id === eventData.stepId ? { ...t, subtitle: eventData.subtitle } : t),
                      } : p),
                    });
                  });
                  break;

                case 'task_chunk':
                  setMessages(prev => {
                    const idx = findExecIdx(prev);
                    if (idx === -1) return prev;
                    return prev.map((m, i) => i !== idx ? m : {
                      ...m,
                      phases: m.phases.map(p => p.id === 'W' ? {
                        ...p,
                        tasks: p.tasks.map(t => t.id === eventData.stepId
                          ? { ...t, streamingContent: (t.streamingContent || '') + (eventData.content || '') }
                          : t),
                      } : p),
                    });
                  });
                  break;

                case 'task_done':
                  setMessages(prev => {
                    const idx = findExecIdx(prev);
                    if (idx === -1) return prev;
                    return prev.map((m, i) => i !== idx ? m : {
                      ...m,
                      phases: m.phases.map(p => p.id === 'W' ? {
                        ...p,
                        tasks: p.tasks.map(t => t.id === eventData.stepId ? { ...t, state: 'done', subtitle: null, streamingContent: '' } : t),
                      } : p),
                    });
                  });
                  await new Promise(r => requestAnimationFrame(r));
                  break;

                case 'execution_done':
                  setMessages(prev => {
                    const idx = findExecIdx(prev);
                    console.log('[gen][debug] execution_done — findExecIdx:', idx, 'messages:', prev.length, 'has _executing:', prev.map(m => m._executing));
                    if (idx === -1) return prev;
                    console.log('[gen][debug] setting _awaitingClosing:true on idx', idx);
                    return prev.map((m, i) => i !== idx ? m : {
                      ...m,
                      _executing: false,
                      _awaitingClosing: true,  // closing stream will append text to this message
                      phases: m.phases.map(p => p.id === 'W' ? { ...p, state: 'done' } : p),
                    });
                  });
                  // Keep isBackendExecutingRef engaged — closing agent stream takes over next.
                  completionData = { reportId: eventData.reportId, version: eventData.version, savedPlanId: eventData.planId, receiptId:eventData.receiptId, briefData: eventData.briefData, psReport: eventData.psReport, designChallenge: eventData.designChallenge, finalProgramSequence: eventData.finalProgramSequence, totalDuration: eventData.totalDuration };
                  pendingGenExpandRef.current = true;
                  // create_plan: defer pabs:generationComplete until after closing stream finishes,
                  // so navigation (savePlan) doesn't wipe in-memory messages before closing text renders.
                  if (capability !== 'create_plan') {
                    window.dispatchEvent(new CustomEvent('pabs:generationComplete', {
                      detail: { capability, reportId: eventData.reportId, planId, briefData: eventData.briefData, psReport: eventData.psReport, designChallenge: eventData.designChallenge, finalProgramSequence: eventData.finalProgramSequence, totalDuration: eventData.totalDuration },
                    }));
                  }
                  break;

                case 'error':
                  setMessages(prev => {
                    const idx = findExecIdx(prev);
                    if (idx === -1) return prev;
                    return prev.map((m, i) => i !== idx ? m : {
                      ...m,
                      _executing: false,
                      phases: m.phases.map(p => p.id === 'W' ? { ...p, state: 'error' } : p),
                    });
                  });
                  setTimeout(() => { isBackendExecutingRef.current = false; }, 0);
                  window.dispatchEvent(new CustomEvent('pabs:generationError', {
                    detail: { capability, planId, error: eventData.message || 'Generation failed' },
                  }));
                  break;

                default:
                  break;
              }
            }
          }

          // Generation stream ended — run closing agent response via the standard chat-stream pipe.
          // Closing text is appended to the same advisory message (not a new bubble) to eliminate
          // the inter-message gap and keep the full interaction in one visual unit.
          if (completionData) {
            const closingPlanId = completionData.savedPlanId || planId;
            try {
              // A new plan's closing reads the saved Decisions state in one small
              // call. Other generations report back through the chat agent.
              const closingRes = capability === 'create_plan' && closingPlanId
                ? await fetch(`/api/generate/create-plan/${closingPlanId}/closing`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                  })
                : await fetch('/api/chat-stream', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                  user_message: '__tool_result__',
                  tool_result_context: {
                    capability,
                    reportId: completionData.reportId,
                    version: completionData.version,
                    planId: completionData.savedPlanId || planId,
                  },
                  conversation_history: closingConvHistory,
                  planId: completionData.savedPlanId || planId,
                  page_context: 'plan',
                  interaction_mode: 'work',
                  currentView: currentView || 'strategy',
                  currentDesignChallenge,
                  shortSummary,
                }),
              });

              const closingReader = closingRes.body.getReader();
              const closingDecoder = new TextDecoder();
              let closingBuffer = '';
              let closingText = '';

              while (true) {
                const { done: cDone, value: cValue } = await closingReader.read();
                if (cDone) break;
                closingBuffer += closingDecoder.decode(cValue, { stream: true });
                const cBlocks = closingBuffer.split('\n\n');
                closingBuffer = cBlocks.pop() || '';
                for (const cBlock of cBlocks) {
                  if (!cBlock.trim()) continue;
                  let cType = null, cData = null;
                  for (const cLine of cBlock.split('\n')) {
                    if (cLine.startsWith('event: ')) cType = cLine.slice(7).trim();
                    else if (cLine.startsWith('data: ')) { try { cData = JSON.parse(cLine.slice(6)); } catch {} }
                  }
                  if (cType === 'chunk' && cData?.content) {
                    closingText += cData.content;
                    setMessages(prev => {
                      const li = prev.findLastIndex(m => m._awaitingClosing);
                      console.log('[gen][debug] closing chunk — findLastIndex(_awaitingClosing):', li, 'messages:', prev.length, 'flags:', prev.map(m => ({ _exec: m._executing, _await: m._awaitingClosing })));
                      if (li < 0) return prev;
                      return prev.map((m, i) => i !== li ? m : {
                        ...m,
                        closingText: closingText,
                      });
                    });
                  }
                }
              }

              // Finalise: clear internal flags, keep combined text
              setMessages(prev => {
                const li = prev.findLastIndex(m => m._awaitingClosing);
                console.log('[gen][debug] FINALIZE — findLastIndex(_awaitingClosing):', li, 'closingText len:', closingText.length);
                if (li < 0) return prev;
                const { _awaitingClosing, _baseText, ...rest } = prev[li];
                return prev.map((m, i) => i !== li ? m : {
                  ...rest,
                  closingText: closingText || rest.closingText || undefined,
                });
              });
            } catch (closingErr) {
              console.error('[gen] closing stream error:', closingErr.message);
              // Clear _awaitingClosing flag on error, keep existing advisory text
              setMessages(prev => {
                const li = prev.findLastIndex(m => m._awaitingClosing);
                if (li < 0) return prev;
                const { _awaitingClosing, _baseText, ...rest } = prev[li];
                return prev.map((m, i) => i !== li ? m : { ...rest });
              });
            }

            // create_plan: dispatch pabs:generationComplete now that closing text has rendered.
            // This triggers EmbeddedChatPanel → handleComponentAction → savePlan → navigation.
            // Save the finished creation chat with the new plan first: the plan
            // page reloads the conversation, and the advisory, workflow and
            // closing messages only exist in this component's state until now.
            if (capability === 'create_plan' && completionData) {
              const finalMessages = await Promise.race([
                new Promise(resolve => setMessages(prev => { resolve(prev); return prev; })),
                new Promise(resolve => setTimeout(() => resolve(null), 1500)),
              ]);
              if (finalMessages) {
                await chatStateManager.persistCreationConversation(finalMessages, completionData.savedPlanId)
                  .catch(error => console.error('[gen] could not save the creation chat:', error.message));
              }
              window.dispatchEvent(new CustomEvent('pabs:generationComplete', {
                detail: { capability, reportId: completionData.reportId, planId:completionData.savedPlanId || planId, savedPlanId:completionData.savedPlanId, receiptId:completionData.receiptId, briefData: completionData.briefData, psReport: completionData.psReport, designChallenge: completionData.designChallenge, finalProgramSequence: completionData.finalProgramSequence, totalDuration: completionData.totalDuration },
              }));
            }
          }
          // Persist the final message state (T + W phases + closing text) to chatStateManager
          // so conversation history is restored correctly on next load.
          setMessages(prev => {
            setTimeout(() => chatStateManager.syncInPlace(prev), 0);
            return prev;
          });
          isBackendExecutingRef.current = false;
          if (capability === 'create_plan') {
            createPlanInFlightRef.current = false;
            if (acquiredCreatePlanBrowserLock) releaseCreatePlanBrowserLock();
          }

        } catch (err) {
          console.error('[gen] error:', err.message);
          window.dispatchEvent(new CustomEvent('pabs:generationError', {
            detail: { capability, planId, error: err.message || 'Generation failed' },
          }));
          setMessages(prev => {
            const idx = findExecIdx(prev);
            if (idx === -1) return prev;
            return prev.map((m, i) => i !== idx ? m : {
              ...m,
              _executing: false,
              phases: m.phases.map(p => p.id === 'W' ? { ...p, state: 'error' } : p),
            });
          });
          isBackendExecutingRef.current = false;
          if (capability === 'create_plan') {
            createPlanInFlightRef.current = false;
            if (acquiredCreatePlanBrowserLock) releaseCreatePlanBrowserLock();
          }
        }
      };

      // Keep ref current so pabs:autoStartGeneration listener always calls the latest version
      executeGenerationWorkflowRef.current = executeGenerationWorkflow;

      const handleActionCancel = (action, messageIndex) => {
        console.log("❌ Canceling AI action:", action.capabilityId);

        // Remove action from the message by filtering out the specific action by capabilityId
        setMessages((prevMessages) =>
          prevMessages.map((msg, idx) =>
            idx === messageIndex
              ? {
                  ...msg,
                  actions: msg.actions?.filter(
                    (a) => a.capabilityId !== action.capabilityId
                  ) || [],
                }
              : msg,
          ),
        );

        setMessages((prevMessages) => [
          ...prevMessages,
          {
            text: `Change cancelled.`,
            sender: "bot",
            isHtml: false,
            timestamp: new Date().toISOString(),
          },
        ]);
      };

      const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          handleSend();
        }
      };

      const addPendingFiles = useCallback((fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        const { accepted, errors, formatRejected } = validateChatFiles(files, pendingAttachments.length);
        if (formatRejected) setShowFormatHelp(true);
        if (accepted.length > 0) {
          const admitted = accepted.slice(0, Math.max(0, CHAT_ATTACHMENT_MAX_FILES - pendingAttachments.length));
          setPendingAttachments(prev => [...prev, ...admitted].slice(0, CHAT_ATTACHMENT_MAX_FILES));
          // Read each file now so it's encoded by send time, and keep the shimmer
          // visible for a brief minimum so the reading→ready transition feels smooth.
          admitted.forEach((att) => {
            const startedAt = Date.now();
            fileToBase64(att.file)
              .then(async (b64) => {
                const elapsed = Date.now() - startedAt;
                if (elapsed < CHAT_ATTACHMENT_MIN_READ_MS) {
                  await new Promise(r => setTimeout(r, CHAT_ATTACHMENT_MIN_READ_MS - elapsed));
                }
                setPendingAttachments(prev => prev.map(item =>
                  item.id === att.id ? { ...item, status: "ready", content_base64: b64 } : item
                ));
              })
              .catch(() => {
                setPendingAttachments(prev => prev.filter(item => item.id !== att.id));
                setAttachmentError(`Could not read ${att.name}. Please try again.`);
              });
          });
        }
        setAttachmentError(errors[0] || "");
      }, [pendingAttachments.length]);

      const removePendingAttachment = useCallback((id) => {
        setPendingAttachments(prev => prev.filter(item => item.id !== id));
        setAttachmentError("");
      }, []);

      const handleFileInputChange = useCallback((e) => {
        addPendingFiles(e.target.files);
        e.target.value = "";
      }, [addPendingFiles]);

      const handleComposerDragOver = useCallback((e) => {
        if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
        e.preventDefault();
        setIsDragActive(true);
      }, []);

      const handleComposerDragLeave = useCallback((e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setIsDragActive(false);
      }, []);

      const handleComposerDrop = useCallback((e) => {
        if (!Array.from(e.dataTransfer?.types || []).includes("Files")) return;
        e.preventDefault();
        setIsDragActive(false);
        addPendingFiles(e.dataTransfer.files);
      }, [addPendingFiles]);

      const handleSend = useCallback(
        async (messageToSend = null, refinementContext = null) => {
          const isEventObject =
            messageToSend &&
            typeof messageToSend === "object" &&
            (typeof messageToSend.preventDefault === "function" ||
              typeof messageToSend.nativeEvent === "object");

          if (isEventObject) {
            messageToSend.preventDefault?.();
            messageToSend = null;
          }

          const isOneShotContext = ['activity_update', 'document_generation'].includes(refinementContext?.interactionType);
          if (isOneShotContext) {
            activeRefinementRef.current = null;
          } else if (refinementContext) {
            activeRefinementRef.current = refinementContext;
          }
          const effectiveRefinementContext = refinementContext || activeRefinementRef.current;

          let message;
          if (messageToSend !== null && messageToSend !== undefined) {
            message =
              typeof messageToSend === "string"
                ? messageToSend
                : String(messageToSend);
          } else {
            message = input.trim();
          }
          const attachmentsForSend = messageToSend ? [] : [...pendingAttachments];

          // Validate message
          if ((!message || message.length === 0) && attachmentsForSend.length === 0) {
            console.log("📝 No message to send - input is empty");
            return;
          }
          if (!message && attachmentsForSend.length > 0) {
            message = `Please read the attached file${attachmentsForSend.length > 1 ? "s" : ""}.`;
          }

          let encodedAttachments = [];
          try {
            // Reuse the base64 captured at attach time; only read on the fly if a
            // file somehow wasn't finished reading yet.
            encodedAttachments = await Promise.all(attachmentsForSend.map(async (attachment) => ({
              name: attachment.name,
              type: attachment.type,
              size: attachment.size,
              content_base64: attachment.content_base64 || await fileToBase64(attachment.file),
            })));
          } catch (fileReadError) {
            setAttachmentError(fileReadError.message || "Could not read the selected file.");
            return;
          }

          console.log("📝 Sending message:", {
            message,
            messageLength: message.length,
            attachments: attachmentsForSend.map(file => file.name),
            fromInput: !messageToSend,
          });

          // Add user message through ChatStateManager
          chatStateManager.addMessage({
            text: message,
            sender: "user",
            isHtml: false,
            attachments: attachmentsForSend.map(file => ({
              name: file.name,
              type: file.type,
              size: file.size,
              ext: file.ext,
            })),
          });

          if (!messageToSend) setInput("");
          setPendingAttachments([]);
          setAttachmentError("");
          setShowFormatHelp(false);

          const activeConversation = await chatStateManager.getActiveConversation();

          // Check for plan creation confirmation
          if (awaitingPlanCreation) {
            const confirmationKeywords = ['yes', 'proceed', 'sure', 'go ahead', 'let\'s go', 'okay', 'ok', 'yep', 'yeah', 'please'];
            const isConfirmation = confirmationKeywords.some(keyword => message.toLowerCase().includes(keyword));
            
            if (isConfirmation) {
              console.log('✅ User confirmed plan creation, synthesizing brief...');
              
              try {
                // Get full conversation history for synthesis
                const conversation_history = (activeConversation?.messages || [])
                  .filter((msg) => !msg.isLoading && !msg.isPlaceholder)
                  .map((msg) => ({
                    sender: msg.sender,
                    text: msg.text
                  }));
                
                console.log('🔍 Synthesizing brief from conversation...', {
                  messageCount: conversation_history.length
                });
                
                // Call synthesis endpoint
                const token = localStorage.getItem("token");
                const synthesisResponse = await fetchWithAuth('/api/synthesize-conversation-brief', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                  },
                  body: JSON.stringify({ conversation_history })
                });
                
                const synthesisData = await synthesisResponse.json();
                const synthesized_brief = synthesisData?.synthesized_brief || '';
                
                console.log('✅ Brief synthesis complete:', {
                  briefLength: synthesized_brief?.length || 0,
                  briefPreview: (synthesized_brief || '').substring(0, 100) + '...'
                });
                
                // Create unique cache key for workflow launch
                const cacheKey = `workflow-${Date.now()}-${Math.random()}`;
                
                // Navigate to plan page with synthesized brief
                navigate('/plan', {
                  state: {
                    initiateStrategyCreation: true,
                    workflowType: awaitingPlanCreation.planType,
                    designChallenge: synthesized_brief, // Use synthesized brief instead of raw text
                    isNewPlan: true,
                    _cacheKey: cacheKey
                  }
                });
                
                // Reset confirmation state
                setAwaitingPlanCreation(null);
                return;
              } catch (error) {
                console.error('❌ Error synthesizing brief:', error);
                // Fallback to original conversation summary if synthesis fails
                const cacheKey = `workflow-${Date.now()}-${Math.random()}`;
                navigate('/plan', {
                  state: {
                    initiateStrategyCreation: true,
                    workflowType: awaitingPlanCreation.planType,
                    designChallenge: awaitingPlanCreation.conversationSummary,
                    isNewPlan: true,
                    _cacheKey: cacheKey
                  }
                });
                setAwaitingPlanCreation(null);
                return;
              }
            }
          }

          if (isAwaitingConfirmation) {
            handleUserConfirmation(message.toLowerCase());
            return;
          }

          setLoading(true);
          setLoadingPhase("routing");
          setSelectedAgent(null);
          setActiveAgents([]);
          leadAgentRef.current = null;

          // Add placeholder message with loading states through ChatStateManager
          chatStateManager.addMessage({
            text: "...",
            sender: "bot",
            isPlaceholder: true,
            isLoading: true,
            loadingPhase: "routing",
          });

          const conversation_history = (activeConversation?.messages || [])
            .filter((msg) => !msg.isLoading && !msg.isPlaceholder)
            .map((msg) => {
              let text = msg.text || '';
              // Append specialist context from sub-agents so the backend has it on the next turn
              if (msg.sender === 'bot' && msg.agentExchangeLog?.length > 0) {
                const specialistNotes = msg.agentExchangeLog
                  .filter(e => e.result?.result?.summary)
                  .map(e => `[${e.result.agent_name} finding: ${e.result.result.summary}]`)
                  .join(' ');
                if (specialistNotes) text = text + `\n\n${specialistNotes}`;
              }
              if (msg.sender === 'bot') {
                text += summarizeToolActivityForHistory(msg);
              }
              return {
                sender: msg.sender,
                // Preserve agent-specific roles for proper conversation context
                role: msg.agent_info?.role || (msg.sender === "bot" ? "assistant" : "user"),
                text,
                isCompressedContext: isCompressedContextMessage(msg),
                agent_info: msg.agent_info, // Preserve agent context
              };
            });

          let lastStreamingSegmentsForError = null;
          try {
            const token = localStorage.getItem("token");
            if (!token) {
              throw new Error("Authentication token not found");
            }


            // Use agent selection from dropdown instead of keyword routing
            let displayAgent = null;
            if (selectedAgents && selectedAgents.length > 0) {
              // Use the first selected agent for display purposes
              const firstAgent = selectedAgents[0];
              displayAgent = { 
                agentId: firstAgent.id, 
                agent_name: firstAgent.name || AGENT_PROFILES[firstAgent.id]?.name
              };
            } else {
              // Fallback to Design Lead if no agents selected
              displayAgent = { agentId: "DESIGN_LEAD", agent_name: AGENT_PROFILES.DESIGN_LEAD.name };
            }

            // Show the selected agent immediately (respecting user choice)
            setTimeout(() => {
              setSelectedAgent(displayAgent);
              setLoadingPhase("analyzing");

              // Update the loading message to show selected agent is now thinking
              setMessages((prevMessages) =>
                prevMessages.map((msg, index) =>
                  index === prevMessages.length - 1 && msg.isLoading
                    ? {
                        ...msg,
                        selectedAgent: displayAgent,
                        loadingPhase: "analyzing",
                      }
                    : msg,
                ),
              );
            }, 150); // Brief delay for smooth UI transition


            const contextType =
              message.toLowerCase().includes("why is") &&
              message.toLowerCase().includes("included")
                ? "tool_explanation"
                : "general";

            const context = {
              currentDesignChallenge,
              selectedPrograms,
              shortSummary,
              goalStatement,
              problemStatement,
              targetStatement,
              objectivesStatement,
              finalProgramSequence,
              deliverablesStatement,
              metricsStatement,
              stakeholdersCustomersInvolvement,
              totalDuration,
              // FIX #35: Pass enabledFeatures so backend can check if step_by_step_view is already enabled
              enabledFeatures: uiConfig?.enabledFeatures || [],
              // Pass currentView so backend knows which feature/tab user is on
              currentView: currentView || 'strategy',
            };

            // STREAMING: Use streaming endpoint for progressive response rendering
            let streamingText = '';
            // Segments track interleaved text + tool calls for ordered rendering.
            // Each text chunk appends to the last text segment; each tool start pushes
            // a tool segment + a new empty text segment (post-tool text goes there).
            let streamingSegments = [{ type: 'text', rawContent: '' }];
            lastStreamingSegmentsForError = streamingSegments;
            let responseData = null;
            
            // Pass the original plan from the last bot message so the agent can continue it
            // without replanning from scratch on each turn.
            const lastBotMsg = [...messages].reverse().find(m => m.sender === 'bot' && !m.isLoading && !m.isPlaceholder);
            const pendingStepsFromLastTurn = lastBotMsg?.pendingPlanSteps;
            const agentPlanFromLastTurn = lastBotMsg?.agentPlan;
            const agentRunIdFromLastTurn = lastBotMsg?.agentRunId;
            const hasPendingContinuation = pendingStepsFromLastTurn?.length > 0;

            const requestBody = {
              user_message: message,
              conversation_id: currentConversationId || null,
              user_context: planStatusRef.current ? {
                planType: planStatusRef.current.planType || 'free',
                creditsLeft: planStatusRef.current.credits?.total ?? null,
                creditsAllowance: planStatusRef.current.credits?.allowance ?? null,
              } : null,
              agent_run_id: agentRunIdFromLastTurn || null,
              conversation_history: conversation_history,
              context_type: contextType,
              selectedAgents: selectedAgents,
              // CRITICAL: Don't send planId or plan context when on Brief page
              planId: isOnBriefPage ? null : (planId || currentPlanId),
              currentWorkflowId: isOnBriefPage ? null : currentWorkflowId,
              // Explicit context signals (backwards-compat conversation_mode preserved)
              conversation_mode: isOnBriefPage ? true : false,
              page_context: isOnBriefPage ? 'brief' : 'plan',
              interaction_mode: interactionMode,
              // Only spread context when NOT on Brief page
              ...(isOnBriefPage ? {} : context),
              ...(encodedAttachments.length > 0 ? { attachments: encodedAttachments } : {}),
              ...(effectiveRefinementContext ? { refinementContext: effectiveRefinementContext } : {}),
              ...(hasPendingContinuation ? { pending_plan_steps: pendingStepsFromLastTurn } : {}),
              ...(hasPendingContinuation && agentPlanFromLastTurn?.length > 0 ? { agent_plan: agentPlanFromLastTurn } : {}),
            };

            // Poll backend for sub-agent status while waiting for SSE.
            // The Replit proxy buffers the entire SSE stream, so agent_start events
            // never arrive mid-response. This poll bridges the gap so the Consulting
            // pill appears immediately when sub-agents are dispatched.
            const _pollPlanId = isOnBriefPage ? null : (planId || currentPlanId);
            let _consultingPoll = null;
            const _stopPoll = () => { if (_consultingPoll) { clearInterval(_consultingPoll); _consultingPoll = null; } };
            if (_pollPlanId) {
              const _pollToken = localStorage.getItem('token');
              let _pollSeen = false;
              _consultingPoll = setInterval(async () => {
                try {
                  const pollRes = await fetch(`/api/chat-status/${_pollPlanId}`, {
                    headers: { Authorization: `Bearer ${_pollToken}` },
                  });
                  const status = await pollRes.json();
                  if (status.phase === 'consulting' && !_pollSeen && status.agents?.length > 0) {
                    _pollSeen = true;
                    setActiveAgents(prev => {
                      const existingKeys = new Set(prev.map(a => a.agentId));
                      const newAgents = (status.agents || [])
                        .filter(a => !existingKeys.has(a.taskId))
                        .map(a => ({ agentId: a.taskId, agentType: a.agentId, name: a.name, state: 'thinking', isLead: false, exchangeLog: [] }));
                      return newAgents.length > 0 ? [...prev, ...newAgents] : prev;
                    });
                  }
                  // Mission execution can chain a planning call, one or more
                  // execution iterations, and sometimes a 60-90s document
                  // generation, all before this chat turn returns anything —
                  // surface what's actually happening in the same "W" phase
                  // pill the UI already renders custom labels for, instead of
                  // a flat "Thinking..." for the whole wait.
                  const missionPhaseLabels = {
                    mission_planning: 'Planning the mission...',
                    mission_executing: 'Running the mission...',
                    mission_generating_document: 'Generating the document — this can take a minute...',
                  };
                  if (missionPhaseLabels[status.phase]) {
                    setMessages(prev => prev.map((msg, index) =>
                      index === prev.length - 1 && msg.isLoading
                        ? { ...msg, phases: [{ id: 'W', state: 'active', label: missionPhaseLabels[status.phase], tasks: [] }] }
                        : msg
                    ));
                  }
                  if (status.phase === 'idle') _stopPoll();
                } catch { /* ignore poll errors */ }
              }, 400);
            }

            try {
              responseData = await streamChatResponse(requestBody, {
                onThinking: () => {
                  setLoadingPhase("thinking");
                },
                onStreamStart: () => {
                  setLoadingPhase("streaming");
                  // Build phases from current sub-agents (via ref — avoids stale closure).
                  // If sub-agents exist: [T done] + [C active with tasks] so C persists through streaming.
                  // If no sub-agents (simple query): leave phases null — keep T active via render fallback.
                  // Setting [T done] here causes a visible gap: user sees T done with no W for the full
                  // T stays active during streaming; finalization sets T done + W active atomically when needed.
                  const streamStartPhases = null;
                  setMessages((prevMessages) =>
                    prevMessages.map((msg, index) =>
                      index === prevMessages.length - 1 && msg.isLoading
                        ? {
                            ...msg,
                            // Keep whatever text was streamed before this point (e.g. the intro).
                            // Do NOT reset to "" — that wipes the intro the agent already spoke.
                            isStreaming: true,
                            loadingPhase: "streaming",
                            ...(streamStartPhases ? { phases: streamStartPhases } : {}),
                            agent_info: msg.agent_info || leadAgentRef.current || undefined,
                          }
                        : msg,
                    ),
                  );
                },
                onChunk: (content) => {
                  streamingText += content;
                  // Append to last text segment (create one if last segment is a tool)
                  const lastSeg = streamingSegments[streamingSegments.length - 1];
                  if (lastSeg && lastSeg.type === 'text') {
                    lastSeg.rawContent += content;
                  } else {
                    streamingSegments.push({ type: 'text', rawContent: content });
                  }
                  const now = Date.now();
                  if (now - lastStreamRenderRef.current >= 30) {
                    lastStreamRenderRef.current = now;
                    const displayText = sanitizeStreamingMarkdown(stripJsonForDisplay(streamingText));
                    const displaySegments = streamingSegments.map(seg =>
                      seg.type === 'text'
                        ? { ...seg, displayContent: sanitizeStreamingMarkdown(stripJsonForDisplay(seg.rawContent)) }
                        : seg
                    );
                    setMessages((prevMessages) =>
                      prevMessages.map((msg, index) =>
                        index === prevMessages.length - 1 && (msg.isLoading || msg.isStreaming)
                          ? {
                              ...msg,
                              text: displayText,
                              rawText: streamingText,
                              segments: displaySegments,
                              isLoading: false,
                              isStreaming: true,
                              isPlaceholder: false,
                            }
                          : msg,
                      ),
                    );
                  }
                },
                onDone: (data) => {
                  _stopPoll();
                  responseData = data;
                  // Auto-reset to advisory when a task completes
                  if (data?.task_complete) {
                    setInteractionMode('advisory');
                  }
                  if (data?.conversation_too_long) {
                    setCompressionSuggested(true);
                  }
                  // Do NOT update message text here — the main block will apply cleanMessageText +
                  // formatAIMessage and set the final version in one shot, preventing a double-render blink.
                  // Persist agent exchange log + pending plan steps to last message if available
                  if (data.agent_exchange_log?.length > 0 || data.pending_plan_steps?.length > 0 || data.agent_plan?.length > 0 || data.agent_run_id) {
                    setMessages(prev => prev.map((m, i) =>
                      i === prev.length - 1 ? {
                        ...m,
                        ...(data.agent_run_id ? { agentRunId: data.agent_run_id } : {}),
                        ...(data.agent_exchange_log?.length > 0 ? { agentExchangeLog: data.agent_exchange_log } : {}),
                        ...(data.pending_plan_steps?.length > 0 ? { pendingPlanSteps: data.pending_plan_steps } : {}),
                        ...(data.agent_plan?.length > 0 ? { agentPlan: data.agent_plan } : {}),
                      } : m
                    ));
                  }
                },
                onAgentRun: (data) => {
                  if (!data?.id) return;
                  setMessages(prev => prev.map((m, i) =>
                    i === prev.length - 1 && (m.isLoading || m.isStreaming || m.isPlaceholder)
                      ? { ...m, agentRunId: data.id, agentRunStatus: data.status, agentRunPhase: data.current_phase }
                      : m
                  ));
                },
                onAgentStart: (data) => {
                  if (data.is_lead) {
                    // Set agent_info synchronously on the loading message so AgentPill appears during T shimmer.
                    const agentInfo = {
                      agentId: data.agent_id,
                      agentType: data.agent_id,
                      agent_name: data.agent_name,
                      name: data.agent_name,
                      isLead: true,
                    };
                    leadAgentRef.current = agentInfo;
                    setMessages(prev => prev.map((msg, i) =>
                      i === prev.length - 1 && (msg.isLoading || msg.isStreaming)
                        ? { ...msg, agent_info: agentInfo }
                        : msg
                    ));
                  }
                },
                onTaskChunk: (data) => {
                  const { taskId, content } = data;

                  // consult_agents sub-task: route chunks to the create_agent_task card.
                  // Sub-task IDs are "{openai_tool_call_id}-{i}" — extract the tool call ID.
                  const consultMatch = taskId && taskId.match(/^(call_[A-Za-z0-9]+)-\d+$/);
                  if (consultMatch) {
                    const toolCallId = consultMatch[1];
                    const segIdx = streamingSegments.findIndex(s => s.type === 'tool' && s.tool_call_id === toolCallId);
                    if (segIdx !== -1) {
                      streamingSegments[segIdx] = {
                        ...streamingSegments[segIdx],
                        streamContent: (streamingSegments[segIdx].streamContent || '') + content,
                      };
                    }
                    setMessages(prev => prev.map((m, i) =>
                      i === prev.length - 1 && (m.isLoading || m.isStreaming)
                        ? {
                            ...m,
                            segments: [...streamingSegments],
                            agentToolCalls: streamingSegments.filter(s => s.type === 'tool'),
                          }
                        : m
                    ));
                    return;
                  }
                },
                onAgentToolStart: (data) => {
                  const capability = data?.params?.capability;
                  const shouldSignalContentExecution =
                    data?.tool === 'create_agent_task' &&
                    capability &&
                    (data.params?.execute_now === true || data.params?.requires_approval === false);
                  if (shouldSignalContentExecution) {
                    window.dispatchEvent(new CustomEvent('pabs:capabilityExecuting', {
                      detail: { capability, planId, params: data.params },
                    }));
                  }

                  const toolSeg = {
                    type:            'tool',
                    tool_call_id:    data.tool_call_id,
                    tool:            data.tool,
                    userDescription: data.userDescription,
                    params_schema:   data.params_schema,
                    params:          data.params,
                    status:          'running',
                  };
                  streamingSegments.push(toolSeg);
                  streamingSegments.push({ type: 'text', rawContent: '' }); // post-tool text lands here
                  setMessages(prev => prev.map((m, i) =>
                    i === prev.length - 1 && (m.isLoading || m.isStreaming)
                      ? {
                          ...m,
                          isLoading: false,   // make tool card visible even if no pre-tool text was streamed
                          isStreaming: true,
                          isPlaceholder: false,
                          agentToolCalls: [...(m.agentToolCalls || []), { ...toolSeg }],
                          // Recompute displayContent so pre-tool text stays visible when the card appears
                          segments: streamingSegments.map(seg =>
                            seg.type === 'text'
                              ? { ...seg, displayContent: sanitizeStreamingMarkdown(stripJsonForDisplay(seg.rawContent)) }
                              : seg
                          ),
                        }
                      : m
                  ));
                },
                onAgentToolDone: (data) => {
                  const startedCapability = data?.params?.capability;
                  const completedCapability =
                    startedCapability ||
                    data?.result?.capability ||
                    data?.result?.task?.capability;
                  if (
                    data?.tool === 'create_agent_task' &&
                    completedCapability &&
                    (data?.result?.executed === true || data?.params?.execute_now === true)
                  ) {
                    window.dispatchEvent(new CustomEvent('pabs:capabilityExecuted', {
                      detail: {
                        capability: completedCapability,
                        planId,
                        result: data.result,
                        status: data.status,
                      },
                    }));
                    // The task already persisted the update server-side. Refresh the plan
                    // from the database; never route rawValue back through a mutating
                    // capability handler or the same update is executed a second time.
                    const taskResult = data?.result?.result;
                    if (PRODUCT_STRATEGY_REACT_FIELDS.has(taskResult?.reactField)) {
                      // ProductStrategyView owns its own loaded report state, so it
                      // needs its targeted refresh event. PlanPage emits the broader
                      // pabs-plan-updated refresh once during finalization below.
                      window.dispatchEvent(new CustomEvent('productStrategyUpdated', {
                        detail: {
                          updatedFields: [taskResult.reactField],
                          planId: planId || currentPlanId,
                        },
                      }));
                    }
                  }

                  // Update the tool segment in the mutable streamingSegments array
                  const segIdx = streamingSegments.findIndex(s => s.type === 'tool' && s.tool_call_id === data.tool_call_id);
                  if (segIdx !== -1) {
                    streamingSegments[segIdx] = { ...streamingSegments[segIdx], result: data.result, status: data.status };
                  }
                  setMessages(prev => prev.map((m, i) =>
                    i === prev.length - 1 && (m.isLoading || m.isStreaming)
                      ? {
                          ...m,
                          agentToolCalls: (m.agentToolCalls || []).map(tc =>
                            tc.tool_call_id === data.tool_call_id
                              ? { ...tc, result: data.result, status: data.status }
                              : tc
                          ),
                          // Recompute displayContent on text segments so pre/post-tool text stays visible
                          segments: streamingSegments.map(s =>
                            s.type === 'tool' && s.tool_call_id === data.tool_call_id
                              ? { ...s, result: data.result, status: data.status }
                              : s.type === 'text'
                              ? { ...s, displayContent: sanitizeStreamingMarkdown(stripJsonForDisplay(s.rawContent)) }
                              : s
                          ),
                        }
                      : m
                  ));
                },
                onDocumentGenerationEvent: (eventType, data) => {
                  window.dispatchEvent(new CustomEvent('pabs:documentGeneration', {
                    detail: { ...data, eventType, planId: planId || currentPlanId },
                  }));
                },
                onStateSync: async (data) => {
                  // Inline updates are already persisted server-side. State sync must
                  // only reload authoritative data, never execute the capability again.
                  if (!PRODUCT_STRATEGY_REACT_FIELDS.has(data.reactField)) return;
                  window.dispatchEvent(new CustomEvent('productStrategyUpdated', {
                    detail: {
                      updatedFields: [data.reactField],
                      planId: planId || currentPlanId,
                    },
                  }));
                },
                onSimulatedTyping: async (data) => {
                  // Proxy buffered all chunks - simulate typing for dev UX
                  console.log("⌨️ Simulating typing effect (dev mode, proxy buffered)");
                  const fullText = data.bot_message || '';
                  // Pre-strip JSON for the typing simulation display
                  const displayFullText = stripJsonForDisplay(fullText);
                  
                  // Return a promise that resolves when typing is complete
                  await new Promise((resolve) => {
                    simulateTyping(
                      displayFullText, // Type the clean text without JSON
                      (partialText) => {
                        // Update message progressively
                        setMessages((prevMessages) =>
                          prevMessages.map((msg, index) =>
                            index === prevMessages.length - 1 && (msg.isLoading || msg.isStreaming)
                              ? {
                                  ...msg,
                                  text: partialText,
                                  rawText: fullText, // Keep raw for CTA parsing later
                                  isLoading: false,
                                  isStreaming: true,
                                  isPlaceholder: false,
                                }
                              : msg,
                          ),
                        );
                      },
                      () => {
                        // Typing complete
                        console.log("✅ Simulated typing complete");
                        resolve();
                      },
                      { charsPerFrame: 4, frameDelay: 12 } // Slightly faster for dev
                    );
                  });
                  
                  // Set responseData after typing completes
                  responseData = data;
                },
                onError: (error) => {
                  console.error("❌❌❌ STREAMING ERROR FROM SERVER:", error.message);
                  console.error("❌❌❌ STREAMING ERROR STACK:", error.error);
                  throw new Error(error.message || "Streaming failed");
                },
              });
            } catch (streamError) {
              _stopPoll();
              console.error("❌ Streaming failed:", streamError);
              throw streamError;
            }


            if (!responseData) {
              console.error("❌ Response data is null or undefined");
              throw new Error("Server returned null response");
            }

            // FIX: Use streamingText as fallback when bot_message is empty (capability-only responses)
            // This ensures modify_tools_content and other JSON-only capabilities work
            if (!responseData.bot_message && streamingText) {
              console.log("📋 Using streamed text as fallback for capability-only response");
              responseData.bot_message = streamingText;
            }

            // FIX: Allow empty bot_message if we have feature_enablement_proposal, component_action, OR streamed content
            if (!responseData.bot_message && !responseData.feature_enablement_proposal && !responseData.component_action && !streamingText) {
              console.error("❌ Response missing bot_message:", responseData);
              throw new Error("Server response missing bot_message");
            }
            
            // Set default message for feature enablement or tool-only response if empty
            if (!responseData.bot_message && (responseData.feature_enablement_proposal || responseData.component_action)) {
              responseData.bot_message = "";
            }


            const containsHtml = /<\/?[a-z][\s\S]*>/i.test(
              responseData.bot_message,
            );

            // Process AI response using AIActionManager
            const actionContext = {
              finalProgramSequence,
              selectedPrograms,
              currentPlanId,
            };

            // ✨ Feature suggestions from backend
            const featureSuggestions = responseData.feature_suggestions || [];
            if (featureSuggestions.length > 0) {
              console.log(`✨ Received ${featureSuggestions.length} feature suggestions`);
            }

            // 📚 Chat-detected learnings count
            const chatLearningsCount = responseData.chat_learnings_count || 0;
            if (chatLearningsCount > 0) {
              console.log(`📚 ${chatLearningsCount} learning(s) detected and auto-submitted`);
              onLearningsCountChanged?.(chatLearningsCount);
            }

            // 🤖 AI ACTION DETECTION: Parse the response for actionable content
            try {

              // Ensure we have a valid message string
              const messageText =
                typeof responseData.bot_message === "string"
                  ? responseData.bot_message
                  : String(responseData.bot_message || "");

              // Registry tools (web_search, create_agent_task, etc.) are executed natively in the
              // agentic loop — they must never become Apply-button actions. Filter them out whether
              // they came from the server component_action or were detected in the response text.
              const REGISTRY_CAPABILITIES = new Set([
                'web_search', 'search_market_intelligence',
                'create_agent_task', 'run_task', 'get_task', 'update_task', 'cancel_task', 'archive_task',
                'evaluate_learning_impact', 'read_strategy_context', 'read_plan_learnings', 'read_competitor_data',
                'read_uploaded_file',
                'get_plan_context',
              ]);

              // Client-side AI response parsing is disabled — all updates now go through
              // native tool calls (create_agent_task task cards) or backend component_action.
              // Parsing agent text for capabilities created ghost Apply buttons.
              const rawDetectedActions = [];
              const detectedActions = [];
              const actionsWithPreviews = [];

              // Strip any residual JSON blocks from the message text (fallback safety net)
              const cleanText = stripJsonForDisplay(messageText);

              const finalFormattedText = formatAIMessage(
                typeof cleanText === "string"
                  ? cleanText
                  : String(cleanText || messageText),
              );

              // Create the bot message
              // FIX #37: Include component_action from backend for capability confirmations
              // Convert component_action to action format with CORRECT preview type for AIActionHandler
              const hasComponentAction = responseData.component_action &&
                responseData.awaiting_confirmation &&
                isComponentActionCapability(responseData.component_action.capability);
              if (responseData.component_action && responseData.awaiting_confirmation && !hasComponentAction) {
                console.warn('Blocked unsupported component action:', responseData.component_action.capability);
              }
              const hasFeatureEnablementProposal = !!responseData.feature_enablement_proposal;
              
              // FIX #38: Filter out save_plan actions when we have feature_enablement_proposal
              // Feature enablement already handles saving, so showing both is redundant
              // CRITICAL: Ensure actionsWithPreviews is always an array before filtering
              const safeActionsWithPreviews = Array.isArray(actionsWithPreviews) ? actionsWithPreviews : [];
              let filteredActionsWithPreviews = safeActionsWithPreviews;
              if (hasFeatureEnablementProposal || hasComponentAction) {
                filteredActionsWithPreviews = safeActionsWithPreviews.filter(action => {
                  const isSavePlan = action?.capabilityId === 'save_plan' || 
                                     action?.type === 'save_plan' ||
                                     action?.preview?.type === 'default';
                  if (isSavePlan) {
                    console.log("🎯 FIX #38: Filtering out redundant save_plan action (feature enablement handles this)");
                  }
                  return !isSavePlan;
                });
              }
              
              let finalActions = filteredActionsWithPreviews || [];
              
              // FIX: Skip component_action entirely if parseAIResponse already returned actions
              // This prevents duplicate UI confirmations caused by both sources creating actions
              // parseAIResponse is the canonical source; component_action is a legacy fallback only
              const hasActionsFromParser = filteredActionsWithPreviews.length > 0;
              const componentCapability = responseData.component_action?.capability;
              
              // Check if parser already detected this specific capability or bulk actions
              const parserHasSameCapability = hasActionsFromParser && filteredActionsWithPreviews.some(action => 
                action.capabilityId === componentCapability ||
                action.type === componentCapability ||
                action.capabilityId === 'bulk_actions' || 
                action.type === 'bulk_actions' ||
                action.preview?.type === 'bulk_actions'
              );
              
              if (parserHasSameCapability) {
                console.log("🔍 Parser already detected capability, skipping component_action to prevent duplicate:", componentCapability);
                if (responseData.additional_actions && responseData.additional_actions.length > 0) {
                  filteredActionsWithPreviews = filteredActionsWithPreviews.map(act => 
                    (act.capabilityId === componentCapability || act.type === componentCapability)
                      ? { ...act, additional_actions: responseData.additional_actions }
                      : act
                  );
                  console.log(`🎯 Attached ${responseData.additional_actions.length} additional_actions to parser-detected action: ${componentCapability}`);
                }
              } else if (hasComponentAction && responseData.component_action && !GENERATION_CAPABILITIES.has(responseData.component_action?.capability)) {
                const componentAction = responseData.component_action;
                const capability = componentAction.capability || 'unknown';
                const actionData = componentAction.data || {};
                
                // FIX #37: Map capability to correct AIActionHandler preview type
                const getPreviewTypeAndFields = (cap, data) => {
                  // Product strategy section updates (Strategy view)
                  const productStrategyUpdates = [
                    'update_strategic_themes', 'update_design_vision', 'update_audience_insights',
                    'update_opportunity_areas', 'update_design_hypotheses', 'update_brief_deconstruction',
                    'update_information_assessment', 'update_strategic_guidance',
                    'update_competitive_ux_positioning', 'update_experience_design_principles',
                    'update_success_metrics', 'update_unmet_needs',
                  ];
                  
                  if (productStrategyUpdates.includes(cap)) {
                    const psNameMap = {
                      'update_strategic_themes': 'Strategic Themes',
                      'update_design_vision': 'Design Vision',
                      'update_audience_insights': 'Audience Insights',
                      'update_opportunity_areas': 'Opportunity Areas',
                      'update_design_hypotheses': 'Design Hypotheses',
                      'update_brief_deconstruction': 'Brief Deconstruction',
                      'update_information_assessment': 'Information Assessment',
                      'update_strategic_guidance': 'Strategic Guidance',
                      'update_competitive_ux_positioning': 'Competitive Positioning',
                      'update_experience_design_principles': 'Experience Design Principles',
                      'update_success_metrics': 'Success Metrics',
                      'update_unmet_needs': 'Unmet Needs',
                    };
                    return {
                      type: 'product_strategy_update',
                      componentName: psNameMap[cap] || cap.replace('update_', '').replace(/_/g, ' '),
                      view: 'Product Strategy',
                      changeType: 'replace',
                    };
                  }

                  // Brief component updates (Brief view)
                  const strategyComponents = [
                    'update_objectives', 'update_goal', 'update_target', 'update_problem',
                    'update_metrics', 'update_deliverables', 'update_stakeholders', 
                    'update_summary', 'update_goal_statement', 'update_target_statement',
                    'update_problem_statement', 'update_objectives_statement',
                    'update_deliverables_statement', 'update_metrics_statement',
                    'update_stakeholders_statement',
                  ];
                  
                  if (strategyComponents.includes(cap)) {
                    const componentName = cap.replace('update_', '').replace(/_/g, ' ');
                    const fieldName = cap.replace('update_', '');
                    const fieldValue = data[fieldName] || data[fieldName.replace(/_([a-z])/g, (_, c) => c.toUpperCase())];
                    let displayValue = data.value || data.content || data.newValue || fieldValue;
                    if (!displayValue) displayValue = JSON.stringify(data);
                    if (Array.isArray(displayValue)) displayValue = displayValue.map(item => typeof item === 'string' ? item : JSON.stringify(item)).join(', ');
                    return {
                      type: 'strategy_component_update',
                      componentName: componentName.charAt(0).toUpperCase() + componentName.slice(1),
                      newValue: typeof displayValue === 'string' ? displayValue : JSON.stringify(displayValue),
                    };
                  }
                  
                  // Tool additions - check if it's multiple tools (bulk) or single tool
                  if (cap === 'add_tool' || cap === 'add_tools_to_stages') {
                    // FIX #49: Extract all tools from various data structures
                    let allTools = [];
                    let programName = data.program || 'Program';
                    
                    // Case 1: data.programs array with toolsByStage (bulk program merge format)
                    if (data.programs && Array.isArray(data.programs)) {
                      data.programs.forEach(prog => {
                        programName = prog.program_of_work || programName;
                        if (prog.toolsByStage) {
                          Object.entries(prog.toolsByStage).forEach(([stageName, stageData]) => {
                            if (stageData.tools && Array.isArray(stageData.tools)) {
                              stageData.tools.forEach(tool => {
                                allTools.push({
                                  ...tool,
                                  stageName: stageName,
                                  programName: prog.program_of_work || programName
                                });
                              });
                            }
                          });
                        }
                      });
                    }
                    
                    // Case 2: data.tools direct array
                    if (data.tools && Array.isArray(data.tools)) {
                      data.tools.forEach(tool => {
                        allTools.push({
                          ...tool,
                          stageName: data.stage || tool.related_stage || 'Stage'
                        });
                      });
                    }
                    
                    // Case 3: Single tool
                    if (allTools.length === 0 && (data.tool || data.toolName)) {
                      allTools.push({
                        tool: data.tool || data.toolName,
                        stageName: data.stage || 'Stage',
                        duration: data.duration || 8
                      });
                    }
                    
                    console.log('🔧 FIX #49: Extracted tools for UI:', {
                      totalTools: allTools.length,
                      fromPrograms: data.programs?.length || 0,
                      fromToolsArray: data.tools?.length || 0,
                      toolNames: allTools.map(t => t.tool || t.name || 'unknown').slice(0, 5)
                    });
                    
                    // Show as bulk_actions if multiple tools
                    if (allTools.length > 1) {
                      const toolActions = allTools.map((tool, idx) => ({
                        id: `add-tool-${idx}`,
                        type: 'add_tool',
                        action: 'add_tool',
                        description: `Add "${tool.tool || tool.name || 'Tool'}" to ${tool.stageName}`,
                        preview: {
                          type: 'add_tool',
                          toolName: tool.tool || tool.name || 'Tool',
                          stage: tool.stageName,
                          duration: tool.duration || 8,
                        }
                      }));
                      
                      // Group by stage for summary
                      const stageGroups = allTools.reduce((acc, t) => {
                        const stage = t.stageName || 'Stage';
                        acc[stage] = (acc[stage] || 0) + 1;
                        return acc;
                      }, {});
                      const stageSummary = Object.entries(stageGroups)
                        .map(([stage, count]) => `${count} to ${stage}`)
                        .join(', ');
                      
                      return {
                        type: 'bulk_actions',
                        summary: `Add ${allTools.length} tools (${stageSummary})`,
                        actions: toolActions,
                        isCrossProgram: false,
                        isCrossStage: Object.keys(stageGroups).length > 1,
                      };
                    }
                    
                    // Single tool addition
                    const singleTool = allTools[0] || {};
                    return {
                      type: 'add_tool',
                      toolName: singleTool.tool || singleTool.name || data.tool || data.toolName || 'New Tool',
                      program: programName,
                      stage: singleTool.stageName || data.stage || 'Stage',
                      duration: singleTool.duration || data.duration || 8,
                    };
                  }
                  
                  // Tool removals
                  if (cap === 'remove_tool' || cap === 'remove_tools_from_stages') {
                    return {
                      type: 'remove_tool',
                      toolName: data.tool || data.toolName || 'Tool',
                      toolsToRemove: data.tools || [data.tool || data.toolName],
                      toolCount: data.tools?.length || 1,
                      program: data.program || 'Program',
                      stage: data.stage || 'Stage',
                    };
                  }
                  
                  // Duration changes
                  if (cap === 'update_duration' || cap === 'change_duration' || cap === 'tool_duration_editing') {
                    return {
                      type: 'duration_change',
                      toolName: data.tool || data.toolName || 'Tool',
                      program: data.program || 'Program',
                      stage: data.stage || 'Stage',
                      currentDuration: data.currentDuration || 'Unknown',
                      newDuration: data.newDuration || data.duration || 8,
                    };
                  }
                  
                  // Tool content modifications (rename, update description, etc)
                  if (cap === 'modify_tools_content') {
                    const modifications = data.modifications || {};
                    const modificationsList = Object.entries(modifications)
                      .map(([key, value]) => `${key}: ${value}`)
                      .join(', ');
                    return {
                      type: 'tool_modification',
                      component: 'tool_management',
                      program: data.program || 'Program',
                      stage: data.stage || 'Stage',
                      toolIdentifier: data.toolIdentifier || data.tool || 'Tool',
                      modifications: modificationsList || 'Update tool content',
                    };
                  }
                  
                  // Bulk actions
                  if (cap === 'bulk_actions' || (data.actions && Array.isArray(data.actions))) {
                    return {
                      type: 'bulk_actions',
                      summary: data.summary || `${data.actions?.length || 0} changes`,
                      actions: data.actions || [],
                      isCrossProgram: data.isCrossProgram || false,
                      isCrossStage: data.isCrossStage || false,
                    };
                  }
                  
                  // Default fallback - use description
                  return {
                    type: 'default',
                    description: componentAction.description || `Apply changes using ${cap}`,
                  };
                };
                
                const previewFields = getPreviewTypeAndFields(capability, actionData);
                
                const actionFromComponent = {
                  id: `capability-${Date.now()}`,
                  capabilityId: capability,
                  type: capability,
                  action: capability,
                  description: componentAction.description || `Execute ${capability.replace(/_/g, ' ')}`,
                  data: actionData,
                  preview: previewFields,
                  status: 'pending',
                  additional_actions: responseData.additional_actions || undefined,
                };
                // FIX #38: Use filteredActionsWithPreviews (not actionsWithPreviews) to exclude redundant save_plan
                // FIX: Deduplicate - only add component_action if not already in filteredActionsWithPreviews
                const existingActions = filteredActionsWithPreviews || [];
                const isDuplicate = existingActions.some(action => 
                  action.capabilityId === capability && 
                  JSON.stringify(action.data) === JSON.stringify(actionData)
                );
                if (isDuplicate) {
                  console.log("🔍 Skipping duplicate component_action - already in parsed actions:", capability);
                  finalActions = existingActions;
                } else {
                  finalActions = [...existingActions, actionFromComponent];
                }
                console.log("🎯 FIX #37: Converted component_action to action with correct preview type:", {
                  capability,
                  previewType: previewFields?.type,
                  actionFromComponent,
                  filteredOutSavePlan: (actionsWithPreviews?.length || 0) !== (filteredActionsWithPreviews?.length || 0)
                });
              }
              
              // FIX: Ensure finalActions is always an array to prevent .length crash
              const safeActions = Array.isArray(finalActions) ? finalActions : [];
              
              const botMessage = {
                text: finalFormattedText,
                sender: "bot",
                timestamp: new Date().toISOString(),
                isHtml: containsHtml,
                agent_info: responseData.agent_info || displayAgent,
                actions: safeActions,
                awaitingConfirmation: safeActions.length > 0,
                feature_enablement_proposal: responseData.feature_enablement_proposal,
                component_action: responseData.component_action,
                additional_actions: responseData.additional_actions || undefined,
                chatLearningsCount,
              };

              // Add proposed actions if they exist
              if (
                responseData.proposed_actions &&
                responseData.proposed_actions.length > 0
              ) {
                botMessage.proposedActions = responseData.proposed_actions;
              }


              // Block subscriber during finalization — replaceLastPlaceholder triggers it async,
              // the direct setMessages below is the single authoritative render update.
              isFinalizingStreamRef.current = true;

              // Pre-compute autoExecuteActions here so finalizedPhases can include W active when needed.
              // This prevents a flash of the fully-finalized advisory state (T✓ C✓ AgentPill) between
              // the finalization render and the EXEC-START render — the two setMessages calls may not
              // always be batched by React 18 depending on the async scheduling path.
              const autoExecuteActions = safeActions.filter(
                a => (ALWAYS_AUTO_EXECUTE.has(a.capabilityId) || a.autoExecute === true) &&
                     TIER_A_CAPABILITIES.has(a.capabilityId) &&
                     !NEVER_AUTO_EXECUTE.has(a.capabilityId) &&
                     currentPlanId
              );

              // Compute T done phases from agent_exchange_log — used in both ChatStateManager and React state
              const finalizedPhases = (() => {
                const rawLog = responseData.agent_exchange_log || [];
                const tasks = rawLog.map(entry => {
                  if (entry.task) {
                    const { task, result } = entry;
                    return { id: task.task_id, label: result?.agent_name || task.agent || 'Agent', state: 'done',
                      agentInfo: { id: task.task_id, name: result?.agent_name || task.agent, state: 'done', isLead: false,
                        instruction: task.instruction || '', detail: result ? [{ type: 'result', ...result }] : [] } };
                  }
                  return { id: entry.agentId || String(Math.random()), label: entry.name || 'Agent', state: 'done', agentInfo: entry };
                });
                // T always persists (icon-only), consulting tasks go to C pill
                const phases = tasks.length > 0
                  ? [{ id: 'T', state: 'done', tasks: [] }, { id: 'C', state: 'done', tasks }]
                  : [{ id: 'T', state: 'done', tasks: [] }];
                return phases;
              })();

              // Replace placeholder with actual bot response content
              // Build agentToolCalls from streamingSegments so they persist in ChatStateManager + get saved to DB
              const finalAgentToolCalls = streamingSegments
                .filter(s => s.type === 'tool')
                .map(s => ({
                  tool_call_id: s.tool_call_id,
                  tool:         s.tool,
                  userDescription: s.userDescription || undefined,
                  params:       s.params || undefined,
                  result:       s.result || undefined,
                  status:       s.status || 'success',
                }));
              const finalSegments = streamingSegments.length > 1
                ? streamingSegments.map(seg =>
                    seg.type === 'text'
                      ? { ...seg, displayContent: sanitizeStreamingMarkdown(stripJsonForDisplay(seg.rawContent)) }
                      : seg
                  )
                : undefined;

              // Dispatch plan refresh for any update_* tasks that executed immediately (execute_now:true)
              if (finalAgentToolCalls.some(tc =>
                tc.tool === 'create_agent_task' && tc.result?.executed === true &&
                tc.result?.section
              )) {
                window.dispatchEvent(new CustomEvent('pabs-plan-updated'));
              }

              chatStateManager.replaceLastPlaceholder(
                finalFormattedText,
                {
                  isHtml: containsHtml,
                  agent_info: responseData.agent_info || displayAgent,
                  actions: safeActions,
                  awaitingConfirmation: safeActions.length > 0,
                  proposedActions: responseData.proposed_actions || [],
                  feature_enablement_proposal: responseData.feature_enablement_proposal,
                  // component_action is transient streaming state — not saved to avoid stale Apply buttons on reload
                  additional_actions: responseData.additional_actions || undefined,
                  chatLearningsCount,
                  agentRunId: responseData.agent_run_id || undefined,
                  agentExchangeLog: responseData.agent_exchange_log?.length > 0 ? responseData.agent_exchange_log : undefined,
                  phases: finalizedPhases,
                  // Persist task cards so they render correctly when conversation is reloaded
                  agentToolCalls: finalAgentToolCalls.length > 0 ? finalAgentToolCalls : undefined,
                  // Persist interleaving so tool cards reload in their original position.
                  segments: finalSegments,
                }
              );

              // FIX: Sync React state with ChatStateManager after streaming completes
              // This ensures actions render correctly since streaming only updates text
              // Use sender + isStreaming/isPlaceholder flags to find the bot placeholder reliably
              // Compute genCapability here so we can capture the closing conv history snapshot
              // inside the setMessages callback (where prev is the live state, not a stale closure).
              const genCapability = responseData.component_action?.capability;
              setMessages((prevMessages) => {
                // Capture closing conv history while we have the freshest messages array.
                // This avoids the stale-closure bug where executeGenerationWorkflow reads
                // messages from an old render and gets CA context instead of PS context.
                if (genCapability && GENERATION_CAPABILITIES.has(genCapability)) {
                  closingConvHistoryRef.current = prevMessages.slice(-2);
                }

                // Find the last bot message that is still streaming/placeholder
                const placeholderIndex = prevMessages.findLastIndex(
                  (msg) => msg.sender === 'bot' && (msg.isStreaming || msg.isPlaceholder)
                );
                if (placeholderIndex === -1) return prevMessages;

                return prevMessages.map((msg, index) =>
                  index === placeholderIndex
                    ? {
                        ...msg,
                        text: finalFormattedText,
                        rawText: responseData.bot_message || '',
                        isLoading: false,
                        isStreaming: false,
                        isPlaceholder: false,
                        thinkingDone: true,
                        actions: safeActions,
                        awaitingConfirmation: safeActions.length > 0,
                        proposedActions: responseData.proposed_actions || [],
                        feature_enablement_proposal: responseData.feature_enablement_proposal,
                        component_action: responseData.component_action,
                        additional_actions: responseData.additional_actions || undefined,
                        agent_info: responseData.agent_info || displayAgent,
                        chatLearningsCount,
                        agentRunId: responseData.agent_run_id || msg.agentRunId,
                        // Preserve agentExchangeLog so the work activity bar stays visible after loading
                        agentExchangeLog: responseData.agent_exchange_log?.length > 0
                          ? responseData.agent_exchange_log
                          : msg.agentExchangeLog,
                        // Always overwrite with authoritative phases from agent_exchange_log
                        phases: finalizedPhases,
                        agentToolCalls: finalAgentToolCalls.length > 0
                          ? finalAgentToolCalls
                          : msg.agentToolCalls,
                        // Persist interleaved segments — used for ordered text+tool rendering.
                        // Only set when tool calls occurred (segments.length > 1).
                        segments: finalSegments || msg.segments,
                      }
                    : msg
                );
              });
              // If a generation workflow is about to fire, pre-engage the execution guard BEFORE
              // clearing isFinalizingStream. This closes the timing window where the chatStateManager
              // subscriber's async await resolves between the two guards being false and merges messages
              // without internal flags (_executing, _awaitingClosing), causing execution_done to miss.
              if (genCapability && GENERATION_CAPABILITIES.has(genCapability)) {
                isBackendExecutingRef.current = true;
              }

              // Release finalization guard — subscriber can fire freely from here on
              isFinalizingStreamRef.current = false;

              // Generation capabilities bypass the Apply button flow entirely.
              // The agent's tool call triggers the SSE generation stream directly.
              if (genCapability && GENERATION_CAPABILITIES.has(genCapability)) {
                executeGenerationWorkflow(genCapability, responseData.component_action?.data || {});
              }

              if (safeActions.some(a => a.autoExecute && NEVER_AUTO_EXECUTE.has(a.capabilityId))) {
                console.warn('⚠️ AI flagged strategic content as auto_execute — blocked. Apply button will show instead.');
              }
              if (autoExecuteActions.length > 0) {
                const autoCapIds = new Set(autoExecuteActions.map(a => a.capabilityId));
                console.log(`⚡ Auto-executing ${autoExecuteActions.length} action(s):`, [...autoCapIds]);
                // Immediately remove auto-execute actions from the message so Apply button never appears
                setMessages(prev => prev.map(msg => {
                  if (!msg.actions?.length) return msg;
                  const remaining = msg.actions.filter(a => !autoCapIds.has(a.capabilityId));
                  if (remaining.length === msg.actions.length) return msg;
                  return { ...msg, actions: remaining, awaitingConfirmation: remaining.length > 0 };
                }));
                // Snapshot T phase tasks from agent_exchange_log — the authoritative source.
                // Each entry is { task: {task_id, agent, instruction, ...}, result: {agent_name, result, duration_ms} }
                const rawAgentLog = responseData?.agent_exchange_log || [];
                const tPhaseTasks = rawAgentLog.map(entry => {
                  if (entry.task) {
                    const { task, result } = entry;
                    return {
                      id: task.task_id,
                      label: result?.agent_name || task.agent || 'Agent',
                      state: 'done',
                      agentInfo: {
                        id: task.task_id,
                        name: result?.agent_name || task.agent,
                        state: 'done',
                        isLead: false,
                        instruction: task.instruction || '',
                        detail: result ? [{ type: 'result', ...result }] : [],
                      },
                    };
                  }
                  // Fallback for legacy format
                  return {
                    id: entry.agentId || entry.id || String(Math.random()),
                    label: entry.name || entry.agentType || 'Agent',
                    state: entry.state === 'done' ? 'done' : 'active',
                    agentInfo: entry,
                  };
                });
                // No setTimeout — synchronous call so React 18 batches this with the finalization
                // setMessages above → single render, no intermediate flash of avatar/text/legacy pill
                const originalMessage = messages.filter(m => m.sender === "user").slice(-1)[0]?.text || "";
                const [primary, ...rest] = autoExecuteActions;
                const batchedAction = {
                  ...primary,
                  additional_actions: rest.map(a => ({ capability: a.capabilityId, data: a.data })),
                };
                // Do NOT compute advisoryTargetIdx from the stale `messages` closure — the advisory
                // is still isStreaming:true here so any index found would point to an old message.
                // React 18 batches all synchronous setMessages in this block, so by the time
                // executeViaBackend's EXEC-START setMessages(prev=>) fires, prev already has the
                // finalized advisory (isLoading:false, rawText set). The dynamic fallback finds it.
                setIsExecutingAction(true);
                executeViaBackend(batchedAction, originalMessage, {
                  inPlace: true,
                  skipPreText: true,
                  tPhaseTasks,
                }).finally(() => {
                  setIsExecutingAction(false);
                  setExecutingActionId(null);
                });
              }

            } catch (error) {
              console.error("Error processing AI response:", error);

              // Fallback: create simple bot message
              const messageText =
                typeof responseData.bot_message === "string"
                  ? responseData.bot_message
                  : String(responseData.bot_message || "");
              const fallbackFormattedText = formatAIMessage(messageText);

              // FIX #37: Include component_action in fallback too - convert to action format with CORRECT preview type
              const hasFallbackComponentAction = responseData.component_action &&
                responseData.awaiting_confirmation &&
                isComponentActionCapability(responseData.component_action.capability);
              let fallbackActions = [];
              
              if (hasFallbackComponentAction && responseData.component_action) {
                const componentAction = responseData.component_action;
                const capability = componentAction.capability || 'unknown';
                const actionData = componentAction.data || {};
                
                // FIX #37: Same mapping function as main flow
                const getPreviewTypeAndFields = (cap, data) => {
                  const strategyComponents = [
                    'update_objectives', 'update_goal', 'update_target', 'update_problem',
                    'update_metrics', 'update_deliverables', 'update_stakeholders', 
                    'update_summary', 'update_goal_statement', 'update_target_statement',
                    'update_problem_statement', 'update_objectives_statement',
                    'update_deliverables_statement', 'update_metrics_statement',
                    'update_stakeholders_statement', 'update_experience_design_principles'
                  ];
                  
                  if (strategyComponents.includes(cap)) {
                    const componentName = cap.replace('update_', '').replace(/_/g, ' ');
                    const fieldName = cap.replace('update_', '');
                    const fieldValue = data[fieldName] || data[fieldName.replace(/_([a-z])/g, (_, c) => c.toUpperCase())];
                    let displayValue = data.value || data.content || data.newValue || fieldValue;
                    if (!displayValue) displayValue = JSON.stringify(data);
                    if (Array.isArray(displayValue)) displayValue = displayValue.map(item => typeof item === 'string' ? item : JSON.stringify(item)).join(', ');
                    return {
                      type: 'strategy_component_update',
                      componentName: componentName.charAt(0).toUpperCase() + componentName.slice(1),
                      newValue: typeof displayValue === 'string' ? displayValue : JSON.stringify(displayValue),
                    };
                  }
                  
                  // Tool additions - check if it's multiple tools (bulk) or single tool
                  if (cap === 'add_tool' || cap === 'add_tools_to_stages') {
                    // FIX #49: Extract all tools from various data structures (FALLBACK VERSION)
                    let allTools = [];
                    let programName = data.program || 'Program';
                    
                    // Case 1: data.programs array with toolsByStage (bulk program merge format)
                    if (data.programs && Array.isArray(data.programs)) {
                      data.programs.forEach(prog => {
                        programName = prog.program_of_work || programName;
                        if (prog.toolsByStage) {
                          Object.entries(prog.toolsByStage).forEach(([stageName, stageData]) => {
                            if (stageData.tools && Array.isArray(stageData.tools)) {
                              stageData.tools.forEach(tool => {
                                allTools.push({
                                  ...tool,
                                  stageName: stageName,
                                  programName: prog.program_of_work || programName
                                });
                              });
                            }
                          });
                        }
                      });
                    }
                    
                    // Case 2: data.tools direct array
                    if (data.tools && Array.isArray(data.tools)) {
                      data.tools.forEach(tool => {
                        allTools.push({
                          ...tool,
                          stageName: data.stage || tool.related_stage || 'Stage'
                        });
                      });
                    }
                    
                    // Case 3: Single tool
                    if (allTools.length === 0 && (data.tool || data.toolName)) {
                      allTools.push({
                        tool: data.tool || data.toolName,
                        stageName: data.stage || 'Stage',
                        duration: data.duration || 8
                      });
                    }
                    
                    // Show as bulk_actions if multiple tools
                    if (allTools.length > 1) {
                      const toolActions = allTools.map((tool, idx) => ({
                        id: `add-tool-${idx}`,
                        type: 'add_tool',
                        action: 'add_tool',
                        description: `Add "${tool.tool || tool.name || 'Tool'}" to ${tool.stageName}`,
                        preview: {
                          type: 'add_tool',
                          toolName: tool.tool || tool.name || 'Tool',
                          stage: tool.stageName,
                          duration: tool.duration || 8,
                        }
                      }));
                      
                      const stageGroups = allTools.reduce((acc, t) => {
                        const stage = t.stageName || 'Stage';
                        acc[stage] = (acc[stage] || 0) + 1;
                        return acc;
                      }, {});
                      const stageSummary = Object.entries(stageGroups)
                        .map(([stage, count]) => `${count} to ${stage}`)
                        .join(', ');
                      
                      return {
                        type: 'bulk_actions',
                        summary: `Add ${allTools.length} tools (${stageSummary})`,
                        actions: toolActions,
                        isCrossProgram: false,
                        isCrossStage: Object.keys(stageGroups).length > 1,
                      };
                    }
                    
                    // Single tool addition
                    const singleTool = allTools[0] || {};
                    return {
                      type: 'add_tool',
                      toolName: singleTool.tool || singleTool.name || data.tool || data.toolName || 'New Tool',
                      program: programName,
                      stage: singleTool.stageName || data.stage || 'Stage',
                      duration: singleTool.duration || data.duration || 8,
                    };
                  }
                  
                  if (cap === 'remove_tool' || cap === 'remove_tools_from_stages') {
                    return {
                      type: 'remove_tool',
                      toolName: data.tool || data.toolName || 'Tool',
                      toolsToRemove: data.tools || [data.tool || data.toolName],
                      toolCount: data.tools?.length || 1,
                      program: data.program || 'Program',
                      stage: data.stage || 'Stage',
                    };
                  }
                  
                  if (cap === 'update_duration' || cap === 'change_duration' || cap === 'tool_duration_editing') {
                    return {
                      type: 'duration_change',
                      toolName: data.tool || data.toolName || 'Tool',
                      program: data.program || 'Program',
                      stage: data.stage || 'Stage',
                      currentDuration: data.currentDuration || 'Unknown',
                      newDuration: data.newDuration || data.duration || 8,
                    };
                  }
                  
                  if (cap === 'bulk_actions' || (data.actions && Array.isArray(data.actions))) {
                    return {
                      type: 'bulk_actions',
                      summary: data.summary || `${data.actions?.length || 0} changes`,
                      actions: data.actions || [],
                      isCrossProgram: data.isCrossProgram || false,
                      isCrossStage: data.isCrossStage || false,
                    };
                  }
                  
                  return {
                    type: 'default',
                    description: componentAction.description || `Apply changes using ${cap}`,
                  };
                };
                
                const previewFields = getPreviewTypeAndFields(capability, actionData);
                
                const actionFromComponent = {
                  id: `capability-${Date.now()}`,
                  capabilityId: capability,
                  type: capability,
                  action: capability,
                  description: componentAction.description || `Execute ${capability.replace(/_/g, ' ')}`,
                  data: actionData,
                  preview: previewFields,
                  status: 'pending',
                  additional_actions: responseData.additional_actions || undefined,
                };
                fallbackActions = [actionFromComponent];
                console.log("🎯 FIX #37 Fallback: Converted component_action to action with correct preview type:", {
                  capability,
                  previewType: previewFields.type,
                  actionFromComponent
                });
              }
              
              const botMessage = {
                text: fallbackFormattedText,
                sender: "bot",
                timestamp: new Date().toISOString(),
                isHtml: containsHtml,
                agent_info: responseData.agent_info || displayAgent,
                actions: fallbackActions,
                awaitingConfirmation: fallbackActions.length > 0,
                feature_enablement_proposal: responseData.feature_enablement_proposal,
                component_action: responseData.component_action,
                additional_actions: responseData.additional_actions || undefined,
                chatLearningsCount,
              };

              if (
                responseData.proposed_actions &&
                responseData.proposed_actions.length > 0
              ) {
                botMessage.proposedActions = responseData.proposed_actions;
              }

              // Replace placeholder with actual bot response content
              chatStateManager.replaceLastPlaceholder(
                fallbackFormattedText,
                {
                  isHtml: containsHtml,
                  agent_info: responseData.agent_info || displayAgent,
                  actions: fallbackActions,
                  awaitingConfirmation: fallbackActions.length > 0,
                  proposedActions: responseData.proposed_actions || [],
                  feature_enablement_proposal: responseData.feature_enablement_proposal,
                  // component_action intentionally omitted — transient, not saved to history
                  additional_actions: responseData.additional_actions || undefined,
                  chatLearningsCount,
                  phases: [{ id: 'T', state: 'done', tasks: [] }],
                  agentToolCalls: (() => {
                    const calls = streamingSegments.filter(s => s.type === 'tool').map(s => ({
                      tool_call_id: s.tool_call_id, tool: s.tool,
                      userDescription: s.userDescription || undefined,
                      params: s.params || undefined, result: s.result || undefined,
                      status: s.status || 'success',
                    }));
                    return calls.length > 0 ? calls : undefined;
                  })(),
                }
              );

              // FIX: Sync React state with ChatStateManager after fallback processing
              // Use sender + isStreaming/isPlaceholder flags to find the bot placeholder reliably
              setMessages((prevMessages) => {
                const placeholderIndex = prevMessages.findLastIndex(
                  (msg) => msg.sender === 'bot' && (msg.isStreaming || msg.isPlaceholder)
                );
                if (placeholderIndex === -1) return prevMessages;

                return prevMessages.map((msg, index) =>
                  index === placeholderIndex
                    ? {
                        ...msg,
                        text: fallbackFormattedText,
                        isStreaming: false,
                        isPlaceholder: false,
                        actions: fallbackActions,
                        awaitingConfirmation: fallbackActions.length > 0,
                        proposedActions: responseData.proposed_actions || [],
                        feature_enablement_proposal: responseData.feature_enablement_proposal,
                        component_action: responseData.component_action,
                        additional_actions: responseData.additional_actions || undefined,
                        agent_info: responseData.agent_info || displayAgent,
                        chatLearningsCount,
                        phases: msg.phases || [{ id: 'T', state: 'done', tasks: [] }],
                      }
                    : msg
                );
              });
            }

            // const { cleanText, actions } = await processAIResponse(result.bot_message, context);

            // // Format the AI message for better readability - use the raw bot_message for formatting
            // const formattedText = formatAIMessage(result.bot_message);

            // // Add the bot message to the conversation
            // const botMessage = {
            //   text: formattedText,
            //   sender: "bot",
            //   timestamp: new Date().toISOString(),
            //   isHtml: containsHtml,
            //   agent_info: result.agent_info || quickSelectedAgent,
            //   actions: actions, // Store detected actions
            //   awaitingConfirmation: actions.length > 0,
            // };

            // Check for proposed actions in the response
            // if (result.proposed_actions && result.proposed_actions.length > 0) {
            //   botMessage.proposedActions = result.proposed_actions;
            // }

            // // Complete the response (the thinking time was the actual AI processing)
            // setMessages((prevMessages) => [
            //   ...prevMessages.slice(0, -1),
            //   botMessage,
            // ]);

            if (responseData.suggested_content?.length > 0) {
              const formattedContent = responseData.suggested_content
                .map((item) => `- **${item.title}**: ${item.description}`)
                .join("\n");
              setSuggestedContent(formattedContent);
              setTargetComponent(responseData.content_type);
            } else if (responseData.bot_message) {
              setSuggestedContent(responseData.bot_message);
              setTargetComponent(null);
            } else {
              console.warn(
                "No suggested content or bot message found in API response:",
                responseData,
              );
              setSuggestedContent("No content available.");
              setTargetComponent(null);
            }

            setIsAwaitingConfirmation(
              Object.prototype.hasOwnProperty.call(
                COMPONENT_MAP,
                responseData.content_type,
              ),
            );

            // Detect plan creation recommendation
            const botMessage = responseData.bot_message || '';
            // Strip markdown to avoid detection issues
            const cleanMessage = botMessage.replace(/\*\*/g, '').toLowerCase();
            const isQuickPlanRecommendation = cleanMessage.includes('ready to create your quick plan') || 
                                            cleanMessage.includes('creating a quick plan') ||
                                            cleanMessage.includes('recommend creating a quick plan') ||
                                            cleanMessage.includes('would you like to proceed with creating the quick plan');
            const isFullPlanRecommendation = cleanMessage.includes('ready to create your full plan') || 
                                           cleanMessage.includes('creating a full plan') ||
                                           cleanMessage.includes('recommend creating a full plan') ||
                                           cleanMessage.includes('would you like to proceed with creating the full plan');
            
            if (isQuickPlanRecommendation || isFullPlanRecommendation) {
              // Get the full conversation for context
              const activeConversation = await chatStateManager.getActiveConversation();
              const conversationText = activeConversation.messages
                .filter(msg => msg.sender === 'user')
                .map(msg => msg.text)
                .join(' ');
              
              setAwaitingPlanCreation({
                planType: isQuickPlanRecommendation ? 'quick_plan' : 'autonomous_design_strategy',
                conversationSummary: conversationText || message
              });
              
              console.log('🎯 Detected plan creation recommendation:', {
                planType: isQuickPlanRecommendation ? 'Quick Strategy' : 'Full Strategy',
                awaitingConfirmation: true
              });
            }

            // Determine if we need to create a title for a new conversation
            let title = null;
            if (!currentConversationId) {
              // Extract summary from the user's first message in this new conversation
              const words = message.trim().split(/\s+/);
              title = words.slice(0, 7).join(" ");
              if (words.length > 7) {
                title += "...";
              }
            }

            // Conversation is automatically saved by ChatStateManager
          } catch (error) {
            console.error("Error during chatbot interaction:", error);
            const failedSegments = (lastStreamingSegmentsForError || [])
              .filter(seg => seg.type === 'tool' || seg.rawContent?.trim() || seg.displayContent?.trim())
              .map(seg => seg.type === 'tool' && seg.status === 'running'
                ? { ...seg, status: 'error', result: seg.result || { error: error.message } }
                : seg.type === 'text'
                ? { ...seg, displayContent: sanitizeStreamingMarkdown(stripJsonForDisplay(seg.rawContent || seg.displayContent || '')) }
                : seg
              );
            const failedToolCalls = failedSegments
              .filter(seg => seg.type === 'tool')
              .map(seg => ({
                tool_call_id: seg.tool_call_id,
                tool: seg.tool,
                userDescription: seg.userDescription || undefined,
                params: seg.params || undefined,
                result: seg.result || undefined,
                status: seg.status || 'error',
              }));

            const errorText = failedToolCalls.length > 0
              ? "Something went wrong before I could finish the full sequence. I preserved the tool activity above so we can continue from what actually ran."
              : "Something went wrong. Please try again later.";

            const replaced = await chatStateManager.replaceLastPlaceholder(errorText, {
              isHtml: false,
              phases: [{ id: 'T', state: 'done', tasks: [] }],
              agentToolCalls: failedToolCalls.length > 0 ? failedToolCalls : undefined,
              segments: failedSegments.length > 1 ? failedSegments : undefined,
            });

            if (!replaced) {
              chatStateManager.addMessage({
                text: errorText,
                sender: "bot",
                isHtml: false,
                agentToolCalls: failedToolCalls.length > 0 ? failedToolCalls : undefined,
                segments: failedSegments.length > 1 ? failedSegments : undefined,
              });
            }

            setMessages(prevMessages => {
              const placeholderIndex = prevMessages.findLastIndex(
                (msg) => msg.sender === 'bot' && (msg.isStreaming || msg.isPlaceholder || msg.isLoading)
              );
              if (placeholderIndex === -1) return prevMessages;
              return prevMessages.map((msg, index) =>
                index === placeholderIndex
                  ? {
                      ...msg,
                      text: errorText,
                      isLoading: false,
                      isStreaming: false,
                      isPlaceholder: false,
                      phases: [{ id: 'T', state: 'done', tasks: [] }],
                      agentToolCalls: failedToolCalls.length > 0 ? failedToolCalls : msg.agentToolCalls,
                      segments: failedSegments.length > 1 ? failedSegments : msg.segments,
                    }
                  : msg
              );
            });
          } finally {
            setLoading(false);
            setLoadingPhase("idle"); // Reset loading phase
          }
        },
        [
          awaitingPlanCreation,
          navigate,
          currentDesignChallenge,
          selectedPrograms,
          shortSummary,
          goalStatement,
          problemStatement,
          targetStatement,
          objectivesStatement,
          finalProgramSequence,
          deliverablesStatement,
          metricsStatement,
          stakeholdersCustomersInvolvement,
          totalDuration,
          input,
          messages,
          isAwaitingConfirmation,
          handleUserConfirmation,
          location.pathname,
          currentPlanId,
          currentConversationId,
          suggestedContent,
          targetComponent,
          selectedAgents,
          pendingAttachments,
        ],
      );

      useEffect(() => {
        const handleSendPlanChat = event => {
          if (event.detail?.planId && String(event.detail.planId) !== String(currentPlanId)) return;
          if (!isEmbedded) {
            setIsOpen(false);
            return;
          }
          const message = String(event.detail?.message || '').trim();
          if (!message) return;
          handleSend(message);
        };
        window.addEventListener('pabs:sendPlanChat', handleSendPlanChat);
        return () => window.removeEventListener('pabs:sendPlanChat', handleSendPlanChat);
      }, [currentPlanId, handleSend, isEmbedded]);

      // FIX #43: Moved useImperativeHandle AFTER handleSend definition to fix brief page chat
      // Previously at line 306, handleSend was used before it was defined
      useImperativeHandle(ref, () => ({
        toggleChat: () => setIsOpen((prev) => !prev),
        openChat: () => setIsOpen(true),
        handleSend: handleSend,
        addMessage: async (message) => {
          // Extra safety: Check if message is valid
          if (!message || typeof message !== "object") {
            console.warn("FloatingChatbot: Invalid message object", message);
            return;
          }

          // Handle progress UI updates specially - use SYNCHRONOUS upsert to prevent race conditions
          if (message.isProgressUIUpdate || message.isProgressUI) {
            const progressId = message.progressId || message.featureId || 'default';
            
            // DEBUG: Log exactly what steps are being received
            console.log("🔬 addMessage PROGRESS UPDATE (using sync upsert):", {
              progressId,
              incomingStepsCount: message.workflowSteps?.length,
              currentStep: message.currentStep
            });
            
            // Use SYNCHRONOUS upsert - this prevents the race condition where two async calls
            // both find no existing message and both add new ones
            const success = chatStateManager.upsertProgressMessage(progressId, message);
            
            if (success) {
              // Force React to update by getting the current messages
              const activeConversation = await chatStateManager.getActiveConversation();
              if (activeConversation) {
                setMessages([...activeConversation.messages]);
              }
            } else {
              console.warn("FloatingChatbot: Failed to upsert progress message");
            }
          } else {
            // Add regular message with validation
            const validatedMessage = {
              text: message.text || "",
              sender: message.sender || "bot",
              timestamp: message.timestamp || new Date().toISOString(),
              isHtml: message.isHtml || false,
              ...message,
            };

            chatStateManager.addMessage(validatedMessage);
          }
        },
        replaceMessage: (id, newMessage) => {
          chatStateManager.replaceMessage(id, newMessage);
        },
        loadConversation: async (conversationArray, conversationId) => {
          console.log(
            "FloatingChatbot: Loading conversation",
            conversationId,
            "with",
            (conversationArray || []).length,
            "messages",
          );

          if (!conversationId) {
            console.error("FloatingChatbot: No conversation ID provided");
            return;
          }

          try {
            // Load conversation through ChatStateManager
            const loadedConversation =
              await chatStateManager.loadConversation(conversationId);

            if (loadedConversation) {
              const reconstructToolSegments = (msg) => {
                if (msg.segments?.length) return msg.segments;
                if (msg.sender !== 'bot' || !msg.agentToolCalls?.length) return undefined;

                const text = String(msg.message || msg.text || '');
                const paragraphBreak = text.indexOf('\n\n');
                const canSplitIntro = paragraphBreak > 0 && paragraphBreak <= 260;
                const intro = canSplitIntro ? text.slice(0, paragraphBreak).trim() : '';
                const rest = canSplitIntro ? text.slice(paragraphBreak).trim() : text.trim();

                return [
                  ...(intro ? [{
                    type: 'text',
                    rawContent: intro,
                    displayContent: sanitizeStreamingMarkdown(stripJsonForDisplay(intro)),
                  }] : []),
                  ...msg.agentToolCalls.map(tc => ({ ...tc, type: 'tool' })),
                  ...(rest ? [{
                    type: 'text',
                    rawContent: rest,
                    displayContent: sanitizeStreamingMarkdown(stripJsonForDisplay(rest)),
                  }] : []),
                ];
              };

              // Convert legacy format if needed
              const formattedMessages = (conversationArray || [])
                .filter(
                  (msg) =>
                    (msg.message || msg.text) &&
                    String(msg.message || msg.text).trim() !== "" &&
                    msg.message !== "Workflow Progress",
                )
                .map((msg) => ({
                  text: msg.message || msg.text,
                  sender: msg.sender,
                  timestamp: msg.timestamp,
                  isHtml: msg.isHtml || false,
                  agentExchangeLog: msg.agentExchangeLog || undefined,
                  agent_info: msg.agent_info || undefined,
                  rawText: msg.rawText || undefined,
                  closingText: msg.closingText !== undefined ? msg.closingText : undefined,
                  actions: msg.actions || undefined,
                  awaitingConfirmation: msg.awaitingConfirmation || undefined,
                  proposedActions: msg.proposedActions || undefined,
                  feature_enablement_proposal: msg.feature_enablement_proposal || undefined,
                  additional_actions: msg.additional_actions || undefined,
                  chatLearningsCount: msg.chatLearningsCount || undefined,
                  phases: msg.sender === 'bot'
                    ? (msg.phases || [{ id: 'T', state: 'done', tasks: [] }])
                    : undefined,
                  agentToolCalls: msg.agentToolCalls?.length ? msg.agentToolCalls : undefined,
                  segments: reconstructToolSegments(msg),
                }));

              // Update the loaded conversation with formatted messages
              loadedConversation.messages = formattedMessages;

              // Force update both conversation and messages state
              setConversation({ ...loadedConversation }); // Force new object reference
              lastLoadedCountRef.current = formattedMessages.length; // mark all as historical
              setMessages([...formattedMessages]); // Force new array reference

              // Force scroll to bottom after messages load — give virtualizer time to initialize
              setTimeout(scrollToBottom, 50);
              setTimeout(scrollToBottom, 300);

              console.log(
                "FloatingChatbot: Successfully loaded conversation with",
                formattedMessages.length,
                "messages",
              );
            } else {
              console.error(
                "FloatingChatbot: Failed to load conversation from ChatStateManager",
              );
            }
          } catch (error) {
            console.error(
              "FloatingChatbot: Error loading conversation:",
              error,
            );
          }
        },
        startNewChat: async (forceReset = false) => {
          activeRefinementRef.current = null;
          const newConversation = await chatStateManager.startNewChat(forceReset);
          console.log(`[PABS-DEBUG][chatRef.startNewChat] newConv=${newConversation?.id} msgCount=${newConversation?.messages?.length}`, newConversation?.messages?.map((m,i)=>({i,sender:m.sender,ts:m.timestamp,text:(m.text||'').slice(0,60)})));
          setConversation(newConversation);
          setMessages([...(newConversation?.messages || [])]);
          if (!isEmbedded) {
            setIsOpen(true);
          }
        },
        addProgressUIMessage: () => {
          chatStateManager.addMessage({
            sender: "progress",
            isHtml: false,
            isProgressUI: true,
          });
          scrollToBottom();
        },
        updateWorkflowStatus: (newStatus) => {
          console.log("Workflow status updated:", newStatus);
        },
      }), [handleSend, isEmbedded, scrollToBottom]);

      const [isExpanded, setIsExpanded] = useState(false);
      const [isMinimised, setIsMinimised] = useState(false);
      const [initialPromptSent, setInitialPromptSent] = useState(false);

      const chatWidth = { base: "100%", md: isExpanded ? "65vh" : "500px" };
      const chatHeight = { base: "100%", md: isMinimised ? "30vh" : isExpanded ? "95vh" : "600px" };

      useEffect(() => {
        if (isOpen && prefilledPrompt && !initialPromptSent) {
          setMessages((prev) => [
            ...prev,
            { text: prefilledPrompt, sender: "user", isHtml: false, timestamp: new Date().toISOString() },
          ]);
          handleSend(prefilledPrompt);
          setInitialPromptSent(true);
        }
      }, [isOpen, prefilledPrompt, handleSend, initialPromptSent]);

      useEffect(() => {
        if (!isOpen) {
          setInitialPromptSent(false);
        }
      }, [isOpen]);


      const renderConfirmationButtons = () => (
        <Flex mt={2} justify="center" wrap="wrap" gap={2}>
          <Button
            size="sm"
            colorScheme="green"
            onClick={() => handleUserConfirmation("confirm")}
          >
            Add content
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={() => handleUserConfirmation("update")}
          >
            Replace content
          </Button>
          <Button
            size="sm"
            colorScheme="red"
            onClick={() => handleUserConfirmation("delete")}
          >
            Delete
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => handleUserConfirmation("discard")}
          >
            Discard
          </Button>
        </Flex>
      );

      const startNewChat = async (forceReset = false) => {
        activeRefinementRef.current = null;
        const newConversation = await chatStateManager.startNewChat(forceReset);
        setConversation(newConversation);
        setMessages([...(newConversation?.messages || [])]);

        // Reset legacy state
        setSuggestedContent([]);
        setTargetComponent(null);
        setIsAwaitingConfirmation(false);
        setAwaitingPlanCreation(null);

        if (!isEmbedded) {
          setIsOpen(true);
        }
      };

      // Function to determine the loading message based on the loading phase and selected agent
      const getLoadingMessage = () => {
        if (loadingPhase === "routing") {
          return "Finding best Pab's Design AI agent...";
        } else if (loadingPhase === "analyzing" && selectedAgent) {
          return `Analyzing with ${selectedAgent.agent_name}...`;
        } else {
          return "Analyzing..."; // Fallback message
        }
      };

      // AgentActionUI Component (assuming this is definedelsewhere)
      const AgentActionUI = ({ action, onApply, onCancel }) => (
        <Flex mt={2} justify="center" wrap="wrap" gap={2}>
          <Button size="sm" colorScheme="green" onClick={() => onApply(action)}>
            Apply
          </Button>
          <Button size="sm" colorScheme="red" onClick={onCancel}>
            Cancel
          </Button>
        </Flex>
      );

      const renderMessage = (message, index) => {
        const isUser = message.sender === "user";
        const isBot = message.sender === "bot";

        if (isCompressedContextMessage(message)) return null;

        // Handle progress UI messages
        if (message.isProgressUI && message.workflowSteps) {
          return (
            <Box key={index} mb={4}>
              <WorkflowProgressUI
                steps={message.workflowSteps}
                featureGroups={message.featureGroups}
                currentStep={message.currentStep}
              />
            </Box>
          );
        }

        // Handle progress UI update messages - render as progress UI
        // CRITICAL FIX: Removed setMessages call during render (React anti-pattern)
        // Updates are now handled in addMessage function, not during render
        if (message.isProgressUIUpdate && message.workflowSteps) {
          // Just render the progress UI - the update was already applied in addMessage
          return (
            <Box key={index} mb={4}>
              <WorkflowProgressUI
                steps={message.workflowSteps}
                featureGroups={message.featureGroups}
                currentStep={message.currentStep}
              />
            </Box>
          );
        }

        const rawForCTAs = message.rawText || message.text || '';
        const rawMessage =
          typeof rawForCTAs === "string"
            ? rawForCTAs
            : String(rawForCTAs || "");
        const { cleanText: ctaParsedText, ctas: messageCTAs } = parseCTAsFromMessage(rawMessage);
        let cleanMessage = stripJsonForDisplay(ctaParsedText);
        if (message.sender === 'bot') {
          const labelPattern = /(\n?\s*)\*\*(Context|Goals|Frustrations|Opportunities|Hypothesis|Rationale|Success Indicator|For Audience|Principle|Description|Target|Metric|Need|Persona|Strategic Value|Potential Impact|Opportunity|Theme|Positioning Statement|Key Differentiators):/gi;
          cleanMessage = cleanMessage.replace(labelPattern, (match, prefix, label, offset) => {
            if (offset === 0) return `**${label}:`;
            return `\n\n**${label}:`;
          });
          cleanMessage = cleanMessage.replace(/([.!?…"*])\s*(Click \*\*Apply\*\*)/gi, '$1\n\n$2');
          // Normalize AI-generated markdown: remove spaces adjacent to ** and * markers.
          // Use [ \t]+ (not \s+) so we never match across newlines —
          // e.g. "** bold **" → "**bold**" but "**line1**\n**line2**" stays intact.
          cleanMessage = cleanMessage.replace(/\*\*[ \t]+(.+?)[ \t]+\*\*/g, '**$1**');
          cleanMessage = cleanMessage.replace(/\*\*[ \t]+(.+?)\*\*/g, '**$1**');
          cleanMessage = cleanMessage.replace(/\*\*(.+?)[ \t]+\*\*/g, '**$1**');
          cleanMessage = cleanMessage.replace(/(?<!\*)\*[ \t]+(.+?)[ \t]+\*(?!\*)/g, '*$1*');
          // Fix unclosed single-asterisk emphasis that doesn't close before a line break.
          // e.g. "you *do\nhave" → "you do\nhave" (the * was never closed on the same line)
          // When a non-space char immediately precedes *, insert a space to prevent word gluing
          // e.g. "proven*10h" → "proven 10h" not "proven10h"
          cleanMessage = cleanMessage.replace(/([^*\s\n])(\*(?!\*))([^*\n]+?)(\n|$)/g, '$1 $3$4');
          cleanMessage = cleanMessage.replace(/(?<!\*)\*(?!\*)([^*\n]+?)(\n|$)/g, '$1$2');
        }

        // ── Hoist phases computation so T pill renders above text, W/C renders below text ──
        // Compute effective phases:
        // 1. message.phases — set during execution (W active) or after finalization
        // 2. T active shimmer — shown while loading/streaming before phases exist
        // 3. null — injected/welcome messages with no chat phases
        let effectivePhases = null;
        if (message.sender === 'bot') {
          if (message.phases) {
            effectivePhases = message.phases;
          } else if (message.isLoading || message.isStreaming) {
            effectivePhases = [{ id: 'T', state: 'active', tasks: [] }];
          }
        }
        const msgAgentInfo = message.agent_info || { agentType: 'PABS', name: 'PABS', isLead: true };
        // AgentPill only when fully finalized — all phases done AND not streaming/loading.
        const msgIsFinalized = !!message.phases
          && !message.phases.some(p => p.state === 'active')
          && !message.isStreaming
          && !message.isLoading;
        // T pill hide: when any non-T phase exists, filter out T done from breadcrumb.
        const msgHasNonTPhase = effectivePhases ? effectivePhases.some(p => p.id !== 'T') : false;
        const msgVisiblePhases = effectivePhases
          ? (msgHasNonTPhase
              ? effectivePhases.filter(p => !(p.id === 'T' && p.state === 'done'))
              : effectivePhases)
          : [];
        // W-execution-only messages (Apply button result): has W phases but no T phase.
        // Their text IS the conclusion — render it below phases like closingText, not above.
        const msgIsExecutionOnly = effectivePhases
          ? effectivePhases.some(p => p.id === 'W') && !effectivePhases.some(p => p.id === 'T')
          : false;
        const githubOutcomeToolCall = getGithubOutcomeToolCall(message);
        // Breadcrumb debt cleanup: non-T phases are chronological work events.
        // They must render where they live in the message/event sequence, never
        // be hoisted above the assistant text by layout ordering.
        const phasesAbove = false;

        // Return the main message structure for normal messages
        return (
          <VStack
            key={index}
            alignSelf={message.sender === "user" ? "flex-end" : "flex-start"}
            width={message.sender === "user" ? undefined : "90%"}
            maxWidth={message.sender === "user" ? "90%" : undefined}
            mb={3}
            spacing={0}
            alignItems={message.sender === "user" ? "flex-end" : "flex-start"}
          >
            {/* ── T-only pill — above text, only while model is thinking (no text yet) ── */}
            {message.sender === 'bot' && !msgHasNonTPhase && msgVisiblePhases.length > 0 && (message.isLoading || message.isStreaming) && (() => {
              // T-only pill: using stable key={phase.id} so CSS transitions fire on prop changes.
              return (
                <Box mb={2} w="100%" px={3}>
                  <Flex align="center" gap={0} flexWrap="nowrap">
                    {msgVisiblePhases.map((phase, pi) => (
                      <PhasePill
                        key={phase.id} phase={phase}
                        isLast={pi === msgVisiblePhases.length - 1}
                        expanded={workExpanded === `${index}-${phase.id}`}
                        onToggle={() => setWorkExpanded(prev =>
                          prev === `${index}-${phase.id}` ? null : `${index}-${phase.id}`
                        )}
                      />
                    ))}
                  </Flex>
                  {effectivePhases.map((phase) => {
                    if (!phase.tasks?.length) return null;
                    const isWPhase = phase.id === 'W';
                    const isCPhase = phase.id === 'C';
                    const isKPhase = phase.id === 'K';
                    const isPhaseActive = phase.state === 'active';
                    // Active phases always show; done phases render always but animate via max-height
                    const isShellOpen = isPhaseActive || workExpanded === `${index}-${phase.id}`;

                    // Tool-type → icon mapping. Fallback per phase if toolType not set.
                    const TOOL_ICON_MAP = {
                      ai_call: 'auto_awesome', db_write: 'save',
                      web_search: 'travel_explore', bulk_actions: 'settings',
                      update_brief: 'edit_note', update_strategy: 'edit_note',
                      update_programs: 'edit_note', update_plan: 'edit_note',
                      notion_read_page: 'description', notion_search: 'manage_search',
                      figma_get_file: 'palette', github_search: 'code',
                    };
                    const phaseFallbackIcon = isWPhase ? 'settings' : isCPhase ? 'forum' : 'language';

                    // Shell border + bg: tinted when active, neutral when done
                    const shellBorderColor = isPhaseActive
                      ? (isWPhase ? '#FED7AA' : isCPhase ? '#BEE3F8' : '#E2E8F0')
                      : '#EDF2F7';
                    const shellBg = isPhaseActive
                      ? (isWPhase ? '#FFFAF5' : isCPhase ? '#F3F9FF' : '#FAFBFC')
                      : '#FAFBFC';

                    return (
                      <Box key={phase.id}
                        style={{
                          maxHeight: isShellOpen ? '600px' : '0px',
                          overflow: 'hidden',
                          transition: 'max-height 0.35s cubic-bezier(0.4,0,0.2,1)',
                        }}
                      >
                        {/* mt lives inside overflow:hidden so it collapses with the content */}
                        <Box mt={2}
                          className={isPhaseActive ? 'phase-shell-enter' : undefined}
                          border="1px solid" borderColor={shellBorderColor}
                          borderRadius="10px" overflow="hidden" bg={shellBg}
                          style={{ transition: 'background-color 0.4s ease, border-color 0.4s ease' }}
                        >
                        <Box px={3} py="10px">
                          {phase.tasks.map(t => {
                            const tActive = t.state === 'active';
                            const tDone   = t.state === 'done';
                            const tError  = t.state === 'error';

                            // Icon: done → task_alt, error → error_outline, active/pending → tool-type specific
                            const taskIcon = tDone ? 'task_alt'
                              : tError ? 'error_outline'
                              : (TOOL_ICON_MAP[t.toolType] || TOOL_ICON_MAP[t.capability] || phaseFallbackIcon);

                            const iconColor = tDone ? '#68D391'
                              : tError ? '#FC8181'
                              : tActive
                                ? (isWPhase ? '#ED8936' : isCPhase ? '#4299E1' : '#718096')
                                : '#CBD5E0';

                            // Done label: gray.700 (#4A5568) matches Figma — not the light gray.400
                            const labelColor = tDone ? '#4A5568'
                              : tError ? 'red.400'
                              : tActive
                                ? (isWPhase ? '#C05621' : isCPhase ? '#2B6CB0' : '#4A5568')
                                : '#A0AEC0';

                            // Log data: finalized tasks use .detail, live activeAgents tasks use .exchangeLog
                            const logData = t.agentInfo?.detail?.length > 0
                              ? t.agentInfo.detail
                              : t.agentInfo?.exchangeLog?.length > 0
                              ? t.agentInfo.exchangeLog
                              : null;
                            const hasExpandableLog = !!(logData?.length > 0) || !!(tDone && t.description);

                            // Short description (1 line, ≤55 chars):
                            // Active → subtitle or instruction context
                            // Done C → result summary from log, fallback to instruction
                            // Done W/K → truncated value preview from backend (e.g. "Updated X to 3h")
                            const rawAgentSummary = logData?.[0]?.result?.result?.summary
                              || logData?.[0]?.result?.summary
                              || null;
                            const doneDescription = (() => {
                              const raw = isCPhase
                                ? (rawAgentSummary || t.agentInfo?.instruction)
                                : (t.description || null);
                              if (!raw) return null;
                              return raw.length > 55 ? raw.substring(0, 55) + '…' : raw;
                            })();
                            const activeSubtitle = (() => {
                              const raw = t.subtitle || t.agentInfo?.instruction || null;
                              if (!raw) return null;
                              return raw.length > 55 ? raw.substring(0, 55) + '…' : raw;
                            })();

                            const isExpanded = expandedLogId === t.id;
                            // Task done while phase still active → compact inline (Figma: "in-line Streaming Done")
                            // Task done after phase completes → full expandable row (Figma: "task-row Done")
                            const isCompactDone = tDone && isPhaseActive;
                            const isReadTask = t.type === 'read';

                            return (
                              <Box key={t.id} mb="8px" _last={{ mb: 0 }} className="task-row-enter">
                                {/* Read task — lightweight "checking" row, always compact */}
                                {isReadTask ? (
                                  <Flex align="center" gap={1.5}>
                                    <Box as="span" className="material-icons-outlined" flexShrink={0}
                                      style={{ fontSize: '13px', color: tDone ? '#68D391' : '#63B3ED' }}>
                                      {tDone ? 'done' : 'manage_search'}
                                    </Box>
                                    <Box minW={0}>
                                      <Text fontSize="xs" fontWeight="400" style={{ color: tDone ? '#A0AEC0' : '#4A5568' }}>
                                        {t.label}
                                      </Text>
                                      {t.subtitle && (
                                        <Text fontSize="9px" style={{ color: '#CBD5E0', fontFamily: 'monospace' }}>
                                          {t.subtitle}
                                        </Text>
                                      )}
                                    </Box>
                                  </Flex>
                                ) : isCompactDone ? (
                                  /* Compact inline: ✓ label — no chevron, no subtitle */
                                  <Flex align="center" gap={1.5}>
                                    <Box as="span" className="material-icons-outlined" flexShrink={0}
                                      style={{ fontSize: '13px', color: '#68D391' }}>
                                      task_alt
                                    </Box>
                                    <Text fontSize="xs" fontWeight="400" style={{ color: '#A0AEC0' }}>
                                      {t.label}
                                    </Text>
                                  </Flex>
                                ) : (
                                <Flex
                                  align="flex-start" gap={1.5}
                                  cursor={hasExpandableLog ? 'pointer' : 'default'}
                                  onClick={hasExpandableLog ? () => setExpandedLogId(prev => prev === t.id ? null : t.id) : undefined}
                                  _hover={hasExpandableLog ? { opacity: 0.75 } : {}}
                                  role={hasExpandableLog ? 'button' : undefined}
                                >
                                  {tActive ? (
                                    <Spinner
                                      size="xs"
                                      color={isWPhase ? 'orange.400' : 'blue.400'}
                                      thickness="1.5px"
                                      speed="0.7s"
                                      flexShrink={0}
                                      mt="1px"
                                    />
                                  ) : (
                                    <Box as="span" className="material-icons-outlined" flexShrink={0} mt="1px"
                                      style={{ fontSize: '13px', color: iconColor, transition: 'color 0.2s ease' }}>
                                      {taskIcon}
                                    </Box>
                                  )}
                                  <Box flex={1} minW={0}>
                                    {/* Done W tasks: title = specific change, subtitle = capability name */}
                                    {/* Active or C tasks: title = label, subtitle = context */}
                                    <Text fontSize="xs" fontWeight={tActive ? '600' : '500'}
                                      className={tActive ? `phase-text-shimmer ${isWPhase ? 'task-title-shimmer-w' : 'task-title-shimmer-c'}` : undefined}
                                      style={tActive ? undefined : { color: labelColor, transition: 'color 0.2s ease' }}>
                                      {(tDone && !isCPhase && doneDescription) ? doneDescription : t.label}{tActive ? '...' : ''}
                                    </Text>
                                    {/* Subtitle line */}
                                    {tActive && activeSubtitle && (
                                      <Text fontSize="10px" lineHeight="1.4" mt="1px"
                                        fontStyle="italic"
                                        style={{ color: isWPhase ? '#C05621' : '#2C5282' }}>
                                        {activeSubtitle}
                                      </Text>
                                    )}
                                    {tDone && !isCPhase && doneDescription && (
                                      <Text fontSize="10px" lineHeight="1.4" mt="1px"
                                        style={{ color: '#A0AEC0' }}>
                                        {t.label}
                                      </Text>
                                    )}
                                    {tDone && isCPhase && doneDescription && (
                                      <Text fontSize="10px" lineHeight="1.4" mt="1px"
                                        style={{ color: '#718096' }}>
                                        {doneDescription}
                                      </Text>
                                    )}
                                  </Box>
                                  {hasExpandableLog && (
                                    <Box as="span" className="material-icons-outlined" flexShrink={0} mt="2px"
                                      style={{ fontSize: '12px', color: '#CBD5E0', transition: 'transform 0.25s ease-in-out',
                                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                      expand_more
                                    </Box>
                                  )}
                                </Flex>
                                )}

                                {/* Compact done tasks skip streaming and expand panels */}
                                {!isCompactDone && (<>
                                {/* Streaming content area — active task, all phases */}
                                {/* Streaming content area — only when there is real streaming content */}
                                {tActive && (t.streamingContent || t.agentInfo?.streamingContent || t.agentInfo?.instruction) && (
                                  <StreamingTextBox
                                    ml={5} mt="4px"
                                    content={(t.streamingContent || t.agentInfo?.streamingContent || t.agentInfo?.instruction || '').replace(/[{}\[\]"\\]/g, '').replace(/:\s+/g, ': ').replace(/,\s*\n/g, '\n').replace(/^\s*\n/gm, '')}
                                  />
                                )}

                                {/* Expandable log — smooth max-height transition */}
                                {hasExpandableLog && (
                                  <Box ml={5} mt={isExpanded ? 1 : 0}
                                    style={{
                                      maxHeight: isExpanded ? '160px' : '0px',
                                      overflow: 'hidden',
                                      transition: 'max-height 0.28s cubic-bezier(0.4,0,0.2,1), margin-top 0.2s ease',
                                    }}>
                                    {logData ? (
                                      <Box position="relative" className="expand-panel-fade expand-panel-fade-dark">
                                        <Box p={2} borderRadius="md" bg="gray.900" color="green.300"
                                          fontSize="10px" fontFamily="monospace" h="140px" overflowY="auto" whiteSpace="pre-wrap">
                                          {JSON.stringify(logData, null, 2)}
                                        </Box>
                                      </Box>
                                    ) : t.description ? (
                                      <Box position="relative" className="expand-panel-fade expand-panel-fade-light">
                                        <Box p={2} borderRadius="md" bg="gray.50" h="140px" overflowY="auto"
                                          fontSize="10px" lineHeight="1.6" style={{ color: '#718096', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                          {t.description}
                                        </Box>
                                      </Box>
                                    ) : null}
                                  </Box>
                                )}
                                </>)}
                              </Box>
                            );
                          })}
                        </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}

            {/* ── AgentPill for finalized T-only messages (no tools used) ── */}
            {message.sender === 'bot' && !msgHasNonTPhase && msgIsFinalized && msgVisiblePhases.length > 0 && (
              <Box mb={1}>
                <AgentPill agentInfo={msgAgentInfo} />
              </Box>
            )}

            {message.isProgressUI ? (
              <Box
                p={3}
                boxShadow="sm"
                lineHeight="1.6"
                borderRadius="16px"
                width="100%"
                bg="gray.50"
              >
                <WorkflowProgressUI
                  steps={message.workflowSteps || []}
                  featureGroups={message.featureGroups}
                  currentStep={message.currentStep}
                  variant="compact"
                  title="Creating Strategy"
                />
              </Box>
            ) : message.sender === 'bot' && message.isLoading ? null
              // Don't render an empty box — bot messages with phases but no text yet would
              // create a dead box that adds ~24px of blank space above the next message.
              : message.sender === 'bot' && (!message.text && !message.isStreaming || msgIsExecutionOnly) ? null : (
              <Box
                className={
                  message.sender === "user"
                    ? "user-message"
                    : index < lastLoadedCountRef.current
                    ? "bot-message-static"
                    : "bot-message"
                }
                p={3}
                lineHeight="1.6"
                borderRadius={message.sender === "user" ? "16px 0px 16px 16px" : "16px"}
                width={message.sender === "user" ? "fit-content" : "100%"}
              >
                {false && message.phases && (
                  <Box mb={2}>
                    <Flex align="center" gap={0} flexWrap="wrap" mb={2}>
                      {message.phases.map((phase, pi) => (
                        <PhasePill
                          key={phase.id}
                          phase={phase}
                          isLast={pi === message.phases.length - 1}
                          expanded={workExpanded === `${index}-${phase.id}`}
                          onToggle={() => setWorkExpanded(prev =>
                            prev === `${index}-${phase.id}` ? null : `${index}-${phase.id}`
                          )}
                        />
                      ))}
                    </Flex>
                    {/* Expanded task list for the toggled phase */}
                    {message.phases.map((phase, pi) => {
                      if (workExpanded !== `${index}-${phase.id}` || !phase.tasks?.length) return null;
                      const isWPhase = phase.id === 'W';
                      return (
                        <Box key={phase.id} mt={2} mb={1} pl={3}
                          borderLeft="2px solid"
                          borderColor={isWPhase ? 'orange.100' : 'blue.100'}
                        >
                          {phase.tasks.map(t => (
                            <Flex key={t.id} align="flex-start" gap={2} py="4px" fontSize="xs">
                              {/* T phase tasks (consulting): show agent avatar. W phase tasks: dot */}
                              {!isWPhase && t.agentInfo ? (
                                <Avatar
                                  size="xs" flexShrink={0} mt="1px"
                                  name={t.label}
                                  bg={t.state === 'done' ? 'green.400' : 'blue.300'}
                                  color="white"
                                  style={{ width: '16px', height: '16px', fontSize: '8px' }}
                                />
                              ) : (
                                <Box w="6px" h="6px" borderRadius="full" flexShrink={0} mt="5px"
                                  bg={t.state === 'done' ? 'green.400' : t.state === 'error' ? 'red.400' : t.state === 'active' ? (isWPhase ? 'orange.400' : 'blue.400') : 'gray.300'}
                                />
                              )}
                              <Box flex={1}>
                                <Flex align="center" gap={2}>
                                  <Text color={t.state === 'done' ? 'gray.500' : 'gray.700'}
                                    fontStyle={t.state === 'active' ? 'italic' : 'normal'}>
                                    {t.label}
                                  </Text>
                                  {t.state === 'active' && (
                                    <Text fontSize="10px" color={isWPhase ? 'orange.400' : 'blue.400'} fontWeight="500">
                                      running
                                    </Text>
                                  )}
                                  {!isWPhase && t.agentInfo?.instruction && (
                                    <Text fontSize="10px" color="gray.400" fontStyle="italic" noOfLines={1}>
                                      "{t.agentInfo.instruction.substring(0, 120)}{t.agentInfo.instruction.length > 120 ? '…' : ''}"
                                    </Text>
                                  )}
                                  {!isWPhase && t.agentInfo?.detail?.length > 0 && (
                                    <Box
                                      as="button"
                                      fontSize="10px"
                                      color="blue.400"
                                      cursor="pointer"
                                      flexShrink={0}
                                      onClick={() => setExpandedLogId(prev => prev === t.id ? null : t.id)}
                                    >
                                      {expandedLogId === t.id ? '▲' : '▼'}
                                    </Box>
                                  )}
                                </Flex>
                                {!isWPhase && expandedLogId === t.id && t.agentInfo?.detail?.length > 0 && (
                                  <Box mt={1} p={2} bg="gray.50" borderRadius="md" fontSize="10px" fontFamily="mono" color="gray.600" whiteSpace="pre-wrap" overflowX="auto" maxH="200px" overflowY="auto">
                                    {JSON.stringify(t.agentInfo.detail, null, 2)}
                                  </Box>
                                )}
                                {t.description && t.state === 'done' && (
                                  <Text fontSize="10px" color="gray.400" mt="1px" lineHeight="1.4">
                                    {t.description}
                                  </Text>
                                )}
                              </Box>
                            </Flex>
                          ))}
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {false && (() => {
                  // If this message uses phases, skip the legacy agent bar entirely
                  if (message.phases) return null;
                  // During loading/streaming the new Thinking pill handles display — legacy bar must not render
                  if (message.isLoading || message.isStreaming) return null;
                  const isLastBotMessage = index === messages.length - 1 && message.sender === 'bot';
                  const agents = normalizeAgents(
                    isLastBotMessage && activeAgents.length > 0 ? activeAgents : null,
                    message.agentExchangeLog
                  );
                  const subAgents = agents.filter(a => !a.isLead);
                  const leadAgent = agents.find(a => a.isLead);
                  if (agents.length === 0 && !message.thinkingDone) return null;
                  return (
                    <Box mb={3}>
                      <Flex align="center" gap={0} mb={workExpanded === index && subAgents.length > 0 ? 1 : 0}>
                        {/* T done — sparkle icon pill, always shows for finalized messages */}
                        {message.thinkingDone && (
                          <Flex
                            align="center" gap={1.5} px={2} minH="28px"
                            borderRadius="full" border="1px solid" borderColor="#CBD5E0"
                            style={{ backgroundColor: '#F7FAFC' }}
                          >
                            <Box as="span" className="material-icons-outlined"
                              style={{ fontSize: '12px', color: '#718096' }}>
                              auto_awesome
                            </Box>
                          </Flex>
                        )}
                        {/* Tasks done — X tasks pill, shown when sub-agents were used */}
                        {subAgents.length > 0 && (
                          <>
                            {message.thinkingDone && (
                              <Text fontSize="10px" color="gray.300" userSelect="none" flexShrink={0} mx="1px">·</Text>
                            )}
                            <Flex
                              as="button" align="center" gap={1.5}
                              px={3} py="5px"
                              borderRadius="full"
                              bg="#F7FAFC" border="1px solid" borderColor="#CBD5E0"
                              cursor="pointer" display="inline-flex"
                              _hover={{ opacity: 0.85 }}
                              onClick={() => setWorkExpanded(prev => prev === index ? null : index)}
                            >
                              <Box as="span" className="material-icons-outlined"
                                style={{ fontSize: '12px', color: '#718096' }}>
                                task_alt
                              </Box>
                              <Text fontSize="xs" fontWeight="medium" color="#4A5568" userSelect="none">
                                {subAgents.length} {subAgents.length === 1 ? 'task' : 'tasks'}
                              </Text>
                              <Box as="span" className="material-icons-outlined"
                                style={{ fontSize: '11px', color: '#718096', opacity: 0.5 }}>
                                {workExpanded === index ? 'expand_less' : 'expand_more'}
                              </Box>
                            </Flex>
                          </>
                        )}
                      </Flex>
                      {subAgents.length > 0 && workExpanded === index && (
                            <Box mt={1} pl={2} borderLeft="2px solid" borderColor="gray.100">
                              {agents.filter(a => a.isLead).map(a => (
                                <Flex key={a.id} align="center" gap={1} py="3px" fontSize="xs" color="gray.500">
                                  <Box w="6px" h="6px" borderRadius="full" bg="blue.400" flexShrink={0} />
                                  <Text fontStyle="italic">{a.name} ★ — orchestrating</Text>
                                </Flex>
                              ))}
                              {subAgents.map(a => (
                                <Box key={a.id}>
                                  <Flex
                                    align="center" gap={2} py="3px" fontSize="xs" color="gray.600"
                                    cursor={a.detail?.length > 0 ? 'pointer' : 'default'}
                                    onClick={() => a.detail?.length > 0 && setExpandedLogId(prev => prev === a.id ? null : a.id)}
                                    _hover={a.detail?.length > 0 ? { color: 'gray.800' } : {}}
                                  >
                                    <Box w="6px" h="6px" borderRadius="full" flexShrink={0}
                                      bg={a.state === 'done' ? 'green.400' : 'blue.400'} />
                                    <Text>{a.name}</Text>
                                    {a.detail?.length > 0 && (
                                      <Box as="span" className="material-icons-outlined"
                                        style={{ fontSize: '11px', color: 'var(--chakra-colors-gray-400)' }}>
                                        {expandedLogId === a.id ? 'expand_less' : 'expand_more'}
                                      </Box>
                                    )}
                                  </Flex>
                                  {a.instruction && (
                                    <Text fontSize="11px" color="gray.500" pl={4} pb="2px" fontStyle="italic">
                                      "{a.instruction.length > 120 ? a.instruction.substring(0, 120) + '…' : a.instruction}"
                                    </Text>
                                  )}
                                  {expandedLogId === a.id && (
                                    <Box mt={1} p={2} borderRadius="md" bg="gray.900" color="green.300"
                                      fontSize="10px" fontFamily="monospace" maxH="200px" overflowY="auto" whiteSpace="pre-wrap">
                                      {JSON.stringify(a.detail, null, 2)}
                                    </Box>
                                  )}
                                </Box>
                              ))}
                            </Box>
                          )}
                    </Box>
                  );
                })()}
                {message.attachments?.length > 0 && (
                  <Flex
                    align="center"
                    gap={2}
                    wrap="nowrap"
                    overflowX="auto"
                    overflowY="hidden"
                    maxW="100%"
                    pb={1}
                    mb={message.text ? 2 : 0}
                    sx={{
                      WebkitOverflowScrolling: "touch",
                      scrollbarWidth: "thin",
                    }}
                  >
                    {message.attachments.map((file, fileIndex) => (
                      <Flex
                        key={`${file.name}-${fileIndex}`}
                        align="center"
                        gap={1.5}
                        px={2.5}
                        py={1.5}
                        bg={message.sender === "user" ? "whiteAlpha.700" : "gray.50"}
                        border="1px solid"
                        borderColor={message.sender === "user" ? "blackAlpha.100" : "gray.200"}
                        borderRadius="lg"
                        flex="0 0 220px"
                        w="220px"
                        maxW="220px"
                        minW={0}
                      >
                        <Tooltip label={file.name} placement="top" openDelay={400} isDisabled={isTouch}>
                          <Text
                            fontSize="xs"
                            fontWeight="600"
                            color="gray.700"
                            isTruncated
                            flex="1 1 auto"
                            minW={0}
                            textAlign="left"
                          >
                            {file.name}
                          </Text>
                        </Tooltip>
                        <Text fontSize="2xs" color="gray.400" whiteSpace="nowrap" flexShrink={0}>
                          {formatAttachmentSize(file.size)}
                        </Text>
                      </Flex>
                    ))}
                  </Flex>
                )}

                {message.sender === 'bot' && message.segments?.length > 1 ? (
                  // Interleaved segments: text blocks and tool cards in the order they occurred
                  message.segments.map((seg, si) => {
                    if (seg.type === 'text') {
                      const segText = seg.displayContent || '';
                      if (!segText.trim()) return null;
                      const afterGithubTool = githubOutcomeToolCall && message.segments
                        .slice(0, si)
                        .some(prev => prev?.type === 'tool' && shouldRenderGithubOutcomeCard(prev));
                      if (afterGithubTool) return null;
                      return (
                        <ReactMarkdown key={si} children={segText} components={markdownComponents} />
                      );
                    }
                    if (seg.type === 'tool') {
                      if (shouldRenderGithubOutcomeCard(seg)) return null;
                      return (
                        <Box key={seg.tool_call_id || si} w="100%" mt={1} mb={1}>
                          <AgentToolCallCard toolCall={seg} />
                        </Box>
                      );
                    }
                    return null;
                  })
                ) : (
                  !message.isLoading ? (
                    <ReactMarkdown
                      children={
                        typeof cleanMessage === "string"
                          ? cleanMessage
                          : String(cleanMessage || "")
                      }
                      components={markdownComponents}
                    />
                  ) : null
                )}
              </Box>
            )}

            {/* ── W/C phases + task shells — below intro text, or above answer when finalized with no closing split ── */}
            {message.sender === 'bot' && msgHasNonTPhase && (() => {
              return (
                <Box mt={phasesAbove ? 0 : 2} mb={phasesAbove ? 2 : 1} w="100%" px={3} style={phasesAbove ? { order: -1 } : undefined}>
                  <Flex align="center" gap={0} flexWrap="nowrap">
                    {msgVisiblePhases.map((phase, pi) => (
                      <PhasePill
                        key={phase.id} phase={phase}
                        isLast={pi === msgVisiblePhases.length - 1 && !msgIsFinalized}
                        expanded={workExpanded === `${index}-${phase.id}`}
                        onToggle={() => setWorkExpanded(prev =>
                          prev === `${index}-${phase.id}` ? null : `${index}-${phase.id}`
                        )}
                      />
                    ))}
                    {msgIsFinalized && !message.isGenerationWorkflow && (
                      <>
                        <Text fontSize="10px" color="gray.300" userSelect="none" flexShrink={0} mx="1px">·</Text>
                        <AgentPill agentInfo={msgAgentInfo} />
                      </>
                    )}
                  </Flex>
                  {(effectivePhases || []).map((phase) => {
                    if (!phase.tasks?.length) return null;
                    const isWPhase = phase.id === 'W';
                    const isCPhase = phase.id === 'C';
                    const isPhaseActive = phase.state === 'active';
                    // Keep shell open during _awaitingClosing so there's no flicker between
                    // execution_done collapsing the shell and the auto-expand useEffect opening it.
                    const isShellOpen = isPhaseActive || workExpanded === `${index}-${phase.id}`
                      || (isWPhase && phase.tasks?.length > 0 && !!message._awaitingClosing);
                    const TOOL_ICON_MAP = {
                      ai_call: 'auto_awesome', db_write: 'save',
                      web_search: 'travel_explore', bulk_actions: 'settings',
                      update_brief: 'edit_note', update_strategy: 'edit_note',
                      update_programs: 'edit_note', update_plan: 'edit_note',
                      notion_read_page: 'description', notion_search: 'manage_search',
                      figma_get_file: 'palette', github_search: 'code',
                    };
                    const phaseFallbackIcon = isWPhase ? 'settings' : isCPhase ? 'forum' : 'language';
                    const shellBorderColor = isPhaseActive
                      ? (isWPhase ? '#FED7AA' : isCPhase ? '#BEE3F8' : '#E2E8F0') : '#EDF2F7';
                    const shellBg = isPhaseActive
                      ? (isWPhase ? '#FFFAF5' : isCPhase ? '#F3F9FF' : '#FAFBFC') : '#FAFBFC';
                    return (
                      <Box key={phase.id}
                        style={{ maxHeight: isShellOpen ? '600px' : '0px', overflow: 'hidden', transition: 'max-height 0.35s cubic-bezier(0.4,0,0.2,1)' }}
                      >
                        <Box mt={2}
                          className={isPhaseActive ? 'phase-shell-enter' : undefined}
                          border="1px solid" borderColor={shellBorderColor}
                          borderRadius="10px" overflow="hidden" bg={shellBg}
                          style={{ transition: 'background-color 0.4s ease, border-color 0.4s ease' }}
                        >
                          <Box px={3} py="10px">
                            {phase.tasks.map(t => {
                              const tActive = t.state === 'active';
                              const tDone = t.state === 'done';
                              const tError = t.state === 'error';
                              const taskIcon = tDone ? 'task_alt' : tError ? 'error_outline'
                                : (TOOL_ICON_MAP[t.toolType] || TOOL_ICON_MAP[t.capability] || phaseFallbackIcon);
                              const iconColor = tDone ? '#68D391' : tError ? '#FC8181'
                                : tActive ? (isWPhase ? '#ED8936' : isCPhase ? '#4299E1' : '#718096') : '#CBD5E0';
                              const labelColor = tDone ? '#4A5568' : tError ? 'red.400'
                                : tActive ? (isWPhase ? '#C05621' : isCPhase ? '#2B6CB0' : '#4A5568') : '#A0AEC0';
                              const logData = t.agentInfo?.detail?.length > 0 ? t.agentInfo.detail
                                : t.agentInfo?.exchangeLog?.length > 0 ? t.agentInfo.exchangeLog : null;
                              const hasExpandableLog = !!(logData?.length > 0) || !!(tDone && t.description);
                              const rawAgentSummary = logData?.[0]?.result?.result?.summary || logData?.[0]?.result?.summary || null;
                              const doneDescription = (() => {
                                const raw = isCPhase ? (rawAgentSummary || t.agentInfo?.instruction) : (t.description || null);
                                if (!raw) return null;
                                return raw.length > 55 ? raw.substring(0, 55) + '…' : raw;
                              })();
                              const activeSubtitle = (() => {
                                const raw = t.subtitle || t.agentInfo?.instruction || null;
                                if (!raw) return null;
                                return raw.length > 55 ? raw.substring(0, 55) + '…' : raw;
                              })();
                              const isExpanded = expandedLogId === t.id;
                              const isCompactDone = tDone && isPhaseActive;
                              const isReadTask = t.type === 'read';
                              return (
                                <Box key={t.id} mb="8px" _last={{ mb: 0 }} className="task-row-enter">
                                  {isReadTask ? (
                                    <Flex align="center" gap={1.5}>
                                      <Box as="span" className="material-icons-outlined" flexShrink={0}
                                        style={{ fontSize: '13px', color: tDone ? '#68D391' : '#63B3ED' }}>
                                        {tDone ? 'done' : 'manage_search'}
                                      </Box>
                                      <Box minW={0}>
                                        <Text fontSize="xs" fontWeight="400" style={{ color: tDone ? '#A0AEC0' : '#4A5568' }}>
                                          {t.label}
                                        </Text>
                                        {t.subtitle && (
                                          <Text fontSize="9px" style={{ color: '#CBD5E0', fontFamily: 'monospace' }}>
                                            {t.subtitle}
                                          </Text>
                                        )}
                                      </Box>
                                    </Flex>
                                  ) : isCompactDone ? (
                                    <Flex align="center" gap={1.5}>
                                      <Box as="span" className="material-icons-outlined" flexShrink={0}
                                        style={{ fontSize: '13px', color: '#68D391' }}>
                                        task_alt
                                      </Box>
                                      <Text fontSize="xs" fontWeight="400" style={{ color: '#A0AEC0' }}>
                                        {t.label}
                                      </Text>
                                    </Flex>
                                  ) : (
                                    <Flex
                                      align="flex-start" gap={1.5}
                                      cursor={hasExpandableLog ? 'pointer' : 'default'}
                                      onClick={hasExpandableLog ? () => setExpandedLogId(prev => prev === t.id ? null : t.id) : undefined}
                                      _hover={hasExpandableLog ? { opacity: 0.75 } : {}}
                                      role={hasExpandableLog ? 'button' : undefined}
                                    >
                                      {tActive ? (
                                        <Spinner
                                          size="xs"
                                          color={isWPhase ? 'orange.400' : 'blue.400'}
                                          thickness="1.5px"
                                          speed="0.7s"
                                          flexShrink={0}
                                          mt="1px"
                                        />
                                      ) : (
                                        <Box as="span" className="material-icons-outlined" flexShrink={0} mt="1px"
                                          style={{ fontSize: '13px', color: iconColor, transition: 'color 0.2s ease' }}>
                                          {taskIcon}
                                        </Box>
                                      )}
                                      <Box flex={1} minW={0}>
                                        <Text fontSize="xs" fontWeight={tActive ? '600' : '500'}
                                          className={tActive ? `phase-text-shimmer ${isWPhase ? 'task-title-shimmer-w' : 'task-title-shimmer-c'}` : undefined}
                                          style={tActive ? undefined : { color: labelColor, transition: 'color 0.2s ease' }}>
                                          {(tDone && !isCPhase && doneDescription) ? doneDescription : t.label}{tActive ? '...' : ''}
                                        </Text>
                                        {tActive && activeSubtitle && (
                                          <Text fontSize="10px" lineHeight="1.4" mt="1px" fontStyle="italic"
                                            style={{ color: isWPhase ? '#C05621' : '#2C5282' }}>
                                            {activeSubtitle}
                                          </Text>
                                        )}
                                        {tDone && !isCPhase && doneDescription && (
                                          <Text fontSize="10px" lineHeight="1.4" mt="1px" style={{ color: '#A0AEC0' }}>
                                            {t.label}
                                          </Text>
                                        )}
                                        {tDone && isCPhase && doneDescription && (
                                          <Text fontSize="10px" lineHeight="1.4" mt="1px" style={{ color: '#718096' }}>
                                            {doneDescription}
                                          </Text>
                                        )}
                                      </Box>
                                      {hasExpandableLog && (
                                        <Box as="span" className="material-icons-outlined" flexShrink={0} mt="2px"
                                          style={{ fontSize: '12px', color: '#CBD5E0', transition: 'transform 0.25s ease-in-out',
                                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                          expand_more
                                        </Box>
                                      )}
                                    </Flex>
                                  )}
                                  {!isCompactDone && (<>
                                    {tActive && (t.streamingContent || t.agentInfo?.streamingContent || t.agentInfo?.instruction) && (
                                      <StreamingTextBox
                                        ml={5} mt="4px"
                                        content={(t.streamingContent || t.agentInfo?.streamingContent || t.agentInfo?.instruction || '').replace(/[{}\[\]"\\]/g, '').replace(/:\s+/g, ': ').replace(/,\s*\n/g, '\n').replace(/^\s*\n/gm, '')}
                                      />
                                    )}
                                    {hasExpandableLog && (
                                      <Box ml={5} mt={isExpanded ? 1 : 0}
                                        style={{ maxHeight: isExpanded ? '160px' : '0px', overflow: 'hidden',
                                          transition: 'max-height 0.28s cubic-bezier(0.4,0,0.2,1), margin-top 0.2s ease' }}>
                                        {logData ? (
                                          <Box position="relative" className="expand-panel-fade expand-panel-fade-dark">
                                            <Box p={2} borderRadius="md" bg="gray.900" color="green.300"
                                              fontSize="10px" fontFamily="monospace" h="140px" overflowY="auto" whiteSpace="pre-wrap">
                                              {JSON.stringify(logData, null, 2)}
                                            </Box>
                                          </Box>
                                        ) : t.description ? (
                                          <Box position="relative" className="expand-panel-fade expand-panel-fade-light">
                                            <Box p={2} borderRadius="md" bg="gray.50" h="140px" overflowY="auto"
                                              fontSize="10px" lineHeight="1.6" style={{ color: '#718096', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                              {t.description}
                                            </Box>
                                          </Box>
                                        ) : null}
                                      </Box>
                                    )}
                                  </>)}
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              );
            })()}

            {/* ── Agent tool call cards — only for messages without segments (legacy path) ── */}
            {/* When segments exist, tool cards are rendered inline within the segments block above */}
            {message.sender === 'bot' && !message.segments?.length && message.agentToolCalls?.length > 0 && (
              <Box w="100%" mt={1}>
                {message.agentToolCalls.filter(tc => !shouldRenderGithubOutcomeCard(tc)).map(tc => (
                  <AgentToolCallCard key={tc.tool_call_id || tc.id} toolCall={tc} />
                ))}
              </Box>
            )}

            {/* ── Closing summary — below W/C phases, above CTA chips ── */}
            {!githubOutcomeToolCall && (message.closingText || (msgIsExecutionOnly && message.text)) && (() => {
              const rawClosing = typeof message.closingText === 'string' ? message.closingText
                : (msgIsExecutionOnly && message.text) ? message.text
                : String(message.closingText || '');
              const { cleanText: ctaCleanedClosing } = parseCTAsFromMessage(rawClosing);
              let cleanClosingText = stripJsonForDisplay(ctaCleanedClosing);
              // Apply same normalizations as cleanMessage to fix formatting
              const labelPattern = /(\n?\s*)\*\*(Context|Goals|Frustrations|Opportunities|Hypothesis|Rationale|Success Indicator|For Audience|Principle|Description|Target|Metric|Need|Persona|Strategic Value|Potential Impact|Opportunity|Theme|Positioning Statement|Key Differentiators):/gi;
              cleanClosingText = cleanClosingText.replace(labelPattern, (match, prefix, label, offset) => {
                if (offset === 0) return `**${label}:`;
                return `\n\n**${label}:`;
              });
              cleanClosingText = cleanClosingText.replace(/\*\*[ \t]+(.+?)[ \t]+\*\*/g, '**$1**');
              cleanClosingText = cleanClosingText.replace(/\*\*[ \t]+(.+?)\*\*/g, '**$1**');
              cleanClosingText = cleanClosingText.replace(/\*\*(.+?)[ \t]+\*\*/g, '**$1**');
              cleanClosingText = cleanClosingText.replace(/(?<!\*)\*[ \t]+(.+?)[ \t]+\*(?!\*)/g, '*$1*');
              cleanClosingText = cleanClosingText.replace(/([^*\s\n])(\*(?!\*))([^*\n]+?)(\n|$)/g, '$1 $3$4');
              cleanClosingText = cleanClosingText.replace(/(?<!\*)\*(?!\*)([^*\n]+?)(\n|$)/g, '$1$2');
              return (
                <Box
                  className={index < lastLoadedCountRef.current ? "bot-message-static" : "bot-message"}
                  p={3} lineHeight="1.6" borderRadius="16px" width="100%"
                >
                  <ReactMarkdown children={cleanClosingText} components={markdownComponents} />
                </Box>
              );
            })()}

            {/* Clarification chips from sub-agents — above copy+learnings row */}
            {message.proposedActions?.filter(a => a.type === 'clarification').length > 0 && (
              <VStack align="stretch" spacing={3} mt={2} mb={2} w="100%">
                {message.proposedActions.filter(a => a.type === 'clarification').map((action, i) => (
                  <Box key={i} w="100%">
                    <Text
                      fontSize="10px"
                      fontWeight="600"
                      color="gray.500"
                      textTransform="uppercase"
                      letterSpacing="0.06em"
                      mb={1}
                      pl={1}
                    >
                      {action.source_agent || 'Agent'} has a question
                    </Text>
                    <Box
                      w="100%"
                      px={3}
                      py={2}
                      borderRadius="8px"
                      border="1px solid"
                      borderColor="gray.300"
                      bg="gray.50"
                      userSelect="text"
                    >
                      <Text fontSize="13px" color="gray.700" whiteSpace="normal" lineHeight="1.4">
                        {action.label}
                      </Text>
                    </Box>
                  </Box>
                ))}
              </VStack>
            )}

            {message.sender === 'bot' && !message.isLoading && githubOutcomeToolCall && (
              <Box w="100%" px={3} mt={2}>
                <GithubOutcomeCard toolCall={githubOutcomeToolCall} />
              </Box>
            )}

            {/* Copy button + learning badge — all finished bot messages with content */}
            {message.sender === "bot" &&
              !message.isLoading &&
              !message.isStreaming &&
              !message.isProgressUI &&
              !message.phases?.some(p => p.state === 'active') &&
              (cleanMessage || message.chatLearningsCount > 0) && (
              <HStack spacing={2} align="center" mt={1} wrap="wrap">
                {cleanMessage && (
                  <CopyMessageButton text={cleanMessage} />
                )}
                {message.chatLearningsCount > 0 && (
                  <LearningDetectedTag
                    count={message.chatLearningsCount}
                    onClick={onOpenLearnings}
                  />
                )}
              </HStack>
            )}

            {/* FIX #52: Render navigation CTA buttons if AI included them */}
            {messageCTAs && messageCTAs.length > 0 && message.sender === "bot" && (
              <VStack align="flex-start" spacing={2} mt={3}>
                {messageCTAs.map((cta, ctaIndex) => (
                  <Button
                    key={ctaIndex}
                    size="sm"
                    colorScheme={cta.style === 'option' ? 'gray' : 'blue'}
                    variant="outline"
                    leftIcon={<ChevronRightIcon />}
                    onClick={() => handleCTAClick(cta)}
                    _hover={{ bg: cta.style === 'option' ? "gray.50" : "blue.50" }}
                  >
                    {cta.label}
                  </Button>
                ))}
              </VStack>
            )}

            {/* Render Feature Enablement Confirmation if message has feature_enablement_proposal */}
            {message.feature_enablement_proposal && (
              <FeatureEnablementConfirmation
                featureId={message.feature_enablement_proposal.featureId}
                planId={currentPlanId}
                programsCount={message.feature_enablement_proposal.programsCount || 0}
                stagesCount={message.feature_enablement_proposal.stagesCount || 0}
                toolsCount={message.feature_enablement_proposal.toolsCount || 0}
                finalProgramSequence={message.feature_enablement_proposal.final_program_sequence}
                selectedPrograms={message.feature_enablement_proposal.selected_programs}
                handleComponentAction={handleComponentAction}
                triggerWorkflow={message.feature_enablement_proposal.triggerWorkflow || false}
                isDocumentOnly={message.feature_enablement_proposal.isDocumentOnly || false}
                onConfirm={(result) => {
                  console.log('✅ Feature enablement confirmed:', result);
                }}
                onCancel={() => {
                  console.log('❌ Feature enablement cancelled');
                  // Remove the feature_enablement_proposal from this message to dismiss the UI
                  setMessages((prevMessages) =>
                    prevMessages.map((msg, msgIdx) =>
                      msgIdx === index
                        ? { ...msg, feature_enablement_proposal: null }
                        : msg
                    )
                  );
                  // Add a confirmation message
                  setMessages((prevMessages) => [
                    ...prevMessages,
                    {
                      text: "Feature enablement cancelled.",
                      sender: "bot",
                      isHtml: false,
                      timestamp: new Date().toISOString(),
                    },
                  ]);
                }}
              />
            )}


            {/* Legacy actions (enable_feature, bulk_actions, etc.) — AIActionHandler card */}
            {(message.actions || []).filter(a => !TIER_A_CAPABILITIES.has(a.capabilityId)).length > 0 && (
              <AIActionHandler
                actions={(message.actions || []).filter(a => !TIER_A_CAPABILITIES.has(a.capabilityId))}
                onActionExecute={(action) => handleActionExecute(action, index)}
                onActionCancel={(action) => handleActionCancel(action, index)}
                isExecuting={isExecutingAction}
                executingActionId={executingActionId}
              />
            )}
          </VStack>
        );
      };

      function getAgentInitials(name) {
        return (name || '?')
          .split(/\s+/)
          .map(w => w[0])
          .join('')
          .toUpperCase()
          .substring(0, 2);
      }

      function normalizeAgents(activeAgentsList, agentExchangeLog) {
        if (activeAgentsList?.length > 0) {
          return activeAgentsList.map(a => ({
            id: a.agentId, name: a.name, state: a.state, isLead: a.isLead, instruction: a.instruction, detail: a.exchangeLog
          }));
        }
        if (agentExchangeLog?.length > 0) {
          return agentExchangeLog.map(entry => {
            // Synthetic execution entry (from backend Tier A execution) — already in display shape
            if (entry.agentId) {
              return {
                id: entry.agentId,
                name: entry.name,
                state: entry.state || 'done',
                isLead: entry.isLead ?? false,
                instruction: '',
                detail: entry.exchangeLog || [],
              };
            }
            // Standard agent exchange log entry — { task, result } shape from ChatStateManager
            const { task, result } = entry;
            return {
              id: task.task_id,
              name: result?.agent_name || task.agent,
              state: 'done',
              isLead: false,
              instruction: task.instruction,
              detail: result ? [{ type: 'result', ...result }] : [],
            };
          });
        }
        return [];
      }

      // Sorted message list — stable reference so virtualizer doesn't thrash
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const sortedMessages = useMemo(() =>
        messages
          .filter((msg) => !isCompressedContextMessage(msg))
          .map((msg, i) => ({ msg, i }))
          .sort((a, b) => {
            const ta = a.msg.timestamp ? new Date(a.msg.timestamp).getTime() : -1;
            const tb = b.msg.timestamp ? new Date(b.msg.timestamp).getTime() : -1;
            if (ta !== tb) return ta - tb;
            return a.i - b.i;
          })
          .map(({ msg }) => msg),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [messages, activeAgents, workExpanded, expandedLogId, isExecutingAction, executingActionId, lastLoadedCountRef.current]
      );

      // Virtualizer — only renders ~15 messages in the DOM regardless of conversation length
      const virtualizer = useVirtualizer({
        count: sortedMessages.length,
        getScrollElement: () => messagesContainerRef.current,
        estimateSize: (index) => {
          const msg = sortedMessages[index];
          const textLen = (msg?.text || '').length;
          if (textLen > 1000) return 800;
          if (textLen > 400) return 300;
          return 120;
        },
        overscan: 30,
        getItemKey: (index) => {
          const msg = sortedMessages[index];
          if (!msg) return index;
          // Use a composite key so messages sharing the same timestamp don't collide.
          // msg.id doesn't exist for most messages, so fall back to sender+timestamp+index.
          return msg.id || `${msg.sender}_${msg.timestamp || ''}_${index}`;
        },
      });
      virtualizerRef.current = virtualizer;

      return (
        <Box
          position={isEmbedded ? "relative" : "fixed"}
          top={isEmbedded ? "0" : { base: "0", md: "20px" }}
          right={isEmbedded ? "0" : { base: "0", md: "40px" }}
          left={isEmbedded ? "auto" : { base: "0", md: "auto" }}
          bottom="auto"
          zIndex={isEmbedded ? "auto" : 999}
          width={isEmbedded ? "100%" : { base: "100%", md: "auto" }}
          height={isEmbedded ? "100%" : { base: "100dvh", md: "auto" }}
          pointerEvents={isEmbedded || isOpen ? "auto" : "none"}
        >
          {(isOpen || isEmbedded) && (
            <Box
              w={isEmbedded ? "100%" : chatWidth}
              h={isEmbedded ? "100%" : chatHeight}
              bg={isEmbedded ? "transparent" : "white"}
              borderRadius={isEmbedded ? "0" : { base: "0", md: "3xl" }}
              boxShadow={isEmbedded ? "none" : "lg"}
              display="flex"
              flexDirection="column"
              border={isEmbedded ? "none" : "1px solid"}
              borderColor={isEmbedded ? "transparent" : "brand.300"}
              transition={{ base: "none", md: "height 0.3s ease-in-out, width 0.3s ease-in-out" }}
              overflow="hidden"
              isolation={isEmbedded ? "isolate" : undefined}
            >
              {!isEmbedded && (
                <Box
                  justify="space-between"
                  align="right"
                  p={2}
                  borderTopRadius="3xl"
                  //backgroundColor="white"
                  height="50px"
                >
                  <Flex>
                    <Flex>
                      <Tooltip label="Expand/Collapse Chat" isDisabled={isTouch}>
                        <IconButton
                          size="md"
                          icon={isExpanded ? <BiCollapse /> : <BiExpand />}
                          variant="ghost"
                          onClick={() => {
                            setIsExpanded((prev) => !prev);
                            setIsMinimised(false);
                          }}
                          borderRadius="3xl"
                          aria-label="Expand/Collapse Chat"
                        />
                      </Tooltip>
                      <Tooltip label="Minimise" isDisabled={isTouch}>
                        <IconButton
                          size="md"
                          icon={<Text fontSize="lg">—</Text>}
                          variant="ghost"
                          onClick={() => {
                            setIsMinimised((prev) => !prev);
                            setIsExpanded(false);
                          }}
                          borderRadius="3xl"
                          aria-label="Minimise Chat"
                        />
                      </Tooltip>
                      <Tooltip label="Start New Chat" isDisabled={isTouch}>
                        <IconButton
                          size="md"
                          icon={<RepeatIcon />}
                          variant="ghost"
                          onClick={startNewChat}
                          borderRadius="3xl"
                          aria-label="Start New Chat"
                        />
                      </Tooltip>
                    </Flex>
                    <Tooltip label="Close Chat" isDisabled={isTouch}>
                      <IconButton
                        size="sm"
                        icon={<CloseIcon />}
                        variant="ghost"
                        onClick={() => setIsOpen(false)}
                        borderRadius="3xl"
                        position="absolute"
                        top="15px"
                        right="15px"
                        aria-label="Close Chat"
                      />
                    </Tooltip>
                  </Flex>
                </Box>
              )}

              <VStack
                ref={messagesContainerRef}
                p={isEmbedded ? 3 : 5}
                pt={isEmbedded ? 3 : 4}
                pb={isEmbedded ? 0 : 5}
                spacing={0}
                overflowY="auto"
                overflowX="hidden"
                flex="1"
                align="stretch"
                minHeight="0"
                isolation="isolate"
                css={{
                  overscrollBehavior: "contain",
                  touchAction: "pan-y",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                {/* Virtual message list — only visible items are in the DOM */}
                <Box
                  style={{ height: `${virtualizer.getTotalSize()}px`, width: '100%', position: 'relative' }}
                >
                  {virtualizer.getVirtualItems().map(virtualItem => (
                    <Box
                      key={virtualItem.key}
                      data-index={virtualItem.index}
                      ref={virtualizer.measureElement}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualItem.start}px)`,
                        paddingBottom: '12px',
                      }}
                    >
                      {renderMessage(sortedMessages[virtualItem.index], virtualItem.index)}
                    </Box>
                  ))}
                </Box>
                {isAwaitingConfirmation && renderConfirmationButtons()}
                {compressionSuggested && !loading && (
                  <Flex
                    align="center"
                    justify="space-between"
                    gap={3}
                    px={3}
                    py={2}
                    mx={2}
                    mb={2}
                    bg="brand.50"
                    border="1px solid"
                    borderColor="brand.200"
                    borderRadius="xl"
                  >
                    <HStack spacing={2} minW={0}>
                      <Box as="span" className="material-icons-outlined" fontSize="16px" color="brand.500">
                        compress
                      </Box>
                      <Text fontSize="xs" color="brand.800" lineHeight="1.4">
                        This conversation is getting long. Compress it so responses stay sharp.
                      </Text>
                    </HStack>
                    <HStack spacing={2} flexShrink={0}>
                      <Button
                        size="xs"
                        colorScheme="gray"
                        variant="ghost"
                        onClick={() => setCompressionSuggested(false)}
                      >
                        Dismiss
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="gray"
                        variant="solid"
                        isLoading={compressing}
                        onClick={handleCompressConversation}
                      >
                        Compress
                      </Button>
                    </HStack>
                  </Flex>
                )}
                <div ref={messagesEndRef} />
              </VStack>

              <Box
                px={isEmbedded ? 3 : 4}
                pb={isEmbedded ? 3 : 2}
                pt={0}
                width="100%"
                flexShrink={0}
                css={{
                  paddingBottom: "max(12px, env(safe-area-inset-bottom, 12px))",
                }}
              >
                {showGitHubBanner && (
                  <GitHubConnectBanner planId={currentPlanId} />
                )}
                <Box
                  display="flex"
                  flexDirection="column"
                  width="100%"
                  bg="white"
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor={isDragActive ? "brand.800" : "brand.300"}
                  boxShadow={isDragActive ? "0 0 0 3px rgba(49, 130, 206, 0.12)" : undefined}
                  onDragOver={handleComposerDragOver}
                  onDragLeave={handleComposerDragLeave}
                  onDrop={handleComposerDrop}
                  _hover={{ borderColor: "brand.500" }}
                  _focusWithin={{ borderColor: "brand.800", boxShadow: "sm" }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".txt,.csv,text/plain,text/csv"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleFileInputChange}
                  />
                  {(isDragActive || pendingAttachments.length > 0 || attachmentError || showFormatHelp) && (
                    <Box px={3} pt={3} pb={pendingAttachments.length > 0 || attachmentError || showFormatHelp ? 1 : 3}>
                      {showFormatHelp && (
                        <Flex
                          align="flex-start"
                          gap={2.5}
                          p={3}
                          mb={(isDragActive || pendingAttachments.length > 0 || attachmentError) ? 2 : 0}
                          bg="orange.50"
                          border="1px solid"
                          borderColor="orange.200"
                          borderRadius="xl"
                          animation={`${attachmentPillEnter} 220ms cubic-bezier(0.16, 1, 0.3, 1)`}
                        >
                          <Flex
                            align="center"
                            justify="center"
                            boxSize="28px"
                            borderRadius="lg"
                            bg="white"
                            flexShrink={0}
                            color="orange.500"
                          >
                            <Box as="span" className="material-icons-outlined" fontSize="16px">
                              error_outline
                            </Box>
                          </Flex>
                          <Box flex="1" minW={0}>
                            <Text fontSize="xs" fontWeight="700" color="orange.800">
                              That file type isn't supported yet
                            </Text>
                            <Text fontSize="xs" color="gray.700" mt="2px" lineHeight="short">
                              Pabs can only read .txt and .csv files right now. Support for PDFs, spreadsheets, and images is coming soon.
                            </Text>
                          </Box>
                          <IconButton
                            aria-label="Dismiss"
                            icon={<CloseIcon />}
                            size="xs"
                            variant="ghost"
                            flexShrink={0}
                            onClick={() => setShowFormatHelp(false)}
                          />
                        </Flex>
                      )}
                      {isDragActive && (
                        <Flex
                          align="center"
                          justify="center"
                          minH="64px"
                          border="1px dashed"
                          borderColor="brand.500"
                          bg="brand.50"
                          borderRadius="xl"
                          color="brand.800"
                          gap={2}
                        >
                          <Box as="span" className="material-icons-outlined" fontSize="18px">
                            upload_file
                          </Box>
                          <Text fontSize="sm" fontWeight="600">Drop files to attach</Text>
                        </Flex>
                      )}
                      {pendingAttachments.length > 0 && (
                        <Flex
                          align="center"
                          gap={2}
                          wrap="nowrap"
                          mt={isDragActive ? 2 : 0}
                          overflowX="auto"
                          overflowY="hidden"
                          pb={1}
                          sx={{
                            WebkitOverflowScrolling: "touch",
                            scrollbarWidth: "thin",
                          }}
                        >
                          {pendingAttachments.map(file => {
                            const isReading = file.status === "reading";
                            return (
                            <Box
                              key={file.id}
                              display="inline-flex"
                              alignItems="center"
                              gap={1.5}
                              px={2.5}
                              py={1.5}
                              bg="gray.50"
                              border="1px solid"
                              borderColor={isReading ? "brand.200" : "gray.200"}
                              borderRadius="lg"
                              flex="0 0 220px"
                              width="220px"
                              maxWidth="220px"
                              minWidth={0}
                              position="relative"
                              overflow="hidden"
                              transition="border-color 0.25s ease"
                              animation={`${attachmentPillEnter} 220ms cubic-bezier(0.16, 1, 0.3, 1)`}
                            >
                              {isReading && (
                                <Box
                                  position="absolute"
                                  top={0}
                                  bottom={0}
                                  left={0}
                                  w="45%"
                                  pointerEvents="none"
                                  bgGradient="linear(to-r, transparent, whiteAlpha.800, transparent)"
                                  animation={`${attachmentShimmer} 1.15s ease-in-out infinite`}
                                />
                              )}
                              <Tooltip label={file.name} placement="top" openDelay={400} isDisabled={isTouch}>
                                <Text
                                  fontSize="xs"
                                  fontWeight="600"
                                  color="gray.700"
                                  isTruncated
                                  flex="1 1 auto"
                                  minW={0}
                                  maxWidth="none"
                                  textAlign="left"
                                >
                                  {file.name}
                                </Text>
                              </Tooltip>
                              {isReading ? (
                                <Spinner size="xs" color="brand.400" speed="0.7s" thickness="2px" flexShrink={0} />
                              ) : (
                                <Text fontSize="2xs" color="gray.400" whiteSpace="nowrap" flexShrink={0}>
                                  {formatAttachmentSize(file.size)}
                                </Text>
                              )}
                              <IconButton
                                aria-label={`Remove ${file.name}`}
                                icon={<CloseIcon />}
                                size="xs"
                                variant="ghost"
                                minW="20px"
                                h="20px"
                                flexShrink={0}
                                onClick={() => removePendingAttachment(file.id)}
                              />
                            </Box>
                            );
                          })}
                        </Flex>
                      )}
                      {attachmentError && (
                        <Text fontSize="xs" color="red.500" mt={2}>
                          {attachmentError}
                        </Text>
                      )}
                      {pendingAttachments.length > 0 && (
                        <Text fontSize="2xs" color="gray.400" mt={2}>
                          Available in this chat. File content is not stored.
                        </Text>
                      )}
                    </Box>
                  )}
                  {/* Middle Area - User Input */}
                  <AutoResizeTextarea
                    ref={inputRef}
                    key={`chat-input-${currentPlanId || "default"}-${isEmbedded ? "embedded" : "floating"}`}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={isOnBriefPage ? "Reply to Pabs..." : "Ask Pabs anything about this plan..."}
                    minRows={1}
                    maxRows={6}
                    style={{
                      border: "none",
                      outline: "none",
                      resize: "none",
                      padding: "12px 16px",
                      fontSize: "16px",
                      backgroundColor: "white",
                      width: "100%",
                      lineHeight: "1.4",
                    }}
                  />

                  {/* Bottom Area */}
                  <ChatFooterBar
                    interactionMode={interactionMode}
                    setInteractionMode={setInteractionMode}
                    onAgentSelect={handleAgentSelect}
                    selectedAgents={selectedAgents}
                    loading={loading}
                    input={input}
                    onSend={handleSend}
                    onAttachClick={() => fileInputRef.current?.click()}
                    hasAttachments={pendingAttachments.length > 0}
                    attachmentsUploading={pendingAttachments.some(a => a.status === "reading")}
                    isOnBriefPage={isOnBriefPage}
                    currentPlanId={currentPlanId}
                  />
                </Box>
              </Box>
            </Box>
          )}
          <CreditTopUpModal
            isOpen={isChatTopUpOpen}
            onClose={() => setIsChatTopUpOpen(false)}
            hasSavedPaymentMethod={planStatus?.hasSavedPaymentMethod}
            planType={planStatus?.planType}
            resetAt={planStatus?.credits?.resetAt}
            initialMode="topup"
          />
        </Box>
      );
    },
  ),
);

export default FloatingChatbot;
